import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Download, Undo2, Redo2, Copy, Plus, Paintbrush, Eraser, Ruler, Layout, Lock, Unlock, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, MousePointer2, Image as FrameImagePlaceholderIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import DownloadDialog from './DownloadDialog';
import PageThumbnailPreview from './PageThumbnailPreview';
import { useToast } from '../../hooks/use-toast';
import { processSvgForRender } from './svgUtils';
import { getFrameClip } from './frameClip';
import { computeFrameImageLayout, clampCropOffsets, minCropScale } from './frameLayout';
import { buildElementTransform } from './elementTransform';
import { resolveImageReplaceTarget, buildReplacePatch } from './imageReplaceTarget';
import { isAspectLocked, constrainResizeToAspect } from './aspectLock';
import { blendModeStyle } from './blendModes';
import { composeFilter, duotoneTableValues, effectTextStyle, shadowBoxCss } from './propertyEffects';
import { cropResize } from './cropResize';
import { computeSnap } from './smartSnap';
import { buildSVGGradient } from './renderUtils';
import { StaticTextBlock, StaticQR, imageImgStyle, imageBoxRadius } from './staticTextRender';
import EditableTextNode from './EditableTextNode';
import { collectFontSpecs } from '../../lib/collectFonts';
import { waitForFontSpecs } from '../../lib/fonts';
// Side-effect import: registers `window.__inspectTextElement(id)` and
// `window.__inspectAllTextElements()` console hooks for live text-rendering
// audits. Investigation-only; no behaviour change at runtime.
import '../../utils/inspectTextElement';
import {
  getOutermostAncestor,
  getGroupBounds,
  expandSelectionToLeaves,
  invalidateBoundsCacheUpward,
  getDirectChildren,
  getDescendants,
  getAncestorChain,
  applyResizeToDescendants,
  applyRotateToDescendants,
} from '../../lib/groups/groupUtils';

// Memoized so DOMParser only re-runs when SVG-relevant props actually change.
// Each of these creates a new object reference when updated via updateElement(),
// which is enough to invalidate useMemo and trigger a re-process.
const SvgContent = React.memo(({ element }) => {
  const html = useMemo(
    () => processSvgForRender(element),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      element.svgContent,  // original source — changes when SVG is replaced
      element.colorMap,    // new object ref on every color-map edit
      element.fill,        // tint / Memphis fill color
      element.stroke,      // nested stroke object — new ref on every stroke edit
      element.gradient,    // nested gradient object — new ref on every gradient edit
    ]
  );
  return (
    <div
      className="w-full h-full [&>svg]:w-full [&>svg]:h-full"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

const MIN_CANVAS = 1;
const MAX_CANVAS = 10000;

const TOOLS = {
  SELECT: 'select',
  PEN: 'pen',
  BRUSH: 'brush',
  ERASER: 'eraser',
  MASK: 'mask',
};

const BRUSH_PRESETS = [
  {
    id: 'basic',
    name: 'Basic',
    description: 'Smooth round stroke',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    widthMultiplier: 1,
    opacityMultiplier: 1,
    filterType: null,
    defaultSize: 4,
  },
  {
    id: 'marker',
    name: 'Marker',
    description: 'Flat semi-transparent',
    strokeLinecap: 'square',
    strokeLinejoin: 'miter',
    widthMultiplier: 2.5,
    opacityMultiplier: 0.5,
    filterType: null,
    defaultSize: 10,
  },
  {
    id: 'calligraphy',
    name: 'Calligraphy',
    description: 'Angled nib pen',
    strokeLinecap: 'butt',
    strokeLinejoin: 'miter',
    widthMultiplier: 1,
    opacityMultiplier: 1,
    filterType: 'calligraphy',
    defaultSize: 6,
  },
  {
    id: 'chalk',
    name: 'Chalk',
    description: 'Rough powdery edges',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    widthMultiplier: 1.5,
    opacityMultiplier: 0.75,
    filterType: 'chalk',
    defaultSize: 8,
  },
  {
    id: 'spray',
    name: 'Spray',
    description: 'Scattered spray paint',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    widthMultiplier: 5,
    opacityMultiplier: 0.2,
    filterType: 'spray',
    defaultSize: 20,
  },
  {
    id: 'texture',
    name: 'Texture',
    description: 'Bristle texture brush',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    widthMultiplier: 1.2,
    opacityMultiplier: 0.85,
    filterType: 'texture',
    defaultSize: 6,
  },
];

const CanvasEditor = ({ elements, selectedElement, selectedElements = [], onSelectElement, onSelectMultiple, onUpdateElement, onDeleteElement, onDeleteSelectedElements, canvasSize, onCanvasSizeChange, zoom, canvasBackground = '#ffffff', backgroundConfig, projectName = 'Untitled Design', onProjectNameChange, onAddPage, onDuplicatePage, onLockPage, onDeletePage, onReorderPages, onHidePage, pages = [], currentPageId, onSwitchPage, activeTool, onToolChange, onAddElement, isAnimationPlaying = false, animationPlayKey = 0, brushSettings: brushSettingsProp, setBrushSettings: setBrushSettingsProp, selectedBrushPreset: selectedBrushPresetProp, setSelectedBrushPreset: setSelectedBrushPresetProp, eraserSettings: eraserSettingsProp, setEraserSettings: setEraserSettingsProp, editRequest, onUndo, onRedo, canUndo = false, canRedo = false, onInteractionStart, onInteractionChange, editingGroupId, onSetEditingGroupId, previewFilter = null, downloadTemplateId = null, downloadSessionType = null, onDownloadQuotaExceeded, onDownloadDisabled, cropElementId = null, onStartCrop, onCommitCrop, onCancelCrop, onUploadImageFromUrl }) => {
  const { toast } = useToast();
  const canvasRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const pageRefs = useRef({});
  // Scroll active page into view whenever currentPageId changes
  useEffect(() => {
    const ref = pageRefs.current[currentPageId];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentPageId]);
  // Clean up stale refs for deleted pages
  useEffect(() => {
    const pageIds = new Set(pages.map(p => p.id));
    Object.keys(pageRefs.current).forEach(id => {
      if (!pageIds.has(id)) delete pageRefs.current[id];
    });
  }, [pages]);
  // Keep a ref to the latest elements array so drag handlers avoid stale closures
  const elementsRef = useRef(elements);
  useEffect(() => { elementsRef.current = elements; }, [elements]);

  // ── GPU-layer promotion gate for the active canvas ──────────────────────────
  // The active page renders inside a `will-change: transform` / `translateZ(0)`
  // compositor layer (see canvas-scaled-layer below). If that layer is promoted
  // BEFORE the current page's webfonts have loaded, the browser rasterizes it
  // with fallback metrics (taller ascent + `line-height: normal`) and — on
  // Chromium — never re-rasterizes once the real font arrives, so every text
  // block stays anchored at the fallback baseline, visibly shifted DOWN. On the
  // first design load this self-heals (font-reflow + loading-state churn force a
  // re-raster), but a page DUPLICATED after load has no follow-up render, so the
  // copy's text is stuck shifted until an interaction (e.g. an arrow-key nudge)
  // forces a re-raster. Gating promotion on `canvasFontsReady` builds the layer
  // once, with correct metrics — mirroring PageThumbnailPreview's fix.
  //
  // Re-keyed on `currentPageId` so switching to a freshly-duplicated page
  // repeats the un-promote → promote cycle, forcing a clean raster every switch
  // even when the font set is unchanged.
  const activeFontSignature = useMemo(() => {
    const active = pages.find(p => p.id === currentPageId);
    return (collectFontSpecs(active ? [active] : []) || [])
      .map((s) => `${s.family}|${s.weight}|${s.style}`).sort().join(',');
  }, [pages, currentPageId]);
  const [canvasFontsReady, setCanvasFontsReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setCanvasFontsReady(false);
    if (!activeFontSignature) { setCanvasFontsReady(true); return undefined; }
    const sigSpecs = activeFontSignature.split(',').map((k) => {
      const [family, weight, style] = k.split('|');
      return { family, weight, style };
    });
    (async () => {
      try {
        // Wait for the actual face binaries (family+weight+style), not just the
        // stylesheets — see waitForFontSpecs. Promoting on stylesheet-load left
        // a window where the layer rasterized with fallback metrics.
        await waitForFontSpecs(sigSpecs);
      } catch { /* best effort — promote anyway so the canvas never stays un-promoted */ }
      if (!cancelled) setCanvasFontsReady(true);
    })();
    return () => { cancelled = true; };
  }, [activeFontSignature, currentPageId]);
  // Tracks the last canvas-coord mouse position during drag; used to compute
  // per-frame deltas when dragging a group (groups have no x/y of their own).
  const dragLastMousePosRef = useRef(null);
  // Snapshot at rotate-start for group rotation (avoids float drift from
  // per-frame multiplicative rotation matrices). Holds:
  //   { startAngleRad, boundsCenter: {x,y}, leafSnapshots: [{id,x,y,width,height,rotation}] }
  // On each rotate-move frame, totalDelta = currentAngle - startAngle is
  // applied to the SNAPSHOTTED leaf positions, not the current state.
  const groupRotateInitRef = useRef(null);
  // Tracks the most recent element click for synthetic double-click detection
  // in handleElementMouseDown — used to enter isolation when the user
  // double-clicks a group (after Task 6's wrap resolves the clicked child
  // to its outermost group, two clicks on the same group within DBL_CLICK_MS
  // count as a double-click and enter isolation).
  const lastElementClickRef = useRef({ id: null, time: 0 });
  const DBL_CLICK_MS = 300;
  // Marquee (drag-to-select) state.
  // Stored in a ref so mousemove/mouseup handlers see the freshest values
  // without re-binding listeners every render.
  const marqueeRef = useRef(null); // { startX, startY, endX, endY, isDragging, isShift }
  const [marqueeBox, setMarqueeBox] = useState(null); // { x, y, w, h } for rendering
  const MARQUEE_DRAG_THRESHOLD = 4; // px in canvas coords before a click becomes a drag
  const [isDragging, setIsDragging] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showResizeDialog, setShowResizeDialog] = useState(false);
  // Keep as strings so users can type freely (e.g. "122", "3") without forced clamping mid-input
  const [resizeWidth,  setResizeWidth]  = useState(String(canvasSize.width));
  const [resizeHeight, setResizeHeight] = useState(String(canvasSize.height));
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  useEffect(() => {
    if (showResizeDialog) {
      setResizeWidth(String(canvasSize.width));
      setResizeHeight(String(canvasSize.height));
      setLockAspectRatio(false);
    }
  }, [showResizeDialog, canvasSize.width, canvasSize.height]);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [editingTextId, setEditingTextId] = useState(null); // For inline text editing
  // Element (frame / image / mask host) currently hovered by an image drag —
  // drives the purple drop highlight. Holds the hovered element's id.
  const [dropTargetId, setDropTargetId] = useState(null);

  // Crop mode keyboard: Enter commits, Escape cancels.
  useEffect(() => {
    if (!cropElementId) return;
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); onCommitCrop?.(); }
      else if (e.key === 'Escape') { e.preventDefault(); onCancelCrop?.(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cropElementId, onCommitCrop, onCancelCrop]);
  // Mirror editingTextId into a ref so the editRequest useEffect can read
  // the LATEST value without taking it as a dep (which would re-fire the
  // effect on every edit-state change and cause loops).
  const editingTextIdRef = useRef(null);
  useEffect(() => { editingTextIdRef.current = editingTextId; }, [editingTextId]);

  // Sync external edit request (from text panel "enter edit mode" action).
  // If an edit is already active on a DIFFERENT element, force-blur its
  // contentEditable FIRST so the existing onBlur commits its text before
  // editingTextId switches — preserves the "ZERO direct exits bypass blur
  // commit" invariant for this indirect path. The .blur() dispatches
  // synchronously, so its onBlur runs in the same tick and batches with
  // the setEditingTextId below.
  useEffect(() => {
    if (!editRequest?.id) return;
    if (editingTextIdRef.current && editingTextIdRef.current !== editRequest.id) {
      editableNodeRef.current?.blur?.();
    }
    setEditingTextId(editRequest.id);
  }, [editRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Surface transform-interaction state to the parent so context UI
  // (e.g. TextFloatingToolbar) can hide itself while the user is dragging,
  // resizing, or rotating. Cheap — the callback only fires when one of the
  // three flags actually flips.
  useEffect(() => {
    onInteractionChange?.(isDragging || isResizing || isRotating);
  }, [isDragging, isResizing, isRotating, onInteractionChange]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, element }

  // Direct Selection tool state
  const [selectedAnchorIndex, setSelectedAnchorIndex] = useState(null);
  const [isDraggingAnchor, setIsDraggingAnchor] = useState(false);
  const [isDraggingHandle, setIsDraggingHandle] = useState(false);
  const [activeHandleType, setActiveHandleType] = useState(null); // 'in' or 'out'

  // Pen tool drawing state
  const [penPoints, setPenPoints] = useState([]);              // Placed anchor points (design coords)
  const [penCursorPos, setPenCursorPos] = useState(null);      // Current cursor position for preview
  const [penDragAnchor, setPenDragAnchor] = useState(null);    // { x, y } of mousedown point
  const [penIsDragging, setPenIsDragging] = useState(false);   // True once drag threshold exceeded
  const [penHandlePreview, setPenHandlePreview] = useState(null); // { handleIn, handleOut } while dragging

  // Brush tool drawing state
  const [brushIsDrawing, setBrushIsDrawing] = useState(false);
  const [brushPoints, setBrushPoints] = useState([]);       // drives live SVG preview
  const [brushCursorPos, setBrushCursorPos] = useState(null);
  // Use lifted state from parent (EditorPage) when provided, otherwise local fallback
  const [brushSettingsLocal, setBrushSettingsLocal] = useState({ size: 4, color: '#000000', opacity: 1 });
  const brushSettings = brushSettingsProp ?? brushSettingsLocal;
  const setBrushSettings = setBrushSettingsProp ?? setBrushSettingsLocal;
  const [selectedBrushPresetLocal, setSelectedBrushPresetLocal] = useState('basic');
  const selectedBrushPreset = selectedBrushPresetProp ?? selectedBrushPresetLocal;
  const setSelectedBrushPreset = setSelectedBrushPresetProp ?? setSelectedBrushPresetLocal;
  // Refs for brush — mutable, always current, no stale-closure issues
  const brushPointsRef = useRef([]);      // single source of truth for in-progress points
  const brushIsDrawingRef = useRef(false);// avoids adding brushIsDrawing to move handler deps
  const brushRafRef = useRef(null);       // pending RAF id for throttled re-renders

  // Eraser tool state
  const [eraserSettingsLocal] = useState({ size: 20, hardness: 100, opacity: 1 });
  const eraserSettings = eraserSettingsProp ?? eraserSettingsLocal;
  const [eraserCursorPos, setEraserCursorPos] = useState(null);
  const [eraserIsDrawing, setEraserIsDrawing] = useState(false);
  const [eraserLivePoints, setEraserLivePoints] = useState([]);  // drives live preview
  // Refs for eraser — same ref-based pattern as brush
  const eraserIsDrawingRef = useRef(false);
  const eraserPointsRef = useRef([]);
  const eraserTargetRef = useRef(null);
  const eraserRafRef = useRef(null);
  const eraserSettingsRef = useRef({ size: 20, hardness: 100, opacity: 1 });

  // Clipping Mask tool state
  const [maskHoverElementId, setMaskHoverElementId] = useState(null);

  // Ruler guides state
  const [guides, setGuides] = useState([]); // [{ id, type: 'horizontal'|'vertical', position: number }]
  // Smart-alignment guides (Canva/Figma): transient purple lines shown only
  // during drag/resize/arrow-move, cleared on release.
  const [smartGuides, setSmartGuides] = useState({ v: [], h: [] });
  const clearSmartGuides = useCallback(() => {
    setSmartGuides((prev) => (prev.v.length || prev.h.length ? { v: [], h: [] } : prev));
  }, []);
  const snapGuideTimerRef = useRef(null);
  const [draggingGuideId, setDraggingGuideId] = useState(null);
  const [guideDragOffset, setGuideDragOffset] = useState(0); // offset from click point to guide position
  const [newGuideDrag, setNewGuideDrag] = useState(null); // { type: 'horizontal'|'vertical', position } when dragging from ruler
  const rulerTopRef = useRef(null);
  const rulerLeftRef = useRef(null);
  // Debounce timer for text element height updates on each keystroke
  const textHeightDebounceRef = useRef(null);
  // Captured at double-click time so the contentEditable ref callback can
  // place the caret at the click position (Figma behaviour) instead of
  // always at end-of-text. Shape: { id, clientX, clientY } or null.
  const pendingTextEditCaretRef = useRef(null);
  // Live typed-text buffer for the currently-edited text element. Owned by
  // the parent so it survives any EditableTextNode remount. If that
  // component is unmounted+remounted mid-edit (auto-grow re-render, parent
  // structure change, StrictMode, etc.), the new instance re-hydrates from
  // this ref instead of from stale `element.text`. Cleared on blur.
  const liveEditTextRef = useRef(null);
  // Handle to the active EditableTextNode. Lets non-blur exit paths
  // (canvas click/mousedown/dblclick, click on another element) trigger
  // the contentEditable's native .blur() while it is STILL MOUNTED, so the
  // synthetic onBlur runs and commits the typed text to pages. Without
  // this, those paths would clear editingTextId first → React unmounts the
  // contentEditable → native blur fires on a removed node → onBlur never
  // runs → text update is lost. (Root cause of the "stale text on
  // click-outside, recovers on tab-switch" bug.)
  const editableNodeRef = useRef(null);
  // Helper: trigger the contentEditable's native .blur() so the existing
  // onBlur commit pathway runs. No-op if nothing is currently being edited.
  const exitTextEdit = () => { editableNodeRef.current?.blur?.(); };

  const RULER_SIZE = 28;
  const GUIDE_COLOR = '#00b4d8';
  const GUIDE_HIT_SIZE = 8;
  const SNAP_THRESHOLD = 8;
  const generateId = () => `guide-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Memoized ruler tick positions — recomputed only when canvas size or zoom changes
  const hTicks50 = useMemo(() =>
    Array.from({ length: Math.floor(canvasSize.width / 50) + 1 }, (_, i) => i * 50),
    [canvasSize.width]);
  const hTicks100 = useMemo(() =>
    Array.from({ length: Math.floor(canvasSize.width / 100) + 1 }, (_, i) => i * 100),
    [canvasSize.width]);
  const vTicks50 = useMemo(() =>
    Array.from({ length: Math.floor(canvasSize.height / 50) + 1 }, (_, i) => i * 50),
    [canvasSize.height]);
  const vTicks100 = useMemo(() =>
    Array.from({ length: Math.floor(canvasSize.height / 100) + 1 }, (_, i) => i * 100),
    [canvasSize.height]);

  const snapToGuides = useCallback((x, y, width, height) => {
    let outX = x;
    let outY = y;
    guides.forEach((g) => {
      if (g.type === 'vertical') {
        if (Math.abs(x - g.position) <= SNAP_THRESHOLD) outX = g.position;
        else if (Math.abs(x + width - g.position) <= SNAP_THRESHOLD) outX = g.position - width;
        else if (Math.abs(x + width / 2 - g.position) <= SNAP_THRESHOLD) outX = g.position - width / 2;
      } else {
        if (Math.abs(y - g.position) <= SNAP_THRESHOLD) outY = g.position;
        else if (Math.abs(y + height - g.position) <= SNAP_THRESHOLD) outY = g.position - height;
        else if (Math.abs(y + height / 2 - g.position) <= SNAP_THRESHOLD) outY = g.position - height / 2;
      }
    });
    return { x: outX, y: outY };
  }, [guides]);


  const clampGuidePosition = useCallback((type, value) => {
    if (type === 'horizontal') return Math.max(0, Math.min(canvasSize.height, value));
    return Math.max(0, Math.min(canvasSize.width, value));
  }, [canvasSize.width, canvasSize.height]);

  const getDesignCoordsFromRuler = useCallback((e, rulerType) => {
    if (rulerType === 'vertical') {
      const r = rulerTopRef.current?.getBoundingClientRect();
      if (!r) return 0;
      return (e.clientX - r.left) / zoom;
    }
    const r = rulerLeftRef.current?.getBoundingClientRect();
    if (!r) return 0;
    return (e.clientY - r.top) / zoom;
  }, [zoom]);

  const handleRulerMouseDown = useCallback((e, rulerType) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getDesignCoordsFromRuler(e, rulerType);
    const clamped = clampGuidePosition(rulerType === 'vertical' ? 'vertical' : 'horizontal', pos);
    setNewGuideDrag({ type: rulerType === 'vertical' ? 'vertical' : 'horizontal', position: clamped });
  }, [getDesignCoordsFromRuler, clampGuidePosition]);

  const handleGuideMouseDown = useCallback((e, guide) => {
    e.stopPropagation();
    const hitHalf = GUIDE_HIT_SIZE / 2;
    if (guide.type === 'horizontal') setGuideDragOffset(e.nativeEvent.offsetY - hitHalf);
    else setGuideDragOffset(e.nativeEvent.offsetX - hitHalf);
    setDraggingGuideId(guide.id);
  }, []);

  const handleGuideDoubleClick = useCallback((e, guideId) => {
    e.stopPropagation();
    setGuides(prev => prev.filter(g => g.id !== guideId));
  }, []);

  const clearAllGuides = useCallback(() => setGuides([]), []);

  useEffect(() => {
    if (!newGuideDrag && !draggingGuideId) return;
    const handleMove = (e) => {
      if (newGuideDrag) {
        const rulerType = newGuideDrag.type === 'vertical' ? 'vertical' : 'horizontal';
        const pos = getDesignCoordsFromRuler(e, rulerType);
        setNewGuideDrag(prev => ({ ...prev, position: clampGuidePosition(prev.type === 'vertical' ? 'vertical' : 'horizontal', pos) }));
      } else if (draggingGuideId) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const designX = (e.clientX - rect.left) / zoom;
        const designY = (e.clientY - rect.top) / zoom;
        setGuides(prev => prev.map(g => {
          if (g.id !== draggingGuideId) return g;
          const pos = g.type === 'horizontal' ? designY - guideDragOffset : designX - guideDragOffset;
          return { ...g, position: clampGuidePosition(g.type === 'vertical' ? 'vertical' : 'horizontal', pos) };
        }));
      }
    };
    const handleUp = () => {
      if (newGuideDrag) {
        setGuides(prev => [...prev, { id: generateId(), type: newGuideDrag.type, position: newGuideDrag.position }]);
        setNewGuideDrag(null);
      }
      setDraggingGuideId(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [newGuideDrag, draggingGuideId, zoom, guideDragOffset, getDesignCoordsFromRuler, clampGuidePosition]);


  const handleCanvasClick = (e) => {
    closeContextMenu();
    if (e.target === canvasRef.current) {
      // Trigger native .blur() on the contentEditable so its onBlur commits
      // the typed text before React unmounts it. (See editableNodeRef.)
      exitTextEdit();
      // For the SELECT tool, handleCanvasMouseUp is authoritative for clearing
      // selection on an empty-canvas click. Clearing here as well would clobber
      // a marquee selection that mouseUp just set (a trailing click event always
      // fires after a mousedown+mouseup on the same target), making marquee
      // selection look like it "does nothing". Non-select tools never start a
      // marquee, so they still rely on this handler for the clear.
      if (activeTool !== TOOLS.SELECT) {
        onSelectElement(null);
      }
    }
  };

  // ── Canvas-level mouse handlers (marquee drag-to-select) ────────────────
  //
  // Clicking on the empty canvas (not on an element) has two behaviours:
  //   • Click (no drag) → clear selection
  //   • Drag on empty canvas with selection tool active → marquee-select
  //     all elements whose bbox intersects the dragged rectangle
  //
  // Distinguishing click vs drag: store the mouse-down origin and DEFER the
  // selection-clear until mouseUp. If the user moved >= MARQUEE_DRAG_THRESHOLD
  // pixels (in canvas coords) before releasing, treat it as a marquee.
  // Otherwise it was a click → clear selection.
  //
  // Only fires for activeTool === SELECT. Pen/Brush/Eraser/Mask each have
  // their own canvas interactions and would conflict.
  const canvasCoordsFromEvent = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  };

  const handleCanvasMouseDown = (e) => {
    if (e.target !== canvasRef.current) return;
    // Crop mode: clicking empty canvas commits the crop (Canva behavior).
    if (cropElementId) {
      onCommitCrop?.();
      return;
    }
    // Trigger native .blur() so the synthetic onBlur fires while the
    // contentEditable is still mounted — commits typed text via liveEditTextRef
    // before React tears the node down. (See editableNodeRef.)
    exitTextEdit();

    // Only start a marquee in selection-tool mode. Other tools handle their
    // own mouse-down on canvas.
    if (activeTool !== TOOLS.SELECT) return;

    const pt = canvasCoordsFromEvent(e);
    if (!pt) return;

    // Isolation exit: clicking on the canvas background OUTSIDE the editing
    // group's bounds drops one level out of isolation (and skips marquee
    // initiation for this click — it's a navigation action, not a select).
    if (editingGroupId) {
      const bounds = getGroupBounds(elements, editingGroupId);
      const insideBounds = bounds
        && pt.x >= bounds.x && pt.x <= bounds.x + bounds.width
        && pt.y >= bounds.y && pt.y <= bounds.y + bounds.height;
      if (!insideBounds && typeof onSetEditingGroupId === 'function') {
        onSetEditingGroupId(null);
        onSelectElement(null);
        return;
      }
    }

    // Defer the selection-clear. We don't know yet if this is a click or a
    // drag — mouseUp/mouseMove will decide.
    marqueeRef.current = {
      startX: pt.x,
      startY: pt.y,
      endX: pt.x,
      endY: pt.y,
      isDragging: false,         // flips true once threshold is crossed
      isShift: !!e.shiftKey,     // preserve modifier for additive selection
    };
  };

  const handleCanvasDoubleClick = (e) => {
    // Double-click on canvas: start inline text edit if text element is under cursor
    if (e.target === canvasRef.current) {
      exitTextEdit(); // commit any in-progress text edit before exiting
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (activeTool === TOOLS.ERASER) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        setEraserCursorPos({ x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom });
      }
    }
    if (activeTool === TOOLS.MASK) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const cx = (e.clientX - rect.left) / zoom;
        const cy = (e.clientY - rect.top) / zoom;
        const hit = findElementAtCanvasPoint(cx, cy);
        setMaskHoverElementId(hit ? hit.id : null);
      }
    }

    // Marquee: if mouse-down was on empty canvas and the user has now moved
    // beyond the threshold, enter active dragging and live-update the box.
    const m = marqueeRef.current;
    if (m) {
      const pt = canvasCoordsFromEvent(e);
      if (!pt) return;
      m.endX = pt.x;
      m.endY = pt.y;
      if (!m.isDragging) {
        const dx = Math.abs(m.endX - m.startX);
        const dy = Math.abs(m.endY - m.startY);
        if (dx >= MARQUEE_DRAG_THRESHOLD || dy >= MARQUEE_DRAG_THRESHOLD) {
          m.isDragging = true;
        }
      }
      if (m.isDragging) {
        const x = Math.min(m.startX, m.endX);
        const y = Math.min(m.startY, m.endY);
        const w = Math.abs(m.endX - m.startX);
        const h = Math.abs(m.endY - m.startY);
        setMarqueeBox({ x, y, w, h });
      }
    }
  };

  /**
   * Figma-style marquee collapse-to-group.
   *
   * Given the leaf hits from elementIntersectsMarquee, walk every group
   * (deepest first) and ask: are ALL of this group's DIRECT children
   * currently in the hit set? If yes, remove those children and add the
   * group itself. The deepest-first order guarantees nested promotion:
   * an inner group collapses first, becoming a hit, which then satisfies
   * its parent's direct-child coverage check.
   *
   * IMPORTANT — direct children (not all descendants):
   * Earlier drafts used getDescendants().filter(!== 'group') for the
   * coverage check, but that breaks nested cascade: once a nested group
   * collapses, its leaves are removed from hits and the outer group can
   * never see them in its descendant scan. getDirectChildren correctly
   * counts a collapsed child group as a hit (it's now in hitIds), so
   * outer promotion works as expected.
   *
   * isolationGroupId: when in isolation mode, only collapse groups that
   * are strict descendants of the editing group (never promote past the
   * isolation boundary). When null, collapse freely.
   *
   * Deterministic: sort is by ancestor-chain length; in the rare case of
   * equal depth, the iteration order falls back to allElements array
   * order (stable within a render).
   */
  const collapseLeavesToGroups = (allElements, leafHits, isolationGroupId) => {
    const hitIds = new Set(leafHits.map((el) => el.id));
    const groups = allElements
      .filter((el) => el.type === 'group' && el.id !== isolationGroupId)
      .slice()
      .sort((a, b) => {
        const ad = getAncestorChain(allElements, a.id).length;
        const bd = getAncestorChain(allElements, b.id).length;
        return bd - ad; // deeper first
      });
    for (const group of groups) {
      if (isolationGroupId) {
        const chain = getAncestorChain(allElements, group.id).map((g) => g.id);
        if (!chain.includes(isolationGroupId)) continue;
      }
      const directChildren = getDirectChildren(allElements, group.id);
      if (directChildren.length === 0) continue;
      const allInHits = directChildren.every((d) => hitIds.has(d.id));
      if (allInHits) {
        for (const d of directChildren) hitIds.delete(d.id);
        hitIds.add(group.id);
      }
    }
    return allElements.filter((el) => hitIds.has(el.id));
  };

  // AABB intersection: returns true if element's [x..x+w, y..y+h] overlaps
  // the marquee rect. Ignores rotation (uses pre-rotation bbox).
  const elementIntersectsMarquee = (el, m) => {
    if (!el || el.visible === false) return false;
    const ex = Number(el.x) || 0;
    const ey = Number(el.y) || 0;
    const ew = Number(el.width) || 0;
    const eh = Number(el.height) || 0;
    if (ew <= 0 || eh <= 0) return false;
    return (
      ex < m.x + m.w &&
      ex + ew > m.x &&
      ey < m.y + m.h &&
      ey + eh > m.y
    );
  };

  const handleCanvasMouseUp = () => {
    // Handle anchor point dragging (Direct Selection tool)
    if (isDraggingAnchor && selectedElement?.type === 'penPath' && selectedAnchorIndex !== null) {
      setIsDraggingAnchor(false);
      setSelectedAnchorIndex(null);
      return;
    }

    // Handle handle point dragging
    if (isDraggingHandle && selectedElement?.type === 'penPath' && selectedAnchorIndex !== null) {
      setIsDraggingHandle(false);
      setActiveHandleType(null);
      return;
    }

    // Marquee finalization. Three outcomes:
    //   1. No marqueeRef → not our event, ignore.
    //   2. marqueeRef.isDragging === false → user clicked without dragging
    //      on empty canvas → clear selection (existing single-click behavior).
    //   3. marqueeRef.isDragging === true → compute intersections, select.
    const m = marqueeRef.current;
    if (m) {
      if (!m.isDragging) {
        // Click without drag = clear selection.
        onSelectElement(null);
      } else {
        const rect = {
          x: Math.min(m.startX, m.endX),
          y: Math.min(m.startY, m.endY),
          w: Math.abs(m.endX - m.startX),
          h: Math.abs(m.endY - m.startY),
        };
        // Candidate pool: when in isolation mode, restrict to the editing
        // group's descendants (leaf or nested group); otherwise the full
        // elements array. Prevents the marquee from selecting siblings of
        // the editing group while the user is "inside" it.
        const candidatePool = editingGroupId
          ? getDescendants(elements, editingGroupId)
          : (elements || []);
        const hits = candidatePool.filter((el) => elementIntersectsMarquee(el, rect));
        // Figma-style collapse: if every direct child of a group is in the
        // hit set, replace those children with the group itself (recurses
        // upward through nested groups). Single render frame — no flicker
        // between leaf-hit state and final group selection.
        const collapsed = collapseLeavesToGroups(elements, hits, editingGroupId);
        if (typeof onSelectMultiple === 'function') {
          const next = m.isShift
            ? // Shift+marquee = add hits to existing selection (de-duped)
              Array.from(new Map(
                [...(selectedElements || []), ...collapsed].map((el) => [el.id, el])
              ).values())
            : collapsed;
          onSelectMultiple(next);
        } else if (collapsed.length === 1) {
          // Graceful degradation: without onSelectMultiple we can at least
          // single-select if exactly one element was inside the marquee.
          onSelectElement(collapsed[0], false);
        } else if (collapsed.length === 0 && !m.isShift) {
          onSelectElement(null);
        }
      }
      marqueeRef.current = null;
      setMarqueeBox(null);
    }
  };

  // Global mouseup safety net: if the user starts a marquee on the canvas
  // and releases OUTSIDE the canvas (e.g. on the toolbar / sidebar), the
  // canvas's onMouseUp doesn't fire. This listener runs the same finalize
  // path so the marquee always closes cleanly. No-op when marqueeRef is null.
  //
  // Bind the listener exactly once and route through a ref so the latest
  // handleCanvasMouseUp closure runs every time — without the ref, dep-list
  // omissions (editingGroupId, isolation pool, etc.) leak in as stale state
  // and a marquee finalised via the window listener would compute hits
  // against the wrong elements snapshot.
  const handleCanvasMouseUpRef = useRef(handleCanvasMouseUp);
  handleCanvasMouseUpRef.current = handleCanvasMouseUp;
  useEffect(() => {
    const onWindowUp = () => {
      if (marqueeRef.current) {
        handleCanvasMouseUpRef.current();
      }
    };
    window.addEventListener('mouseup', onWindowUp);
    return () => window.removeEventListener('mouseup', onWindowUp);
  }, []);

    // Direct Selection tool - handle anchor point selection and dragging
    const handleAnchorPointMouseDown = (e, element, pointIndex) => {
      e.stopPropagation();
      if (activeTool !== 'direct-selection') return;

      setSelectedAnchorIndex(pointIndex);
      setIsDraggingAnchor(true);
      onSelectElement(element);
    };

    // Direct Selection tool - handle bezier handle dragging
    const handleHandleMouseDown = (e, element, pointIndex, handleType) => {
      e.stopPropagation();
      if (activeTool !== 'direct-selection') return;

      setSelectedAnchorIndex(pointIndex);
      setIsDraggingHandle(true);
      setActiveHandleType(handleType);
      onSelectElement(element);
    };

    // --- Bezier geometry utilities ---

    // Split a cubic bezier at parameter t using de Casteljau's algorithm.
    // Returns the new mid-point and updated control handles for both sub-segments.
    const splitCubicAt = (p0, cp1, cp2, p3, t) => {
      const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      const l1 = lerp(p0, cp1, t);
      const l2 = lerp(cp1, cp2, t);
      const l3 = lerp(cp2, p3, t);
      const l4 = lerp(l1, l2, t);
      const l5 = lerp(l2, l3, t);
      const l6 = lerp(l4, l5, t); // split point
      return {
        point: l6,
        handleOut1: l1,  // updated handleOut for the original start point
        handleIn2: l4,   // handleIn for the new mid-point
        handleOut2: l5,  // handleOut for the new mid-point
        handleIn3: l3,   // updated handleIn for the original end point
      };
    };

    // Find the closest parameter t on a cubic bezier to a given (px, py) via sampling.
    const closestTOnCubic = (p0, cp1, cp2, p3, px, py, steps = 60) => {
      let minDist = Infinity, closestT = 0.5;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const mt = 1 - t;
        const x = mt ** 3 * p0.x + 3 * mt ** 2 * t * cp1.x + 3 * mt * t ** 2 * cp2.x + t ** 3 * p3.x;
        const y = mt ** 3 * p0.y + 3 * mt ** 2 * t * cp1.y + 3 * mt * t ** 2 * cp2.y + t ** 3 * p3.y;
        const d = Math.hypot(x - px, y - py);
        if (d < minDist) { minDist = d; closestT = t; }
      }
      return { t: closestT, dist: minDist };
    };

    // Update path when anchor point is moved
    const updatePenPathFromPoints = (element, newPoints) => {
      let pathData = '';
      newPoints.forEach((point, index) => {
        if (index === 0) {
          pathData += `M ${point.x} ${point.y}`;
        } else {
          const prevPoint = newPoints[index - 1];
          const cp1 = prevPoint.handleOut || prevPoint;
          const cp2 = point.handleIn || point;
          pathData += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${point.x} ${point.y}`;
        }
      });

      if (element.closed) {
        const firstPoint = newPoints[0];
        const lastPoint = newPoints[newPoints.length - 1];
        const cp1 = lastPoint.handleOut || lastPoint;
        const cp2 = firstPoint.handleIn || firstPoint;
        pathData += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${firstPoint.x} ${firstPoint.y} Z`;
      }

      return pathData;
    };

    // --- Pen Tool helpers ---

    // Convert a mouse event to design-space coordinates
    const getCanvasCoords = useCallback((e) => {
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / zoom,
        y: (e.clientY - rect.top) / zoom,
      };
    }, [zoom]);

    // Build an SVG path string from in-progress points (absolute design coords)
    const buildPreviewPath = (pts) => {
      let d = '';
      pts.forEach((point, index) => {
        if (index === 0) {
          d += `M ${point.x} ${point.y}`;
        } else {
          const prev = pts[index - 1];
          const cp1 = prev.handleOut || prev;
          const cp2 = point.handleIn || point;
          d += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${point.x} ${point.y}`;
        }
      });
      return d;
    };

    // Finalize the in-progress pen path into a real element
    // Pass customPts to override penPoints (used when double-click trims the last point)
    const finalizePenPath = useCallback((closed, customPts) => {
      const pts = customPts ?? penPoints;
      if (pts.length < 2) {
        setPenPoints([]);
        setPenCursorPos(null);
        setPenDragAnchor(null);
        setPenIsDragging(false);
        setPenHandlePreview(null);
        return;
      }

      // Bounding box over all point coords + handles
      const allX = pts.flatMap(p => [p.x, p.handleIn?.x, p.handleOut?.x].filter(v => v != null));
      const allY = pts.flatMap(p => [p.y, p.handleIn?.y, p.handleOut?.y].filter(v => v != null));
      const minX = Math.min(...allX);
      const minY = Math.min(...allY);
      const maxX = Math.max(...allX);
      const maxY = Math.max(...allY);
      const w = Math.max(maxX - minX, 1);
      const h = Math.max(maxY - minY, 1);

      // Normalize to element-local coordinates
      const normalizedPoints = pts.map(p => ({
        x: p.x - minX,
        y: p.y - minY,
        handleIn: p.handleIn ? { x: p.handleIn.x - minX, y: p.handleIn.y - minY } : null,
        handleOut: p.handleOut ? { x: p.handleOut.x - minX, y: p.handleOut.y - minY } : null,
      }));

      // Build SVG path
      let pathData = '';
      normalizedPoints.forEach((point, index) => {
        if (index === 0) {
          pathData += `M ${point.x} ${point.y}`;
        } else {
          const prev = normalizedPoints[index - 1];
          const cp1 = prev.handleOut || prev;
          const cp2 = point.handleIn || point;
          pathData += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${point.x} ${point.y}`;
        }
      });
      if (closed && normalizedPoints.length > 1) {
        const first = normalizedPoints[0];
        const last = normalizedPoints[normalizedPoints.length - 1];
        const cp1 = last.handleOut || last;
        const cp2 = first.handleIn || first;
        pathData += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${first.x} ${first.y} Z`;
      }

      onAddElement({
        type: 'penPath',
        x: minX,
        y: minY,
        width: w,
        height: h,
        rotation: 0,
        path: pathData,
        points: normalizedPoints,
        closed,
        stroke: '#000000',
        strokeWidth: 2,
        fill: closed ? 'rgba(99,102,241,0.15)' : 'none',
        opacity: 1,
      });

      setPenPoints([]);
      setPenCursorPos(null);
      setPenDragAnchor(null);
      setPenIsDragging(false);
      setPenHandlePreview(null);
      onToolChange(TOOLS.SELECT);
    }, [penPoints, onAddElement, onToolChange]);

    // --- Pen Tool mouse handlers (used by the overlay div inside canvas) ---

    const handlePenMouseDown = useCallback((e) => {
      e.stopPropagation();
      const coords = getCanvasCoords(e);

      // Close path if clicking near the first anchor
      if (penPoints.length > 1) {
        const first = penPoints[0];
        const screenDist = Math.hypot((coords.x - first.x) * zoom, (coords.y - first.y) * zoom);
        if (screenDist < 12) {
          finalizePenPath(true);
          return;
        }
      }

      setPenDragAnchor(coords);
      setPenIsDragging(false);
      setPenHandlePreview(null);
    }, [getCanvasCoords, penPoints, zoom, finalizePenPath]);

    const handlePenMouseMove = useCallback((e) => {
      const coords = getCanvasCoords(e);
      setPenCursorPos(coords);

      if (penDragAnchor && e.buttons === 1) {
        const screenDist = Math.hypot(
          (coords.x - penDragAnchor.x) * zoom,
          (coords.y - penDragAnchor.y) * zoom,
        );
        if (screenDist > 4) {
          setPenIsDragging(true);
          setPenHandlePreview({
            handleOut: coords,
            handleIn: { x: 2 * penDragAnchor.x - coords.x, y: 2 * penDragAnchor.y - coords.y },
          });
        }
      }
    }, [getCanvasCoords, penDragAnchor, zoom]);

    const handlePenMouseUp = useCallback((_e) => {
      if (!penDragAnchor) return;

      const newPoint = {
        x: penDragAnchor.x,
        y: penDragAnchor.y,
        handleIn: penIsDragging && penHandlePreview ? penHandlePreview.handleIn : null,
        handleOut: penIsDragging && penHandlePreview ? penHandlePreview.handleOut : null,
      };

      setPenPoints(prev => [...prev, newPoint]);
      setPenDragAnchor(null);
      setPenIsDragging(false);
      setPenHandlePreview(null);
    }, [penDragAnchor, penIsDragging, penHandlePreview]);

    const handlePenDoubleClick = useCallback((e) => {
      e.stopPropagation();
      // By dblclick time, both mouseups have fired → penPoints has 2 extra points.
      // Trim the last one (added by the 2nd click) before finalizing.
      const trimmed = penPoints.slice(0, -1);
      if (trimmed.length >= 2) {
        finalizePenPath(false, trimmed);
      } else {
        setPenPoints([]);
        setPenCursorPos(null);
        setPenDragAnchor(null);
        setPenIsDragging(false);
        setPenHandlePreview(null);
      }
    }, [penPoints, finalizePenPath]);

    // --- Brush Tool ---
    // Catmull-Rom → cubic Bézier conversion (tension = 1/6).
    // Gives C² continuity — no kinks at joints even with very large strokeWidths.
    const buildSmoothPath = useCallback((points) => {
      if (points.length < 2) return '';
      if (points.length === 2) {
        const [a, b] = points;
        return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
      }
      let d = `M ${points[0].x} ${points[0].y}`;
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];
        // Control points derived from Catmull-Rom formula
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)},${cp2x.toFixed(2)} ${cp2y.toFixed(2)},${p2.x} ${p2.y}`;
      }
      return d;
    }, []);

    const handleBrushMouseDown = useCallback((e) => {
      e.stopPropagation();
      const coords = getCanvasCoords(e);
      const initial = [coords];
      brushPointsRef.current = initial;
      brushIsDrawingRef.current = true;
      setBrushIsDrawing(true);
      setBrushPoints(initial);
    }, [getCanvasCoords]);

    const handleBrushMouseMove = useCallback((e) => {
      const coords = getCanvasCoords(e);
      setBrushCursorPos(coords);
      if (!brushIsDrawingRef.current || e.buttons !== 1) return;

      const current = brushPointsRef.current;
      const last = current[current.length - 1];
      if (!last) return;
      const dist = Math.hypot(coords.x - last.x, coords.y - last.y);
      if (dist < 2) return; // skip points that are too close — reduces noise

      // Write to the ref immediately (always current, no stale closure)
      brushPointsRef.current = [...current, coords];

      // Throttle React state updates to at most one per animation frame
      if (!brushRafRef.current) {
        brushRafRef.current = requestAnimationFrame(() => {
          brushRafRef.current = null;
          setBrushPoints([...brushPointsRef.current]);
        });
      }
    }, [getCanvasCoords]);

    // Extracted so both onMouseUp and the window-level safety listener can call it.
    // Reads from refs — guaranteed to have the latest points even with stale closures.
    const finalizeBrushStroke = useCallback(() => {
      if (!brushIsDrawingRef.current) return; // guard: don't fire twice

      // Cancel any pending RAF before resetting state
      if (brushRafRef.current) {
        cancelAnimationFrame(brushRafRef.current);
        brushRafRef.current = null;
      }

      const pts = brushPointsRef.current;
      brushIsDrawingRef.current = false;
      brushPointsRef.current = [];
      setBrushIsDrawing(false);
      setBrushPoints([]);

      if (pts.length < 2) return;

      // All calculation and element creation happens HERE (outside any setState updater),
      // so React StrictMode's double-invocation of updaters cannot cause duplicate layers.
      const preset = BRUSH_PRESETS.find(p => p.id === selectedBrushPreset) || BRUSH_PRESETS[0];
      const xs = pts.map(p => p.x);
      const ys = pts.map(p => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);
      const pad = Math.ceil(brushSettings.size * preset.widthMultiplier * 2);
      const w = Math.max(maxX - minX, 1) + pad * 2;
      const h = Math.max(maxY - minY, 1) + pad * 2;
      const normalized = pts.map(p => ({ x: p.x - minX + pad, y: p.y - minY + pad }));
      const path = buildSmoothPath(normalized);

      if (path && typeof onAddElement === 'function') {
        onAddElement({
          type: 'brush',
          x: minX - pad,
          y: minY - pad,
          width: w,
          height: h,
          rotation: 0,
          path,
          brushType: selectedBrushPreset,
          stroke: brushSettings.color,
          strokeWidth: brushSettings.size,
          opacity: brushSettings.opacity,
        });
      }
    }, [brushSettings, selectedBrushPreset, buildSmoothPath, onAddElement]);

    const handleBrushMouseUp = useCallback((_e) => {
      finalizeBrushStroke();
    }, [finalizeBrushStroke]);

    // --- Eraser Tool ---

    // Find the topmost erasable element at a canvas-coordinate point (non-rotated bounding box hit test)
    const findElementAtCanvasPoint = useCallback((cx, cy) => {
      const els = elementsRef.current;
      const erasableTypes = ['image', 'shape', 'penPath', 'brush', 'text'];
      for (let i = els.length - 1; i >= 0; i--) {
        const el = els[i];
        if (el.locked || el.visible === false) continue;
        if (!erasableTypes.includes(el.type)) continue;
        if (cx >= el.x && cx <= el.x + el.width && cy >= el.y && cy <= el.y + el.height) {
          return el;
        }
      }
      return null;
    }, []);

    const handleEraserMouseDown = useCallback((e) => {
      e.stopPropagation();
      const coords = getCanvasCoords(e);
      setEraserCursorPos(coords);
      const target = findElementAtCanvasPoint(coords.x, coords.y);
      if (!target) return;

      // Convert canvas coords to element-local coords
      const localPt = { x: coords.x - target.x, y: coords.y - target.y };
      eraserPointsRef.current = [localPt];
      eraserIsDrawingRef.current = true;
      eraserTargetRef.current = target;
      setEraserIsDrawing(true);
      setEraserLivePoints([localPt]);
    }, [getCanvasCoords, findElementAtCanvasPoint]);

    const handleEraserMouseMove = useCallback((e) => {
      const coords = getCanvasCoords(e);
      setEraserCursorPos(coords);
      if (!eraserIsDrawingRef.current || e.buttons !== 1) return;

      const target = eraserTargetRef.current;
      if (!target) return;

      const localPt = { x: coords.x - target.x, y: coords.y - target.y };
      const current = eraserPointsRef.current;
      const last = current[current.length - 1];
      if (last && Math.hypot(localPt.x - last.x, localPt.y - last.y) < 2) return;

      eraserPointsRef.current = [...current, localPt];
      if (!eraserRafRef.current) {
        eraserRafRef.current = requestAnimationFrame(() => {
          eraserRafRef.current = null;
          setEraserLivePoints([...eraserPointsRef.current]);
        });
      }
    }, [getCanvasCoords]);

    const finalizeEraserStroke = useCallback(() => {
      if (!eraserIsDrawingRef.current) return;
      if (eraserRafRef.current) { cancelAnimationFrame(eraserRafRef.current); eraserRafRef.current = null; }

      const pts = eraserPointsRef.current;
      const target = eraserTargetRef.current;
      eraserIsDrawingRef.current = false;
      eraserPointsRef.current = [];
      eraserTargetRef.current = null;
      setEraserIsDrawing(false);
      setEraserLivePoints([]);

      if (pts.length < 2 || !target) return;

      const path = buildSmoothPath(pts);
      if (!path) return;

      const newStroke = {
        path,
        strokeWidth: eraserSettingsRef.current.size,
        hardness: eraserSettingsRef.current.hardness,
        opacity: eraserSettingsRef.current.opacity,
      };
      const existing = target.eraserStrokes || [];
      onUpdateElement(target.id, { eraserStrokes: [...existing, newStroke] });
    }, [buildSmoothPath, onUpdateElement]);

    const handleEraserMouseUp = useCallback(() => {
      finalizeEraserStroke();
    }, [finalizeEraserStroke]);

    const handleEraserMouseLeave = useCallback(() => {
      setEraserCursorPos(null);
    }, []);

    // --- Direct Selection: add anchor by clicking on a path segment ---
    const handleSegmentClick = (e, element, segmentIndex) => {
      e.stopPropagation();
      if (activeTool !== 'direct-selection') return;

      const rect = canvasRef.current.getBoundingClientRect();
      const localX = (e.clientX - rect.left) / zoom - element.x;
      const localY = (e.clientY - rect.top) / zoom - element.y;

      const pts = element.points;
      const nextIdx = element.closed ? (segmentIndex + 1) % pts.length : segmentIndex + 1;
      if (nextIdx >= pts.length) return;

      const p0  = { x: pts[segmentIndex].x, y: pts[segmentIndex].y };
      const cp1 = pts[segmentIndex].handleOut || p0;
      const p3  = { x: pts[nextIdx].x, y: pts[nextIdx].y };
      const cp2 = pts[nextIdx].handleIn || p3;

      const { t } = closestTOnCubic(p0, cp1, cp2, p3, localX, localY);
      const clampedT = Math.max(0.05, Math.min(0.95, t));
      const split = splitCubicAt(p0, cp1, cp2, p3, clampedT);

      const newPoints = pts.map(pt => ({ ...pt }));
      // Update handleOut of the segment start point
      newPoints[segmentIndex] = { ...newPoints[segmentIndex], handleOut: split.handleOut1 };

      const insertIdx = segmentIndex + 1;
      newPoints.splice(insertIdx, 0, {
        x: split.point.x,
        y: split.point.y,
        handleIn: split.handleIn2,
        handleOut: split.handleOut2,
      });

      // Update handleIn of the segment end point (account for closed-path wrap-around)
      const actualNextIdx = element.closed && nextIdx === 0 ? 0 : insertIdx + 1;
      if (actualNextIdx < newPoints.length) {
        newPoints[actualNextIdx] = { ...newPoints[actualNextIdx], handleIn: split.handleIn3 };
      }

      const newPath = updatePenPathFromPoints(element, newPoints);
      onUpdateElement(element.id, { points: newPoints, path: newPath });
      setSelectedAnchorIndex(insertIdx);
    };

    // --- Direct Selection: delete the currently selected anchor point ---
    const deleteSelectedAnchorPoint = () => {
      if (!selectedElement || selectedElement.type !== 'penPath' || selectedAnchorIndex === null) return;
      const pts = selectedElement.points;
      if (pts.length <= 2) {
        // Too few points remaining — delete the whole element
        onDeleteElement(selectedElement.id);
        onSelectElement(null);
        setSelectedAnchorIndex(null);
        return;
      }
      const newPoints = pts.filter((_, i) => i !== selectedAnchorIndex);
      const newPath = updatePenPathFromPoints(selectedElement, newPoints);
      onUpdateElement(selectedElement.id, { points: newPoints, path: newPath });
      setSelectedAnchorIndex(null);
    };

    const handleCreateMask = () => {
      if (!selectedElements || selectedElements.length < 2) return;

      // Figma style: Top-most object in the array (highest z-index) becomes the mask
      const sortedSelected = [...selectedElements].sort((a, b) => {
        const idxA = elementsRef.current.findIndex(el => el.id === a.id);
        const idxB = elementsRef.current.findIndex(el => el.id === b.id);
        return idxB - idxA;
      });

      const maskShape = sortedSelected[0];
      const contentObjects = sortedSelected.slice(1);
      const contentIds = contentObjects.map(el => el.id);

      // Release any existing mask relationship on content elements first
      contentObjects.forEach(contentEl => {
        if (contentEl.clippedTo && contentEl.clippedTo !== maskShape.id) {
          onUpdateElement(contentEl.clippedTo, { isMaskShape: false, maskChildIds: null });
        }
        onUpdateElement(contentEl.id, { clippedTo: maskShape.id });
      });

      onUpdateElement(maskShape.id, {
        isMaskShape: true,
        maskChildIds: contentIds,
        maskChildId: null,
        isClipBase: null,
        clipChildId: null,
      });

      toast({
        title: 'Mask Created',
        description: `Masked ${contentObjects.length} layer(s) into shape`
      });
    };

    const handleReleaseMask = () => {
      if (!selectedElement) return;

      const elements = elementsRef.current;

      if (selectedElement.isMaskShape || selectedElement.isClipBase) {
        // Release all children
        const childIds = selectedElement.maskChildIds ||
          (selectedElement.maskChildId ? [selectedElement.maskChildId] : []).concat(
            selectedElement.clipChildId ? [selectedElement.clipChildId] : []
          );
        childIds.forEach(childId => {
          onUpdateElement(childId, { clippedTo: null, clipMask: null });
        });
        onUpdateElement(selectedElement.id, {
          isMaskShape: false,
          maskChildIds: null,
          maskChildId: null,
          isClipBase: false,
          clipChildId: null,
        });
        toast({ title: 'Mask Released', description: 'Separated shape from contents' });
      } else if (selectedElement.clippedTo || selectedElement.clipMask) {
        // Release this content element from its mask
        const maskShapeId = selectedElement.clippedTo || selectedElement.clipMask?.clipBaseId;
        onUpdateElement(selectedElement.id, { clippedTo: null, clipMask: null });

        if (maskShapeId) {
          const maskShape = elements.find(el => el.id === maskShapeId);
          if (maskShape) {
            const currentIds = maskShape.maskChildIds || (maskShape.maskChildId ? [maskShape.maskChildId] : []);
            const newIds = currentIds.filter(id => id !== selectedElement.id);
            if (newIds.length === 0) {
              onUpdateElement(maskShapeId, { isMaskShape: false, maskChildIds: null, maskChildId: null, isClipBase: false, clipChildId: null });
            } else {
              onUpdateElement(maskShapeId, { maskChildIds: newIds });
            }
          }
        }
        toast({ title: 'Layer Released', description: 'Removed layer from mask' });
      }
    };

    const handleElementContextMenu = (e, element) => {
      e.preventDefault();
      e.stopPropagation();
      {
        const _groupTarget = getOutermostAncestor(elements, element.id, editingGroupId) || element;
        onSelectElement(_groupTarget, false);
      }
      setContextMenu({ x: e.clientX, y: e.clientY });
    };

    const closeContextMenu = () => setContextMenu(null);

    const handleElementMouseDown = (e, element) => {
      if (element.locked) return;
      e.stopPropagation();
      closeContextMenu();

      // Clipping Mask tool: toggle element into/out of multi-selection without dragging
      if (activeTool === TOOLS.MASK) {
        {
          const _groupTarget = getOutermostAncestor(elements, element.id, editingGroupId) || element;
          onSelectElement(_groupTarget, true); // always shift-style toggle
        }
        return;
      }

      // Save history before any drag/move starts (called once per interaction)
      if (!e.shiftKey) onInteractionStart?.();

      // Clear text editing if clicking a different element. Route through
      // the contentEditable's native .blur() so its onBlur commits the typed
      // text BEFORE React unmounts the edit subtree (the unmount otherwise
      // races the focus transition and the synthetic onBlur never fires).
      if (editingTextId && editingTextId !== element.id) {
        exitTextEdit();
      }

      // Pass shift key state for multi-selection support.
      // Also intercept "double-click on a group" to enter isolation mode:
      // two clicks on the same resolved group target within DBL_CLICK_MS
      // → onSetEditingGroupId(group.id) + clear selection + return early
      // (skipping drag-init so no rogue isDragging state).
      {
        const _groupTarget = getOutermostAncestor(elements, element.id, editingGroupId) || element;
        const now = performance.now();
        const prev = lastElementClickRef.current;
        if (
          !e.shiftKey &&
          _groupTarget.type === 'group' &&
          prev.id === _groupTarget.id &&
          now - prev.time < DBL_CLICK_MS &&
          typeof onSetEditingGroupId === 'function'
        ) {
          onSetEditingGroupId(_groupTarget.id);
          onSelectElement(null);
          lastElementClickRef.current = { id: null, time: 0 };
          return;
        }
        lastElementClickRef.current = { id: _groupTarget.id, time: now };
        onSelectElement(_groupTarget, e.shiftKey);
      }

      // Only start dragging if not holding shift (to allow multi-select without drag)
      if (!e.shiftKey) {
        setIsDragging(true);
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const rawMouseX = (e.clientX - rect.left) / zoom;
        const rawMouseY = (e.clientY - rect.top) / zoom;
        // Seed last-mouse-pos ref for the group-drag path (groups have no x/y;
        // the drag-move handler branches on selectedElement.type === 'group'
        // and uses frame-to-frame mouse delta instead of selectedElement.x).
        dragLastMousePosRef.current = { x: rawMouseX, y: rawMouseY };
        setDragOffset({
          x: rawMouseX - element.x,
          y: rawMouseY - element.y,
        });
      }
    };

    const handleResizeMouseDown = (e, handle) => {
      if (!selectedElement || selectedElement.locked) return;
      e.stopPropagation();
      onInteractionStart?.();
      setIsResizing(true);
      setResizeHandle(handle);
    };

    const handleRotateMouseDown = (e) => {
      if (!selectedElement || selectedElement.locked) return;
      e.stopPropagation();
      onInteractionStart?.();
      setIsRotating(true);
      // Group rotation: snapshot the START state so per-frame rotation applies
      // totalDelta to the snapshot rather than accumulating per-frame matrix
      // multiplications (which would drift with floating point error).
      if (selectedElement.type === 'group') {
        const oldBounds = getGroupBounds(elements, selectedElement.id);
        if (!oldBounds) return;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left) / zoom;
        const mouseY = (e.clientY - rect.top) / zoom;
        const cx = oldBounds.x + oldBounds.width / 2;
        const cy = oldBounds.y + oldBounds.height / 2;
        const startAngleRad = Math.atan2(mouseY - cy, mouseX - cx);
        const leaves = expandSelectionToLeaves(elements, [selectedElement.id]);
        // Deep-copy the leaf snapshot fields we'll rotate from.
        const leafSnapshots = leaves.map((leaf) => ({
          id: leaf.id,
          x: leaf.x,
          y: leaf.y,
          width: leaf.width,
          height: leaf.height,
          rotation: leaf.rotation,
        }));
        groupRotateInitRef.current = {
          startAngleRad,
          boundsCenter: { x: cx, y: cy },
          leafSnapshots,
        };
      }
    };

    const handleMouseMove = (e) => {
      if (!selectedElement) return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left) / zoom;
      const mouseY = (e.clientY - rect.top) / zoom;

      if (isDragging) {
        // ── Group drag: pure delta cascade across all leaf descendants ──
        // Groups have no x/y/width/height of their own (no own bbox), so the
        // standard snap-and-absolute-position path would NaN-bomb. Use frame-
        // to-frame mouse delta and apply uniformly to every leaf. Snap-to-
        // guides for grouped elements is deferred to a later task (would need
        // to snap on the group's computed bbox).
        if (selectedElement?.type === 'group') {
          const last = dragLastMousePosRef.current;
          if (!last) return;
          const dx = mouseX - last.x;
          const dy = mouseY - last.y;
          dragLastMousePosRef.current = { x: mouseX, y: mouseY };
          const leaves = expandSelectionToLeaves(elements, [selectedElement.id]);
          for (const leaf of leaves) {
            onUpdateElement(leaf.id, { x: leaf.x + dx, y: leaf.y + dy });
          }
          // Invalidate ancestor-group bounds cache so the outline + any future
          // resize/rotate handles read fresh bounds on next render.
          for (const leaf of leaves) {
            invalidateBoundsCacheUpward(elements, leaf.id);
          }
          return;
        }

        let x = mouseX - dragOffset.x;
        let y = mouseY - dragOffset.y;
        let snapped = snapToGuides(x, y, selectedElement.width, selectedElement.height);
        // Smart alignment: page center + object edges/centers. Only for a
        // single moving element (multi-select keeps ruler snap only).
        if (selectedElements?.length <= 1) {
          const movingRect = { x: snapped.x, y: snapped.y, width: selectedElement.width, height: selectedElement.height };
          const statics = elements
            .filter((el) => el.id !== selectedElement.id && el.type !== 'group')
            .map((el) => ({ x: el.x, y: el.y, width: el.width, height: el.height }));
          const s = computeSnap(movingRect, statics, { width: canvasSize.width, height: canvasSize.height }, 8);
          snapped = { x: snapped.x + s.dx, y: snapped.y + s.dy };
          setSmartGuides({ v: s.guidesV, h: s.guidesH });
        }
        const deltaX = snapped.x - selectedElement.x;
        const deltaY = snapped.y - selectedElement.y;
        // Multi-layer selection: move all selected by the same delta
        const toMove = selectedElements?.length > 1 ? selectedElements : [selectedElement];
        toMove.forEach((el) => {
          if (el.id === selectedElement.id) {
            onUpdateElement(el.id, { x: snapped.x, y: snapped.y });
          } else {
            const elSnapped = snapToGuides(el.x + deltaX, el.y + deltaY, el.width, el.height);
            onUpdateElement(el.id, { x: elSnapped.x, y: elSnapped.y });
          }
        });

        // Mask group movement: when dragging the mask shape alone, also move ALL its content children
        if (selectedElements?.length <= 1) {
          const isMask = selectedElement.isMaskShape || selectedElement.isClipBase;
          if (isMask) {
            const childIds = selectedElement.maskChildIds ||
              (selectedElement.maskChildId ? [selectedElement.maskChildId] : []).concat(
                selectedElement.clipChildId ? [selectedElement.clipChildId] : []
              );
            childIds.forEach(childId => {
              const childEl = elementsRef.current.find(el => el.id === childId);
              if (childEl) {
                onUpdateElement(childId, { x: childEl.x + deltaX, y: childEl.y + deltaY });
              }
            });
          }
        }
      } else if (isResizing && resizeHandle) {
        // ── Group resize: scale all leaf descendants around the appropriate
        // anchor (opposite edge of the dragged handle) using the same
        // getGroupBounds source as the outline render — so outline and
        // descendants never drift apart. Bypasses the single-element path
        // which would NaN-bomb on selectedElement.x/width (undefined for groups).
        //
        // Anchor selection per handle:
        //   e → anchor at left edge (bounds.x stays put as scale origin)
        //   w → anchor at right edge (bounds.x + bounds.width stays put)
        //   s → anchor at top edge
        //   n → anchor at bottom edge
        // applyResizeToDescendants always scales around bounds.x/y, so we
        // pass a synthetic bounds object whose .x/.y IS the anchor point.
        //
        // Float-drift note: per-frame multiplicative scale accumulates floating
        // point error across many mouse-move events. Acceptable for now; a
        // snapshot-at-drag-start strategy can address this later if needed.
        if (selectedElement?.type === 'group') {
          const oldBounds = getGroupBounds(elements, selectedElement.id);
          if (!oldBounds || oldBounds.width === 0 || oldBounds.height === 0) return;

          let newWidth = oldBounds.width;
          let newHeight = oldBounds.height;

          if (resizeHandle.includes('e')) {
            newWidth = Math.max(20, mouseX - oldBounds.x);
          }
          if (resizeHandle.includes('w')) {
            newWidth = Math.max(20, oldBounds.width + (oldBounds.x - mouseX));
          }
          if (resizeHandle.includes('s')) {
            newHeight = Math.max(20, mouseY - oldBounds.y);
          }
          if (resizeHandle.includes('n')) {
            newHeight = Math.max(20, oldBounds.height + (oldBounds.y - mouseY));
          }

          const scaleX = newWidth / oldBounds.width;
          const scaleY = newHeight / oldBounds.height;

          // Anchor at the OPPOSITE edge from the dragged handle.
          const anchorX = resizeHandle.includes('w')
            ? oldBounds.x + oldBounds.width
            : oldBounds.x;
          const anchorY = resizeHandle.includes('n')
            ? oldBounds.y + oldBounds.height
            : oldBounds.y;
          const syntheticBounds = { x: anchorX, y: anchorY, width: oldBounds.width, height: oldBounds.height };

          const leaves = expandSelectionToLeaves(elements, [selectedElement.id]);
          const updates = applyResizeToDescendants(leaves, syntheticBounds, scaleX, scaleY);
          updates.forEach((patch, id) => onUpdateElement(id, patch));
          // Invalidate up: next getGroupBounds read (outline render, next frame)
          // sees the post-resize leaf positions.
          for (const leaf of leaves) {
            invalidateBoundsCacheUpward(elements, leaf.id);
          }
          return;
        }

        let newWidth = selectedElement.width;
        let newHeight = selectedElement.height;
        let newX = selectedElement.x;
        let newY = selectedElement.y;

        if (resizeHandle.includes('e')) {
          newWidth = Math.max(20, mouseX - selectedElement.x);
        }
        if (resizeHandle.includes('w')) {
          newWidth = Math.max(20, selectedElement.width + (selectedElement.x - mouseX));
        }
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(20, mouseY - selectedElement.y);
        }
        if (resizeHandle.includes('n')) {
          newHeight = Math.max(20, selectedElement.height + (selectedElement.y - mouseY));
        }

        // Aspect lock (element flag or Shift override) constrains the box to the
        // original ratio. penPath scales its own points and opts out.
        const lockAspect = (isAspectLocked(selectedElement) || e.shiftKey)
          && selectedElement.type !== 'penPath';
        if (lockAspect) {
          const c = constrainResizeToAspect(selectedElement.width, selectedElement.height, newWidth, newHeight);
          newWidth = Math.max(20, c.width);
          newHeight = Math.max(20, c.height);
        }

        // Derive anchored position from the (possibly constrained) size so the
        // opposite edge stays fixed for w/n handles.
        if (resizeHandle.includes('w')) {
          newX = selectedElement.x + selectedElement.width - newWidth;
        }
        if (resizeHandle.includes('n')) {
          newY = selectedElement.y + selectedElement.height - newHeight;
        }

        // For penPath elements, scale the points when resizing
        if (selectedElement.type === 'penPath' && selectedElement.points) {
          const scaleX = newWidth / selectedElement.width;
          const scaleY = newHeight / selectedElement.height;

          // Scale all points and their handles
          const scaledPoints = selectedElement.points.map(point => ({
            x: point.x * scaleX,
            y: point.y * scaleY,
            handleIn: point.handleIn ? {
              x: point.handleIn.x * scaleX,
              y: point.handleIn.y * scaleY
            } : null,
            handleOut: point.handleOut ? {
              x: point.handleOut.x * scaleX,
              y: point.handleOut.y * scaleY
            } : null
          }));

          const newPath = updatePenPathFromPoints({ ...selectedElement, closed: selectedElement.closed }, scaledPoints);

          onUpdateElement(selectedElement.id, {
            width: newWidth,
            height: newHeight,
            x: newX,
            y: newY,
            points: scaledPoints,
            path: newPath
          });
        } else {
          onUpdateElement(selectedElement.id, {
            width: newWidth,
            height: newHeight,
            x: newX,
            y: newY
          });
        }
      } else if (isRotating) {
        // ── Group rotation: snapshot-at-start strategy to avoid float drift.
        // Each frame computes totalDelta = currentAngle - startAngle and
        // applies it to the SNAPSHOTTED leaf positions, not the current
        // (already-rotated) state. Mathematically: snapshot-fixed pivot +
        // single rotation matrix per frame, no accumulation.
        //
        // applyRotateToDescendants reads bounds.x + bounds.width/2 to find
        // the pivot. We pass a synthetic bounds whose .x/.y IS the pivot
        // and width/height = 0 — same result, no need to store full bounds.
        if (selectedElement?.type === 'group') {
          const init = groupRotateInitRef.current;
          if (!init) return;
          const { startAngleRad, boundsCenter, leafSnapshots } = init;
          const currentAngleRad = Math.atan2(mouseY - boundsCenter.y, mouseX - boundsCenter.x);
          const totalDeltaDeg = ((currentAngleRad - startAngleRad) * 180) / Math.PI;
          const pivotBounds = { x: boundsCenter.x, y: boundsCenter.y, width: 0, height: 0 };
          const updates = applyRotateToDescendants(leafSnapshots, pivotBounds, totalDeltaDeg);
          updates.forEach((patch, id) => onUpdateElement(id, patch));
          // Invalidate group bounds cache so the outline re-renders fresh
          // on the next frame, following the rotating children.
          for (const leaf of leafSnapshots) {
            invalidateBoundsCacheUpward(elements, leaf.id);
          }
          return;
        }

        const centerX = selectedElement.x + selectedElement.width / 2;
        const centerY = selectedElement.y + selectedElement.height / 2;
        const angle = Math.atan2(mouseY - centerY, mouseX - centerX) * (180 / Math.PI);
        onUpdateElement(selectedElement.id, { rotation: angle + 90 });
      } else if (isDraggingAnchor && selectedElement?.type === 'penPath' && selectedAnchorIndex !== null) {
        // Move anchor point (Direct Selection tool)
        const localX = mouseX - selectedElement.x;
        const localY = mouseY - selectedElement.y;

        const newPoints = [...selectedElement.points];
        const point = newPoints[selectedAnchorIndex];
        const dx = localX - point.x;
        const dy = localY - point.y;

        // Move anchor and its handles together
        newPoints[selectedAnchorIndex] = {
          ...point,
          x: localX,
          y: localY,
          handleIn: point.handleIn ? { x: point.handleIn.x + dx, y: point.handleIn.y + dy } : null,
          handleOut: point.handleOut ? { x: point.handleOut.x + dx, y: point.handleOut.y + dy } : null
        };

        const newPath = updatePenPathFromPoints(selectedElement, newPoints);
        onUpdateElement(selectedElement.id, { points: newPoints, path: newPath });
      } else if (isDraggingHandle && selectedElement?.type === 'penPath' && selectedAnchorIndex !== null) {
        // Move bezier handle (Direct Selection tool)
        const localX = mouseX - selectedElement.x;
        const localY = mouseY - selectedElement.y;

        const newPoints = [...selectedElement.points];
        const point = { ...newPoints[selectedAnchorIndex] };

        if (activeHandleType === 'in') {
          point.handleIn = { x: localX, y: localY };
        } else if (activeHandleType === 'out') {
          point.handleOut = { x: localX, y: localY };
        }

        newPoints[selectedAnchorIndex] = point;
        const newPath = updatePenPathFromPoints(selectedElement, newPoints);
        onUpdateElement(selectedElement.id, { points: newPoints, path: newPath });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setIsRotating(false);
      setResizeHandle(null);
      setIsDraggingAnchor(false);
      setIsDraggingHandle(false);
      setActiveHandleType(null);
      clearSmartGuides();
    };

    // Move anchor point with arrow keys
    const moveAnchorPoint = (dx, dy) => {
      if (!selectedElement || selectedElement.type !== 'penPath' || selectedAnchorIndex === null) return;

      const newPoints = [...selectedElement.points];
      const point = newPoints[selectedAnchorIndex];

      newPoints[selectedAnchorIndex] = {
        ...point,
        x: point.x + dx,
        y: point.y + dy,
        handleIn: point.handleIn ? { x: point.handleIn.x + dx, y: point.handleIn.y + dy } : null,
        handleOut: point.handleOut ? { x: point.handleOut.x + dx, y: point.handleOut.y + dy } : null
      };

      const newPath = updatePenPathFromPoints(selectedElement, newPoints);
      onUpdateElement(selectedElement.id, { points: newPoints, path: newPath });
    };

    useEffect(() => {
      if (isDragging || isResizing || isRotating || isDraggingAnchor || isDraggingHandle) {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isDragging, isResizing, isRotating, isDraggingAnchor, isDraggingHandle, selectedElement, selectedElements, dragOffset, zoom, resizeHandle, selectedAnchorIndex, activeHandleType]);

    // Safety net: if the user releases the mouse button outside the canvas overlay,
    // the overlay's onMouseUp won't fire. This window listener catches that case so the
    // stroke is always finalized and never left in a stuck "drawing" state.
    useEffect(() => {
      if (!brushIsDrawing) return;
      const onWindowMouseUp = () => finalizeBrushStroke();
      window.addEventListener('mouseup', onWindowMouseUp);
      return () => window.removeEventListener('mouseup', onWindowMouseUp);
    }, [brushIsDrawing, finalizeBrushStroke]);

    // Keep eraserSettingsRef in sync so finalizeEraserStroke always reads latest settings
    useEffect(() => {
      eraserSettingsRef.current = eraserSettings;
    }, [eraserSettings]);

    // Safety net window mouseup for eraser (mirrors brush pattern)
    useEffect(() => {
      if (!eraserIsDrawing) return;
      const onWindowMouseUp = () => finalizeEraserStroke();
      window.addEventListener('mouseup', onWindowMouseUp);
      return () => window.removeEventListener('mouseup', onWindowMouseUp);
    }, [eraserIsDrawing, finalizeEraserStroke]);

    // Keyboard controls
    useEffect(() => {
      const handleKeyDown = (e) => {
        // Don't handle keyboard events when editing text
        if (editingTextId) return;

        // Keys typed into a form field belong to that field. Without this,
        // typing a hex colour in the properties panel fired the tool shortcuts
        // (E → eraser, B → brush) and Backspace deleted the selected element.
        const t = e.target;
        const inEditable = t && (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT');
        if (inEditable) return;

        // P key: activate pen tool
        if ((e.key === 'p' || e.key === 'P') && !e.ctrlKey && !e.metaKey) {
          if (typeof onToolChange === 'function') onToolChange(TOOLS.PEN);
          setPenPoints([]);
          setPenCursorPos(null);
          setPenDragAnchor(null);
          return;
        }

        // B key: activate brush tool
        if ((e.key === 'b' || e.key === 'B') && !e.ctrlKey && !e.metaKey) {
          if (typeof onToolChange === 'function') onToolChange(TOOLS.BRUSH);
          setBrushPoints([]);
          setBrushCursorPos(null);
          setBrushIsDrawing(false);
          return;
        }

        // E key: activate eraser tool
        if ((e.key === 'e' || e.key === 'E') && !e.ctrlKey && !e.metaKey) {
          if (typeof onToolChange === 'function') onToolChange(TOOLS.ERASER);
          setEraserCursorPos(null);
          return;
        }

        // M key: activate clipping mask tool
        if ((e.key === 'm' || e.key === 'M') && !e.ctrlKey && !e.metaKey) {
          if (typeof onToolChange === 'function') onToolChange(TOOLS.MASK);
          setMaskHoverElementId(null);
          return;
        }

        // Escape: cancel in-progress pen drawing
        if (e.key === 'Escape' && penPoints.length > 0) {
          setPenPoints([]);
          setPenCursorPos(null);
          setPenDragAnchor(null);
          setPenIsDragging(false);
          setPenHandlePreview(null);
          return;
        }

        const hasSelection = selectedElement || (selectedElements?.length > 0);
        if (!hasSelection) return;

        // Delete key: in direct-selection mode, delete anchor point first
        if ((e.key === 'Delete' || e.key === 'Backspace') && activeTool === 'direct-selection' && selectedAnchorIndex !== null && selectedElement?.type === 'penPath') {
          e.preventDefault();
          deleteSelectedAnchorPoint();
          return;
        }

        // Delete key: delete all selected when multi-select, else single
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          if (selectedElements?.length > 1 && onDeleteSelectedElements) {
            onDeleteSelectedElements();
          } else if (selectedElement) {
            onDeleteElement(selectedElement.id);
          }
          return;
        }

        // Arrow keys for movement.
        //
        // The step is expressed in SCREEN pixels and converted to canvas
        // pixels. A fixed 1-canvas-px step is invisible on a print-sized
        // document: on a 2550×3300 sheet viewed at 28% it moves the element a
        // quarter of a screen pixel, which reads as "the arrow keys do
        // nothing". At 100% zoom this still resolves to exactly 1px.
        const screenStep = e.shiftKey ? 10 : 1;
        const moveAmount = Math.max(1, Math.round(screenStep / (zoom || 1)));
        let updates = {};

        // If Direct Selection tool is active and an anchor is selected, move the anchor (single only)
        if (activeTool === 'direct-selection' && selectedAnchorIndex !== null && selectedElement?.type === 'penPath') {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              moveAnchorPoint(-moveAmount, 0);
              return;
            case 'ArrowRight':
              e.preventDefault();
              moveAnchorPoint(moveAmount, 0);
              return;
            case 'ArrowUp':
              e.preventDefault();
              moveAnchorPoint(0, -moveAmount);
              return;
            case 'ArrowDown':
              e.preventDefault();
              moveAnchorPoint(0, moveAmount);
              return;
            default:
              break;
          }
        }

        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            updates = { x: -moveAmount, y: 0 };
            break;
          case 'ArrowRight':
            e.preventDefault();
            updates = { x: moveAmount, y: 0 };
            break;
          case 'ArrowUp':
            e.preventDefault();
            updates = { x: 0, y: -moveAmount };
            break;
          case 'ArrowDown':
            e.preventDefault();
            updates = { x: 0, y: moveAmount };
            break;
          default:
            return;
        }

        if (Object.keys(updates).length > 0) {
          const dx = updates.x ?? 0;
          const dy = updates.y ?? 0;

          // Single-element nudge. Smart guides are still computed so the user
          // sees when the nudge lands on an alignment, but the snap offset is
          // NOT applied: with an 8px snap tolerance and a 1px step, snapping
          // pulled the element straight back to where it started, so nothing
          // ever moved. A keyboard nudge is an explicit, exact request —
          // dragging is where snapping belongs.
          if ((selectedElements?.length ?? 0) <= 1 && selectedElement && selectedElement.type !== 'group') {
            const moving = { x: selectedElement.x + dx, y: selectedElement.y + dy, width: selectedElement.width, height: selectedElement.height };
            const statics = elementsRef.current
              .filter((el) => el.id !== selectedElement.id && el.type !== 'group')
              .map((el) => ({ x: el.x, y: el.y, width: el.width, height: el.height }));
            const s = computeSnap(moving, statics, { width: canvasSize.width, height: canvasSize.height }, 8);
            onUpdateElement(selectedElement.id, { x: moving.x, y: moving.y });
            invalidateBoundsCacheUpward(elements, selectedElement.id);
            if (s.guidesV.length || s.guidesH.length) {
              setSmartGuides({ v: s.guidesV, h: s.guidesH });
              clearTimeout(snapGuideTimerRef.current);
              snapGuideTimerRef.current = setTimeout(() => clearSmartGuides(), 600);
            } else {
              clearSmartGuides();
            }
            return;
          }

          // Expand any selected groups to their leaf descendants — groups have
          // no x/y to nudge directly; nudging cascades to leaves uniformly.
          const selectionIds = (selectedElements?.length > 1
            ? selectedElements
            : (selectedElement ? [selectedElement] : [])
          ).map((el) => el.id);
          const toMove = expandSelectionToLeaves(elements, selectionIds);
          toMove.forEach((el) => {
            onUpdateElement(el.id, { x: el.x + dx, y: el.y + dy });
          });
          for (const el of toMove) {
            invalidateBoundsCacheUpward(elements, el.id);
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
      // `zoom` is a dependency because the nudge step is derived from it — a
      // stale closure would keep nudging at the zoom level the listener was
      // bound at.
    }, [selectedElement, selectedElements, onUpdateElement, onDeleteElement, onDeleteSelectedElements, editingTextId, onToolChange, penPoints, activeTool, selectedAnchorIndex, deleteSelectedAnchorPoint, zoom]);

    const renderResizeHandles = (element) => {
      if (selectedElement?.id !== element.id || element.locked) return null;

      const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
      const handlePositions = {
        nw: { top: -4, left: -4, cursor: 'nwse-resize' },
        n: { top: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
        ne: { top: -4, right: -4, cursor: 'nesw-resize' },
        e: { top: '50%', right: -4, transform: 'translateY(-50%)', cursor: 'ew-resize' },
        se: { bottom: -4, right: -4, cursor: 'nwse-resize' },
        s: { bottom: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
        sw: { bottom: -4, left: -4, cursor: 'nesw-resize' },
        w: { top: '50%', left: -4, transform: 'translateY(-50%)', cursor: 'ew-resize' }
      };

      return (
        <>
          {handles.map(handle => (
            <div
              key={handle}
              onMouseDown={(e) => handleResizeMouseDown(e, handle)}
              style={{
                position: 'absolute',
                width: 8,
                height: 8,
                backgroundColor: '#6366f1',
                border: '2px solid white',
                borderRadius: '50%',
                ...handlePositions[handle],
                zIndex: 10
              }}
            />
          ))}
          {/* Rotate handle */}
          <div
            onMouseDown={handleRotateMouseDown}
            style={{
              position: 'absolute',
              top: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 20,
              height: 20,
              cursor: 'grab',
              zIndex: 10
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </div>
        </>
      );
    };

    // ---------------------------------------------------------------------------
    // Clipping Mask Helpers
    // ---------------------------------------------------------------------------

    // Returns JSX SVG elements for use inside a <clipPath> definition.
    // All coordinates are in content-element-local space (offsetX/offsetY shifts mask→content).
    const getMaskShapeSVGContent = (maskEl, contentEl) => {
      const offsetX = maskEl.x - contentEl.x;
      const offsetY = maskEl.y - contentEl.y;
      const w = maskEl.width;
      const h = maskEl.height;
      const rotation = maskEl.rotation || 0;

      const wrapWithRotation = (child) => {
        if (rotation === 0) return child;
        const cx = offsetX + w / 2;
        const cy = offsetY + h / 2;
        return <g transform={`rotate(${rotation} ${cx} ${cy})`}>{child}</g>;
      };

      if (maskEl.type === 'shape') {
        switch (maskEl.shape) {
          case 'rect':
          case 'roundedRect': {
            const radius = maskEl.cornerRadius || (maskEl.shape === 'roundedRect' ? 12 : 0);
            const rx = Math.min(radius, w / 2);
            return wrapWithRotation(<rect x={offsetX} y={offsetY} width={w} height={h} rx={rx} ry={rx} />);
          }
          case 'circle':
            return wrapWithRotation(<ellipse cx={offsetX + w / 2} cy={offsetY + h / 2} rx={w / 2} ry={h / 2} />);
          case 'triangle': {
            const pts = `${offsetX + w / 2},${offsetY} ${offsetX},${offsetY + h} ${offsetX + w},${offsetY + h}`;
            return wrapWithRotation(<polygon points={pts} />);
          }
          case 'diamond': {
            const pts = `${offsetX + w / 2},${offsetY} ${offsetX + w},${offsetY + h / 2} ${offsetX + w / 2},${offsetY + h} ${offsetX},${offsetY + h / 2}`;
            return wrapWithRotation(<polygon points={pts} />);
          }
          case 'hexagon': {
            const cx = offsetX + w / 2; const cy = offsetY + h / 2;
            const r = Math.min(w, h) / 2;
            let pts = '';
            for (let i = 0; i < 6; i++) { const a = (i * 60 - 90) * Math.PI / 180; pts += `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)} `; }
            return wrapWithRotation(<polygon points={pts.trim()} />);
          }
          case 'pentagon': {
            const cx = offsetX + w / 2; const cy = offsetY + h / 2;
            const r = Math.min(w, h) / 2;
            let pts = '';
            for (let i = 0; i < 5; i++) { const a = (i * 72 - 90) * Math.PI / 180; pts += `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)} `; }
            return wrapWithRotation(<polygon points={pts.trim()} />);
          }
          case 'octagon': {
            const cx = offsetX + w / 2; const cy = offsetY + h / 2;
            const r = Math.min(w, h) / 2;
            let pts = '';
            for (let i = 0; i < 8; i++) { const a = (i * 45 - 90 + 22.5) * Math.PI / 180; pts += `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)} `; }
            return wrapWithRotation(<polygon points={pts.trim()} />);
          }
          case 'star': {
            const cx = offsetX + w / 2; const cy = offsetY + h / 2;
            const outer = Math.min(w, h) / 2; const inner = outer * 0.4;
            let d = '';
            for (let i = 0; i < 10; i++) { const r = i % 2 === 0 ? outer : inner; const a = (i * 36 - 90) * Math.PI / 180; d += (i === 0 ? 'M' : 'L') + `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`; }
            return wrapWithRotation(<path d={d + 'Z'} />);
          }
          case 'heart': {
            const d = `M${offsetX + w / 2},${offsetY + h * 0.9} C${offsetX + w * 0.2},${offsetY + h * 0.7} ${offsetX + w * 0.05},${offsetY + h * 0.5} ${offsetX + w * 0.05},${offsetY + h * 0.3} C${offsetX + w * 0.05},${offsetY + h * 0.15} ${offsetX + w * 0.15},${offsetY + h * 0.05} ${offsetX + w * 0.275},${offsetY + h * 0.05} C${offsetX + w * 0.35},${offsetY + h * 0.05} ${offsetX + w * 0.42},${offsetY + h * 0.1} ${offsetX + w / 2},${offsetY + h * 0.2} C${offsetX + w * 0.58},${offsetY + h * 0.1} ${offsetX + w * 0.65},${offsetY + h * 0.05} ${offsetX + w * 0.725},${offsetY + h * 0.05} C${offsetX + w * 0.85},${offsetY + h * 0.05} ${offsetX + w * 0.95},${offsetY + h * 0.15} ${offsetX + w * 0.95},${offsetY + h * 0.3} C${offsetX + w * 0.95},${offsetY + h * 0.5} ${offsetX + w * 0.8},${offsetY + h * 0.7} ${offsetX + w / 2},${offsetY + h * 0.9} Z`;
            return wrapWithRotation(<path d={d} />);
          }
          case 'arrow': {
            const pts = `${offsetX},${offsetY + h * 0.3} ${offsetX + w * 0.7},${offsetY + h * 0.3} ${offsetX + w * 0.7},${offsetY} ${offsetX + w},${offsetY + h / 2} ${offsetX + w * 0.7},${offsetY + h} ${offsetX + w * 0.7},${offsetY + h * 0.7} ${offsetX},${offsetY + h * 0.7}`;
            return wrapWithRotation(<polygon points={pts} />);
          }
          case 'cross': {
            const cw = w * 0.35; const ch = h * 0.35;
            const pts = `${offsetX + w / 2 - cw / 2},${offsetY} ${offsetX + w / 2 + cw / 2},${offsetY} ${offsetX + w / 2 + cw / 2},${offsetY + h / 2 - ch / 2} ${offsetX + w},${offsetY + h / 2 - ch / 2} ${offsetX + w},${offsetY + h / 2 + ch / 2} ${offsetX + w / 2 + cw / 2},${offsetY + h / 2 + ch / 2} ${offsetX + w / 2 + cw / 2},${offsetY + h} ${offsetX + w / 2 - cw / 2},${offsetY + h} ${offsetX + w / 2 - cw / 2},${offsetY + h / 2 + ch / 2} ${offsetX},${offsetY + h / 2 + ch / 2} ${offsetX},${offsetY + h / 2 - ch / 2} ${offsetX + w / 2 - cw / 2},${offsetY + h / 2 - ch / 2}`;
            return wrapWithRotation(<polygon points={pts} />);
          }
          case 'parallelogram': {
            const skewX = w * 0.25;
            const pts = `${offsetX + skewX},${offsetY} ${offsetX + w},${offsetY} ${offsetX + w - skewX},${offsetY + h} ${offsetX},${offsetY + h}`;
            return wrapWithRotation(<polygon points={pts} />);
          }
          case 'cylinder': {
            const cylRx = w / 2; const cylRy = h * 0.15;
            const d = `M ${offsetX},${offsetY + cylRy} L ${offsetX},${offsetY + h - cylRy} A ${cylRx},${cylRy} 0 0,0 ${offsetX + w},${offsetY + h - cylRy} L ${offsetX + w},${offsetY + cylRy} A ${cylRx},${cylRy} 0 0,1 ${offsetX},${offsetY + cylRy} Z`;
            return wrapWithRotation(<path d={d} />);
          }
          case 'line':
            return wrapWithRotation(<rect x={offsetX} y={offsetY} width={w} height={h} />);
          default:
            return wrapWithRotation(<rect x={offsetX} y={offsetY} width={w} height={h} />);
        }
      }

      if (maskEl.type === 'text') {
        const lines = (maskEl.text || '').split('\n');
        const fontSize = maskEl.fontSize || 16;
        const lineHeight = (maskEl.lineHeight || 1.2) * fontSize;
        const textEl = (
          <text
            x={offsetX}
            y={offsetY}
            fontFamily={maskEl.fontFamily || 'sans-serif'}
            fontSize={fontSize}
            fontWeight={maskEl.fontWeight || 'normal'}
            fontStyle={maskEl.fontStyle || 'normal'}
            letterSpacing={maskEl.letterSpacing || 0}
            dominantBaseline="text-before-edge"
          >
            {lines.map((line, i) => (
              <tspan key={i} x={offsetX} dy={i === 0 ? 0 : lineHeight}>{line || '\u00A0'}</tspan>
            ))}
          </text>
        );
        return wrapWithRotation(textEl);
      }

      if (maskEl.type === 'image') {
        // SVG <image> inside <clipPath>: the image's alpha channel forms the clip boundary
        const imgEl = (
          <image
            href={maskEl.src}
            x={offsetX}
            y={offsetY}
            width={w}
            height={h}
            preserveAspectRatio="none"
          />
        );
        return wrapWithRotation(imgEl);
      }

      if (maskEl.type === 'sticker') {
        // Emoji stickers can't be converted to paths in-browser; use bounding box rect
        return wrapWithRotation(<rect x={offsetX} y={offsetY} width={w} height={h} />);
      }

      if (maskEl.type === 'penPath' || maskEl.type === 'drawing') {
        if (!maskEl.path) return null;
        return (
          <path d={maskEl.path} transform={`translate(${offsetX} ${offsetY})`} />
        );
      }

      if (maskEl.type === 'svg' && maskEl.svgContent) {
        const pathMatch = maskEl.svgContent.match(/\bd="([^"]+)"/);
        if (pathMatch) return <path d={pathMatch[1]} transform={`translate(${offsetX} ${offsetY})`} />;
        const polyMatch = maskEl.svgContent.match(/\bpoints="([^"]+)"/);
        if (polyMatch) return <polygon points={polyMatch[1]} transform={`translate(${offsetX} ${offsetY})`} />;
      }

      // Fallback: full bounding-box rect
      return <rect x={offsetX} y={offsetY} width={w} height={h} />;
    };

    // Renders a zero-size <svg><defs><clipPath> block for a content element.
    // The returned element is injected as the first child of the content element's wrapper div.
    const renderClipPathDefs = (contentEl) => {
      if (!contentEl.clippedTo) return null;
      const maskEl = elementsRef.current.find(el => el.id === contentEl.clippedTo);
      if (!maskEl) return null;
      const clipPathId = `mask-clip-${contentEl.id}`;
      const shapeContent = getMaskShapeSVGContent(maskEl, contentEl);
      if (!shapeContent) return null;
      return (
        <svg
          key={`clipdef-${contentEl.id}`}
          width="0"
          height="0"
          style={{ position: 'absolute', overflow: 'visible', pointerEvents: 'none' }}
        >
          <defs>
            <clipPath id={clipPathId}>
              {shapeContent}
            </clipPath>
          </defs>
        </svg>
      );
    };

    // ---------------------------------------------------------------------------
    // Eraser mask helpers — used by image, shape, text, penPath, brush render cases

    const renderEraserMaskDefs = (element) => {
      const hasCommitted = element.eraserStrokes && element.eraserStrokes.length > 0;
      const isLiveTarget = eraserIsDrawing && eraserTargetRef.current?.id === element.id && eraserLivePoints.length >= 2;
      if (!hasCommitted && !isLiveTarget) return null;
      const maskId = `eraser-mask-${element.id}`;
      return (
        <svg
          width={element.width}
          height={element.height}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <defs>
            <mask id={maskId}>
              <rect x="0" y="0" width={element.width} height={element.height} fill="white" />
              {(element.eraserStrokes || []).map((stroke, idx) => (
                <path
                  key={idx}
                  d={stroke.path}
                  stroke="black"
                  strokeWidth={stroke.strokeWidth}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity={stroke.opacity ?? 1}
                  style={{
                    filter: stroke.hardness < 80
                      ? `blur(${((100 - stroke.hardness) / 100) * Math.max(stroke.strokeWidth / 4, 1)}px)`
                      : undefined
                  }}
                />
              ))}
              {isLiveTarget && (
                <path
                  d={buildSmoothPath(eraserLivePoints)}
                  stroke="black"
                  strokeWidth={eraserSettings.size}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity={eraserSettings.opacity}
                  style={{
                    filter: eraserSettings.hardness < 80
                      ? `blur(${((100 - eraserSettings.hardness) / 100) * Math.max(eraserSettings.size / 4, 1)}px)`
                      : undefined
                  }}
                />
              )}
            </mask>
          </defs>
        </svg>
      );
    };

    const getEraserMaskStyle = (element) => {
      const hasCommitted = element.eraserStrokes && element.eraserStrokes.length > 0;
      const isLiveTarget = eraserIsDrawing && eraserTargetRef.current?.id === element.id && eraserLivePoints.length >= 2;
      if (!hasCommitted && !isLiveTarget) return {};
      const maskId = `eraser-mask-${element.id}`;
      return { mask: `url(#${maskId})`, WebkitMask: `url(#${maskId})` };
    };

    // ---------------------------------------------------------------------------

    const renderChartSVG = (element) => {
      const DC = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981'];
      const getColor = (i) => (element.dataColors || [])[i] || DC[i % DC.length];
      const mainColor = element.color || DC[0];

      // Normalize data: supports legacy number[] and new {label, value}[]
      const data = (element.data || []).map((d, i) =>
        typeof d === 'number'
          ? { label: `Item ${i + 1}`, value: Number(d) }
          : { label: d.label || `Item ${i + 1}`, value: Number(d.value) || 0 }
      );
      if (data.length === 0) return null;

      const values = data.map(d => d.value);
      const maxVal = Math.max(...values, 1);
      const niceMax = Math.ceil(maxVal / 10) * 10 || 100;

      const showGrid    = element.showGridLines !== false;
      const showLabels  = element.showDataLabels !== false;
      const showLegend  = element.showLegend !== false;
      const legendPos   = element.legendPosition || 'bottom';
      const xTitle      = (element.xAxisTitle || '').trim();
      const yTitle      = (element.yAxisTitle || '').trim();
      const isPie       = element.chartType === 'pie' || element.chartType === 'donut';

      /* ---- PIE / DONUT ---- */
      if (isPie) {
        const PER_ROW = 3;
        const legRows = showLegend ? Math.ceil(data.length / PER_ROW) : 0;
        const legH    = legRows * 16 + (legRows > 0 ? 6 : 0);
        const cx = 200, cy = (300 - legH) / 2;
        const outerR = Math.min(cx, cy) - 14;
        const innerR = element.chartType === 'donut' ? outerR * 0.52 : 0;
        const total  = values.reduce((a, b) => a + b, 0) || 1;

        let angle = -Math.PI / 2;
        const slices = data.map((d, i) => {
          const ratio = d.value / total;
          const sa = angle, ea = angle + ratio * 2 * Math.PI;
          const ma = (sa + ea) / 2;
          angle = ea;
          const laf = ratio > 0.5 ? 1 : 0;
          const ox1 = cx + outerR * Math.cos(sa), oy1 = cy + outerR * Math.sin(sa);
          const ox2 = cx + outerR * Math.cos(ea), oy2 = cy + outerR * Math.sin(ea);
          let pathD;
          if (innerR > 0) {
            const ix1 = cx + innerR * Math.cos(sa), iy1 = cy + innerR * Math.sin(sa);
            const ix2 = cx + innerR * Math.cos(ea), iy2 = cy + innerR * Math.sin(ea);
            pathD = `M ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${laf} 1 ${ox2} ${oy2} L ${ix2} ${iy2} A ${innerR} ${innerR} 0 ${laf} 0 ${ix1} ${iy1} Z`;
          } else {
            pathD = `M ${cx} ${cy} L ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${laf} 1 ${ox2} ${oy2} Z`;
          }
          const lr = innerR > 0 ? (outerR + innerR) / 2 : outerR * 0.62;
          return {
            pathD,
            color: getColor(i),
            label: d.label,
            lx: cx + lr * Math.cos(ma),
            ly: cy + lr * Math.sin(ma),
            pct: Math.round(ratio * 100),
          };
        });

        const legStartY = 300 - legH + 4;
        const legItemW  = 400 / PER_ROW;
        return (
          <>
            {slices.map((s, i) => (
              <g key={i}>
                <path d={s.pathD} fill={s.color} />
                {showLabels && s.pct >= 5 && (
                  <text x={s.lx} y={s.ly} fontSize="10" fill="#fff"
                    textAnchor="middle" dominantBaseline="middle" fontWeight="600">
                    {s.pct}%
                  </text>
                )}
              </g>
            ))}
            {showLegend && slices.map((s, i) => {
              const col = Math.floor(i / PER_ROW);
              const row = i % PER_ROW;
              const lx = col * legItemW + 8;
              const ly = legStartY + row * 16;
              const lbl = s.label.length > 11 ? s.label.slice(0, 10) + '…' : s.label;
              return (
                <g key={`leg-${i}`}>
                  <rect x={lx} y={ly + 1} width="9" height="9" fill={s.color} rx="1.5" />
                  <text x={lx + 13} y={ly + 8} fontSize="9" fill="#555">{lbl}</text>
                </g>
              );
            })}
          </>
        );
      }

      /* ---- AXIS CHARTS (bar / column / line / area) ---- */
      const lM = yTitle ? 58 : 48;
      const rM = (showLegend && legendPos === 'top-right') ? 72 : 10;
      const tM = showLabels ? 26 : 16;
      const bM = 14 + (xTitle ? 16 : 0) + (showLegend && legendPos === 'bottom' ? 20 : 0) + 6;

      const cX = lM, cY = tM;
      const cW = 400 - lM - rM;
      const cH = 300 - tM - bM;

      // Y-axis scale ticks (6 levels: 0 → niceMax)
      const TICKS = 5;
      const yTicks = Array.from({ length: TICKS + 1 }, (_, i) => {
        const v = Math.round((niceMax / TICKS) * i);
        return { v, y: cY + cH - (v / niceMax) * cH };
      });

      const barGW  = cW / data.length;
      const barW   = barGW * 0.68;
      const barPad = (barGW - barW) / 2;
      const xC     = (i) => cX + i * barGW + barGW / 2;
      const yV     = (v) => cY + cH - (v / niceMax) * cH;

      const linePts  = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xC(i)} ${yV(d.value)}`).join(' ');
      const areaPath = `${linePts} L ${xC(data.length - 1)} ${cY + cH} L ${xC(0)} ${cY + cH} Z`;
      const gradId   = `ag-${element.id}`;

      const xLabelY = cY + cH + 13;
      const xTitleY = 300 - (showLegend && legendPos === 'bottom' ? 20 : 4);
      const legY    = 300 - 14;

      return (
        <>
          {/* Gradient for area chart */}
          {element.chartType === 'area' && (
            <defs>
              <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={mainColor} stopOpacity="0.4" />
                <stop offset="100%" stopColor={mainColor} stopOpacity="0.04" />
              </linearGradient>
            </defs>
          )}

          {/* Grid lines */}
          {showGrid && yTicks.map(({ v, y }) => (
            <line key={`g-${v}`} x1={cX} y1={y} x2={cX + cW} y2={y}
              stroke="#e5e7eb" strokeWidth="1" />
          ))}

          {/* Y-axis tick labels */}
          {yTicks.map(({ v, y }) => (
            <text key={`yt-${v}`} x={cX - 5} y={y} fontSize="9" fill="#9ca3af"
              textAnchor="end" dominantBaseline="middle">{v}</text>
          ))}

          {/* Y-axis title */}
          {yTitle && (
            <text x={11} y={cY + cH / 2} fontSize="10" fill="#6b7280"
              textAnchor="middle" fontWeight="500"
              transform={`rotate(-90, 11, ${cY + cH / 2})`}>
              {yTitle.length > 18 ? yTitle.slice(0, 17) + '…' : yTitle}
            </text>
          )}

          {/* Bar / Column */}
          {(element.chartType === 'bar' || element.chartType === 'column') && data.map((d, i) => {
            const bx = cX + i * barGW + barPad;
            const bh = Math.max((d.value / niceMax) * cH, 2);
            const by = cY + cH - bh;
            return (
              <g key={i}>
                <rect x={bx} y={by} width={barW} height={bh} fill={getColor(i)} rx="3" />
                {showLabels && (
                  <text x={bx + barW / 2} y={by - 4} fontSize="9" fill="#374151"
                    textAnchor="middle">{d.value}</text>
                )}
              </g>
            );
          })}

          {/* Area */}
          {element.chartType === 'area' && (
            <>
              <path d={areaPath} fill={`url(#${gradId})`} />
              <path d={linePts} fill="none" stroke={mainColor} strokeWidth="2.5" strokeLinejoin="round" />
              {data.map((d, i) => (
                <g key={i}>
                  <circle cx={xC(i)} cy={yV(d.value)} r="4" fill={mainColor} />
                  {showLabels && (
                    <text x={xC(i)} y={yV(d.value) - 8} fontSize="9" fill="#374151" textAnchor="middle">
                      {d.value}
                    </text>
                  )}
                </g>
              ))}
            </>
          )}

          {/* Line */}
          {element.chartType === 'line' && (
            <>
              <path d={linePts} fill="none" stroke={mainColor} strokeWidth="2.5" strokeLinejoin="round" />
              {data.map((d, i) => (
                <g key={i}>
                  <circle cx={xC(i)} cy={yV(d.value)} r="4.5" fill={getColor(i)} stroke="#fff" strokeWidth="1.5" />
                  {showLabels && (
                    <text x={xC(i)} y={yV(d.value) - 9} fontSize="9" fill="#374151" textAnchor="middle">
                      {d.value}
                    </text>
                  )}
                </g>
              ))}
            </>
          )}

          {/* Axes */}
          <line x1={cX} y1={cY} x2={cX} y2={cY + cH} stroke="#d1d5db" strokeWidth="1.5" />
          <line x1={cX} y1={cY + cH} x2={cX + cW} y2={cY + cH} stroke="#d1d5db" strokeWidth="1.5" />

          {/* X-axis category labels */}
          {data.map((d, i) => {
            const lbl = d.label.length > 8 ? d.label.slice(0, 7) + '…' : d.label;
            return (
              <text key={`xl-${i}`} x={xC(i)} y={xLabelY} fontSize="9" fill="#9ca3af" textAnchor="middle">
                {lbl}
              </text>
            );
          })}

          {/* X-axis title */}
          {xTitle && (
            <text x={cX + cW / 2} y={xTitleY} fontSize="10" fill="#6b7280"
              textAnchor="middle" fontWeight="500">
              {xTitle.length > 26 ? xTitle.slice(0, 25) + '…' : xTitle}
            </text>
          )}

          {/* Legend — bottom (per data item) */}
          {showLegend && legendPos === 'bottom' && (() => {
            const visible = data.slice(0, 5);
            const itemW = 60;
            const totalW = visible.length * itemW;
            const startX = cX + cW / 2 - totalW / 2;
            return visible.map((d, i) => {
              const lx = startX + i * itemW;
              const lbl = d.label.length > 5 ? d.label.slice(0, 4) + '…' : d.label;
              return (
                <g key={`leg-${i}`}>
                  <rect x={lx} y={legY} width="9" height="9" fill={getColor(i)} rx="1.5" />
                  <text x={lx + 12} y={legY + 7} fontSize="9" fill="#555">{lbl}</text>
                </g>
              );
            });
          })()}

          {/* Legend — top-right (per data item) */}
          {showLegend && legendPos === 'top-right' && data.map((d, i) => {
            const ly = cY + i * 16;
            if (ly + 10 > cY + cH) return null;
            const lx = 400 - rM + 4;
            const lbl = d.label.length > 7 ? d.label.slice(0, 6) + '…' : d.label;
            return (
              <g key={`leg-${i}`}>
                <rect x={lx} y={ly} width="8" height="8" fill={getColor(i)} rx="1" />
                <text x={lx + 11} y={ly + 7} fontSize="8.5" fill="#555">{lbl}</text>
              </g>
            );
          })}
        </>
      );
    };

    // ---------------------------------------------------------------------------

    // ── Image drag-and-drop replacement (frames, images, clipping masks) ──
    // A drag carries either a ready-to-use local src (x-editor-image-src) or a
    // Media-panel asset URL that must be uploaded first (x-editor-media-src).
    const dragHasImagePayload = (e) =>
      e.dataTransfer.types.includes('application/x-editor-image-src')
      || e.dataTransfer.types.includes('application/x-editor-media-src');

    const handleImageDragOver = (e, element) => {
      if (!dragHasImagePayload(e)) return;
      // Only highlight when this element can actually receive the image.
      if (!resolveImageReplaceTarget(element, elementsRef.current)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      if (dropTargetId !== element.id) setDropTargetId(element.id);
    };

    const handleImageDragLeave = (element) => {
      if (dropTargetId === element.id) setDropTargetId(null);
    };

    const handleImageDrop = (e, element) => {
      const localSrc = e.dataTransfer.getData('application/x-editor-image-src');
      const mediaSrc = e.dataTransfer.getData('application/x-editor-media-src');
      setDropTargetId(null);
      if (!localSrc && !mediaSrc) return;
      const target = resolveImageReplaceTarget(element, elementsRef.current);
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      onInteractionStart?.(); // single undo snapshot for the whole replace

      const apply = (src) => onUpdateElement(target.targetId, buildReplacePatch(target.kind, src));

      if (localSrc) {
        apply(localSrc);
      } else if (mediaSrc && onUploadImageFromUrl) {
        // Media-panel asset: upload/import first, then assign in place. No new
        // canvas element is created. Failure leaves the original untouched.
        Promise.resolve(onUploadImageFromUrl(mediaSrc))
          .then((storedUrl) => { if (storedUrl) apply(storedUrl); })
          .catch(() => {});
      }
    };

    // Crop-mode pan: drags reposition the photo inside the fixed frame.
    const handleCropPanStart = (e, element) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startImg = { ...(element.image || {}) };
      const onMove = (me) => {
        const el = elementsRef.current.find((x) => x.id === element.id);
        if (!el || !el.image) return;
        const dx = (me.clientX - startX) / zoom;
        const dy = (me.clientY - startY) / zoom;
        const { offsetX, offsetY } = clampCropOffsets(
          el, el.image.naturalWidth, el.image.naturalHeight,
          (startImg.offsetX || 0) + dx, (startImg.offsetY || 0) + dy
        );
        onUpdateElement(element.id, { image: { ...el.image, offsetX, offsetY } });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    // Plain-image crop pan: same as frames but mutates cropOffsetX/Y.
    const handleImageCropPanStart = (e, element) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startOffX = element.cropOffsetX || 0;
      const startOffY = element.cropOffsetY || 0;
      const onMove = (me) => {
        const el = elementsRef.current.find((x) => x.id === element.id);
        if (!el) return;
        const dx = (me.clientX - startX) / zoom;
        const dy = (me.clientY - startY) / zoom;
        const pseudo = {
          width: el.width, height: el.height, fit: el.fit || 'cover',
          image: { offsetX: 0, offsetY: 0, scale: el.cropScale || 1 },
        };
        const { offsetX, offsetY } = clampCropOffsets(
          pseudo, el.naturalWidth, el.naturalHeight, startOffX + dx, startOffY + dy
        );
        onUpdateElement(element.id, { cropOffsetX: offsetX, cropOffsetY: offsetY });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    // ── Inner-image transform (Canva-style edit-inside-frame/mask) ──────────
    // Unified read/write of the inner image's scale/rotation/offset across the
    // two storage shapes: frames keep them on `image.*`; clipped/plain images
    // keep them on `crop*`. All writes go through onUpdateElement during a crop
    // session that already snapshotted history once → single undo entry.
    const getInnerTransform = (el, kind) => kind === 'frame'
      ? { scale: el.image?.scale ?? 1, rotation: el.image?.rotation ?? 0, offsetX: el.image?.offsetX ?? 0, offsetY: el.image?.offsetY ?? 0 }
      : { scale: el.cropScale ?? 1, rotation: el.cropRotation ?? 0, offsetX: el.cropOffsetX ?? 0, offsetY: el.cropOffsetY ?? 0 };

    const setInnerTransform = (el, kind, patch) => {
      if (kind === 'frame') {
        onUpdateElement(el.id, { image: { ...el.image, ...patch } });
        return;
      }
      const map = {};
      if ('scale' in patch) map.cropScale = patch.scale;
      if ('rotation' in patch) map.cropRotation = patch.rotation;
      if ('offsetX' in patch) map.cropOffsetX = patch.offsetX;
      if ('offsetY' in patch) map.cropOffsetY = patch.offsetY;
      onUpdateElement(el.id, map);
    };

    // Build the pseudo-frame clampCropOffsets expects for a given element/kind.
    const innerClampFrame = (el, kind, scale) => kind === 'frame'
      ? { ...el, image: { ...el.image, scale } }
      : { width: el.width, height: el.height, fit: el.fit || 'cover', image: { offsetX: 0, offsetY: 0, scale } };

    const innerNatural = (el, kind) => kind === 'frame'
      ? [el.image?.naturalWidth, el.image?.naturalHeight]
      : [el.naturalWidth, el.naturalHeight];

    // Corner-drag scale (uniform, center-anchored). Delta projected onto the
    // corner's outward diagonal drives the scale; offsets re-clamp so a cover
    // image never exposes the frame.
    const handleInnerImageScaleStart = (e, element, corner, kind, layout) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const start = getInnerTransform(elementsRef.current.find((x) => x.id === element.id) || element, kind);
      const refSize = Math.max(layout.width, layout.height) || 1;
      const dir = { nw: [-1, -1], ne: [1, -1], se: [1, 1], sw: [-1, 1] }[corner] || [1, 1];
      const onMove = (me) => {
        const el = elementsRef.current.find((x) => x.id === element.id);
        if (!el) return;
        const dx = (me.clientX - startX) / zoom;
        const dy = (me.clientY - startY) / zoom;
        const proj = dx * dir[0] + dy * dir[1];
        let s = start.scale * (1 + proj / refSize);
        s = Math.max(minCropScale(element.fit), Math.min(5, s));
        const [nw, nh] = innerNatural(el, kind);
        const cur = getInnerTransform(el, kind);
        const { offsetX, offsetY } = clampCropOffsets(innerClampFrame(el, kind, s), nw, nh, cur.offsetX, cur.offsetY);
        setInnerTransform(el, kind, { scale: s, offsetX, offsetY });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    // Rotate the image inside its fixed frame via the rotate handle. Angle is
    // measured from the image center (computed from the wrapper's screen rect).
    const handleInnerImageRotateStart = (e, element, kind, layout) => {
      e.stopPropagation();
      e.preventDefault();
      const wrapper = e.currentTarget.closest(`[data-frame-wrapper="${element.id}"]`);
      const rect = wrapper ? wrapper.getBoundingClientRect() : null;
      const start = getInnerTransform(elementsRef.current.find((x) => x.id === element.id) || element, kind);
      const cxScreen = rect ? rect.left + (layout.left + layout.width / 2) * zoom : e.clientX;
      const cyScreen = rect ? rect.top + (layout.top + layout.height / 2) * zoom : e.clientY;
      const startAngle = Math.atan2(e.clientY - cyScreen, e.clientX - cxScreen) * 180 / Math.PI;
      const onMove = (me) => {
        const el = elementsRef.current.find((x) => x.id === element.id);
        if (!el) return;
        const ang = Math.atan2(me.clientY - cyScreen, me.clientX - cxScreen) * 180 / Math.PI;
        let r = start.rotation + (ang - startAngle);
        r = ((r % 360) + 360) % 360;
        setInnerTransform(el, kind, { rotation: r });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    // Visible transform box: rotated to match the image, 4 corner scale handles
    // + a rotate handle above. Rendered inside the (clipping) wrapper so it
    // tracks the frame's own rotation.
    // Crop-box resize: drag a side/corner handle to crop that side. The element
    // box (= crop window) resizes while the inner offset compensates so the
    // image stays visually fixed (Canva-style, non-destructive).
    const handleCropBoxResizeStart = (e, element, handle, kind) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const el0 = elementsRef.current.find((x) => x.id === element.id) || element;
      const inner0 = getInnerTransform(el0, kind);
      const box0 = { x: el0.x, y: el0.y, width: el0.width, height: el0.height, offsetX: inner0.offsetX, offsetY: inner0.offsetY };
      const onMove = (me) => {
        const dx = (me.clientX - startX) / zoom;
        const dy = (me.clientY - startY) / zoom;
        const r = cropResize(box0, handle, dx, dy);
        if (kind === 'frame') {
          onUpdateElement(element.id, { x: r.x, y: r.y, width: r.width, height: r.height, image: { ...el0.image, offsetX: r.offsetX, offsetY: r.offsetY } });
        } else {
          onUpdateElement(element.id, { x: r.x, y: r.y, width: r.width, height: r.height, cropOffsetX: r.offsetX, cropOffsetY: r.offsetY });
        }
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    const CROP_HANDLE_CURSOR = {
      n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
      ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize',
    };

    // Crop overlay: 8 boundary handles (sides + corners) + a rotate handle.
    // Handles crop the box; dragging the image body pans it; the slider zooms.
    const renderInnerImageHandles = (element, layout, kind) => {
      const handles = [
        ['nw', 0, 0], ['n', 0.5, 0], ['ne', 1, 0],
        ['w', 0, 0.5], ['e', 1, 0.5],
        ['sw', 0, 1], ['s', 0.5, 1], ['se', 1, 1],
      ];
      return (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 65 }}>
          {/* Visible crop boundary */}
          <div style={{ position: 'absolute', inset: 0, outline: '1.5px solid #7c3aed' }} />
          {handles.map(([name, fx, fy]) => {
            const corner = name.length === 2;
            return (
              <div
                key={name}
                data-testid={`crop-handle-${name}`}
                onMouseDown={(e) => handleCropBoxResizeStart(e, element, name, kind)}
                style={{
                  position: 'absolute', left: `${fx * 100}%`, top: `${fy * 100}%`,
                  width: corner ? 14 : 12, height: corner ? 14 : 12,
                  marginLeft: corner ? -7 : -6, marginTop: corner ? -7 : -6,
                  background: '#fff', border: '2px solid #7c3aed', borderRadius: corner ? 2 : 6,
                  cursor: CROP_HANDLE_CURSOR[name], pointerEvents: 'auto', zIndex: 66,
                }}
              />
            );
          })}
          {/* Rotate the image inside the crop window */}
          <div
            data-testid="inner-handle-rotate"
            onMouseDown={(e) => handleInnerImageRotateStart(e, element, kind, layout || { left: 0, top: 0, width: element.width, height: element.height })}
            style={{
              position: 'absolute', left: '50%', top: -28,
              width: 14, height: 14, marginLeft: -7,
              background: '#fff', border: '2px solid #7c3aed', borderRadius: '50%',
              cursor: 'grab', pointerEvents: 'auto', zIndex: 66,
            }}
          />
        </div>
      );
    };

    // Shared crop-mode chrome: Done/Cancel buttons + zoom slider, rendered
    // inside the element wrapper (frame or plain image) while cropping.
    const renderCropControls = (element, scaleValue, onScaleChange) => (
      <>
        <div
          style={{
            position: 'absolute', top: -44, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 6, zIndex: 70,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            data-testid="crop-done"
            onClick={() => onCommitCrop?.()}
            style={{ background: '#7c3aed', color: '#fff', borderRadius: 8, padding: '4px 14px', fontSize: 12, fontWeight: 600 }}
          >
            Done
          </button>
          <button
            type="button"
            data-testid="crop-cancel"
            onClick={() => onCancelCrop?.()}
            style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '4px 14px', fontSize: 12 }}
          >
            Cancel
          </button>
        </div>
        <input
          type="range"
          data-testid="crop-zoom"
          min={minCropScale(element.fit)}
          max={3}
          step={0.01}
          value={scaleValue}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={onScaleChange}
          style={{ position: 'absolute', bottom: -34, left: '10%', width: '80%', zIndex: 70 }}
        />
      </>
    );

    const renderElement = (element) => {
      const isSelected = selectedElement?.id === element.id;
      const isMultiSelected = selectedElements?.some(el => el.id === element.id);

      // Determine border style based on selection state
      let borderStyle = 'none';
      if (isSelected) {
        borderStyle = '2px solid #6366f1'; // Primary selection - purple
      } else if (isMultiSelected) {
        borderStyle = '2px solid #a855f7'; // Multi-selection - lighter purple
      }

      const hasAnimation = isAnimationPlaying && element.animationType && element.animationType !== 'none';
      const animationStyle = hasAnimation
        ? {
          animation: `design-${element.animationType} ${element.animationDuration ?? 500}ms ${element.animationEasing ?? 'ease'} ${element.animationDelay ?? 0}ms forwards`,
          animationFillMode: 'forwards',
        }
        : {};
      const isMaskEl = element.isMaskShape || element.isClipBase;
      // Live filter preview: when the user hovers a filter card in the right
      // sidebar, previewFilter = { id, filter } temporarily overrides the
      // element.filter so the canvas reflects the hovered effect immediately.
      const effectiveFilter = (previewFilter && previewFilter.id === element.id)
        ? (previewFilter.filter || 'none')
        : (element.filter || 'none');
      // Compose base filter + non-destructive effect/shadow presets. Blend mode
      // maps to CSS mix-blend-mode (honored on export + thumbnails).
      const composedFilter = composeFilter({ ...element, filter: effectiveFilter });
      const commonStyle = {
        position: 'absolute',
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
        transform: buildElementTransform(element),
        cursor: element.locked ? 'not-allowed' : 'move',
        border: borderStyle,
        boxSizing: 'border-box',
        filter: composedFilter,
        mixBlendMode: blendModeStyle(element),
        boxShadow: shadowBoxCss(element),
        opacity: element.opacity || 1,
        pointerEvents: element.locked ? 'none' : 'auto',
        // Mask shape indicator: dashed purple outline ONLY when selected, so it's invisible in the design
        ...(isMaskEl && (isSelected || isMultiSelected) && element.type !== 'shape' && element.type !== 'penPath' && element.type !== 'drawing' ? {
          outline: '1.5px dashed rgba(99,102,241,0.8)',
          outlineOffset: '1px',
        } : {}),
        ...animationStyle,
      };
      const elementKey = isAnimationPlaying ? `${element.id}-play-${animationPlayKey}` : element.id;

      switch (element.type) {
        case 'text': {
          const isEditingThis = editingTextId === element.id;
          // Dev-mode one-shot dump: logs each text element's raw `text` value
          // ONCE per element-id per session. Captures whitespace, newlines,
          // and any invisible characters via JSON.stringify so we can verify
          // whether stored data already lacks expected spaces (i.e. the
          // corruption was at write time, not render time). Gated by a
          // window flag so production builds stay silent.
          if (typeof window !== 'undefined' && window.__DEBUG_TEXT_EDIT__) {
            window.__textElDumpSeen__ = window.__textElDumpSeen__ || new Set();
            if (!window.__textElDumpSeen__.has(element.id)) {
              window.__textElDumpSeen__.add(element.id);
              // eslint-disable-next-line no-console
              console.log(`[text-data] ${element.id}`,
                JSON.stringify(element.text ?? element.content ?? ''),
                { fontSize: element.fontSize, letterSpacing: element.letterSpacing, width: element.width });
            }
          }
          // Blend shadowOpacity (0–1) into shadowColor hex → rgba
          const _shadowOpacity = element.shadowOpacity ?? 1;
          const _shadowColorRaw = element.shadowColor ?? '#000000';
          const _resolvedShadowColor = (() => {
            if (_shadowOpacity >= 1) return _shadowColorRaw;
            const m = _shadowColorRaw.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
            return m
              ? `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${_shadowOpacity})`
              : _shadowColorRaw;
          })();
          const hasShadow = (element.shadowBlur ?? 0) > 0 || (element.shadowOffsetX ?? 0) !== 0 || (element.shadowOffsetY ?? 0) !== 0 || element.shadowColor;
          const textShadowStyle = hasShadow
            ? {
              textShadow: `${element.shadowOffsetX ?? 0}px ${element.shadowOffsetY ?? 0}px ${element.shadowBlur ?? 4}px ${_resolvedShadowColor}`,
            }
            : {};
          const textCurve = element.textCurve != null ? Number(element.textCurve) : 0;
          const showCurved = !isEditingThis && textCurve !== 0;

          // Fill enabled toggle
          const hasFillEnabled = element.fillEnabled !== false;

          // Gradient fill
          const fillGrad = element.fillGradient;
          const hasGradientFill = hasFillEnabled && fillGrad && fillGrad.colors && fillGrad.colors.length >= 2;

          // Stroke
          const strokeWidth = Number(element.strokeWidth) || 0;
          const strokeColor = element.stroke || '#000000';
          const strokeGrad = element.strokeGradient;
          const hasGradientStroke = strokeGrad && strokeGrad.colors && strokeGrad.colors.length >= 2;

          // Whether to render via SVG (needed for gradient fill/stroke applied to glyph shapes)
          const useGradientSVG = !isEditingThis && !showCurved && (hasGradientFill || hasGradientStroke);

          // Straight text: HTML fill color (no gradient)
          const textStrokeStyle = !useGradientSVG && strokeWidth > 0
            ? { WebkitTextStroke: `${strokeWidth}px ${strokeColor}` }
            : {};
          const htmlFillColor = isMaskEl ? 'transparent'
            : !hasFillEnabled ? 'transparent'
            : element.color;

          // SVG text common helpers
          const svgFontSize = Number(String(element.fontSize || '16').replace('px', '')) || 16;
          const svgShadowFilter = hasShadow ? (
            <filter id={`text-shadow-${element.id}`} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow
                dx={element.shadowOffsetX ?? 0}
                dy={element.shadowOffsetY ?? 0}
                stdDeviation={(element.shadowBlur ?? 4) / 2}
                floodColor={_resolvedShadowColor}
                floodOpacity={_shadowOpacity}
              />
            </filter>
          ) : null;

          const textClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          return (
            <div
              key={elementKey}
              data-export-type="text"
              data-export-id={element.id}
              style={{
                ...commonStyle,
                fontSize: element.fontSize,
                fontFamily: element.fontFamily,
                color: useGradientSVG ? undefined : htmlFillColor,
                fontWeight: element.fontWeight,
                fontStyle: element.fontStyle,
                textAlign: element.textAlign,
                textDecoration: element.textDecoration,
                letterSpacing: element.letterSpacing || 'normal',
                lineHeight: element.lineHeight || 'normal',
                padding: 0,
                overflow: isEditingThis ? 'visible' : undefined,
                userSelect: isEditingThis ? 'text' : 'none',
                WebkitUserSelect: isEditingThis ? 'text' : 'none',
                cursor: isEditingThis ? 'text' : 'move',
                ...textShadowStyle,
                ...textStrokeStyle,
                clipPath: textClipId ? `url(#${textClipId})` : undefined,
                ...getEraserMaskStyle(element),
                // Unified text effect (outline/hollow/neon/lift/shadow) — applied
                // last so it wins; only for the non-edit static render.
                ...(!isEditingThis ? effectTextStyle(element) : {}),
              }}
              onMouseDown={(e) => {
                if (!isEditingThis) {
                  handleElementMouseDown(e, element);
                }
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Capture the click point in viewport coords so the contentEditable
                // ref can place the caret where the user double-clicked (Figma-style)
                // instead of always at end-of-text.
                pendingTextEditCaretRef.current = {
                  id: element.id,
                  clientX: e.clientX,
                  clientY: e.clientY,
                };
                // Reset the live-text buffer SYNCHRONOUSLY before triggering
                // the re-render. EditableTextNode's mount useLayoutEffect
                // reads this ref to decide whether to hydrate from prop or
                // recover live typing; if a previous edit session left a
                // value here, the new session would see it instead of the
                // current element.text. Clearing here guarantees a fresh
                // buffer for every edit session start.
                liveEditTextRef.current = null;
                setEditingTextId(element.id);
                onSelectElement(element);
              }}
            >
              {textClipId && renderClipPathDefs(element)}
              {renderEraserMaskDefs(element)}
              {/* Single text-render guarantee: the inner content sits inside
                  a keyed wrapper whose key flips on every isEditingThis
                  transition. That forces React to fully unmount the previous
                  subtree (contentEditable or static block) before mounting
                  the next, eliminating any reconciliation window in which
                  both could co-exist in the DOM. The render below is also a
                  strict ternary chain — exactly one branch ever evaluates. */}
              <div key={`text-render-${element.id}-${isEditingThis ? 'edit' : 'view'}`} style={{ width: '100%', height: '100%' }}>
              {isEditingThis ? (
                // Wrap the contentEditable in the SAME flex container the
                // static render uses (display:flex / alignItems:flex-start /
                // padding:'0 8px') so typed text sits exactly where the
                // static text was. Identical layout = no visual jump on
                // enter/exit edit. Top-anchor (alignItems:flex-start) makes
                // multiline growth extend downward without Y-drift (Figma).
                <div style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '0 8px',
                  boxSizing: 'border-box',
                }}>
                <EditableTextNode
                  // key forces a fresh mount whenever the user starts editing
                  // a different element. Within a single edit session for the
                  // same element this is stable, so the inner useLayoutEffect
                  // runs ONCE and React cannot remount mid-edit.
                  key={`edit-${element.id}`}
                  // Imperative handle so non-blur exit paths can call
                  // .blur() on the live contentEditable (see exitTextEdit).
                  ref={editableNodeRef}
                  debug={typeof window !== 'undefined' && window.__DEBUG_TEXT_EDIT__}
                  debugId={element.id}
                  initialText={element.text ?? element.content ?? ''}
                  // Parent-owned live text buffer — on any remount, the new
                  // instance picks up the user's latest typing from this ref
                  // instead of being clobbered by stale element.text.
                  liveTextRef={liveEditTextRef}
                  initialCaret={
                    pendingTextEditCaretRef.current?.id === element.id
                      ? { clientX: pendingTextEditCaretRef.current.clientX, clientY: pendingTextEditCaretRef.current.clientY }
                      : null
                  }
                  style={{
                    outline: 'none',
                    minWidth: '20px',
                    minHeight: '1em',
                    width: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'normal',
                    overflowWrap: 'normal',
                    boxSizing: 'border-box',
                  }}
                  onInput={(e) => {
                    const sh = e.target.scrollHeight;
                    if (sh <= 0) return;
                    const elId = element.id;
                    const prevH = element.height;
                    clearTimeout(textHeightDebounceRef.current);
                    textHeightDebounceRef.current = setTimeout(() => {
                      if (sh !== prevH) onUpdateElement(elId, { height: sh });
                    }, 150);
                  }}
                  onBlur={(e, serialized) => {
                    clearTimeout(textHeightDebounceRef.current);
                    pendingTextEditCaretRef.current = null;
                    // Prefer the parent-owned live buffer over the
                    // serialized DOM read — if anything raced the blur
                    // (e.g. browser cleared the DOM before the event
                    // handler ran), liveEditTextRef still has the last
                    // keystroke. Falls back to serialized for the no-typing
                    // case where liveTextRef is null.
                    const newText = liveEditTextRef.current ?? serialized;
                    // Clear AFTER reading. Next edit session starts fresh.
                    liveEditTextRef.current = null;
                    const sh = e.target.scrollHeight;
                    const prevText = element.text ?? element.content ?? '';
                    try {
                      const finalHeight = sh > 0 ? sh : element.height;
                      // Order matters: commit text FIRST so pages contains
                      // the new value BEFORE editingTextId clears and the
                      // static-render branch mounts. React 18 batches both
                      // setStates, so the static render sees the new
                      // element.text in the same paint — no stale-flash on
                      // outside-click.
                      onUpdateElement(element.id, {
                        width: element.width,
                        height: finalHeight,
                        text: newText,
                      });
                      setEditingTextId(null);
                    } catch (err) {
                      console.error('[text-edit] blur commit failed:', err);
                      setEditingTextId(null);
                    }
                    if (typeof window !== 'undefined' && window.__DEBUG_TEXT_EDIT__) {
                      // eslint-disable-next-line no-console
                      console.groupCollapsed(`[text-edit:commit] ${element.id}`);
                      // eslint-disable-next-line no-console
                      console.log('prev element.text :', JSON.stringify(prevText));
                      // eslint-disable-next-line no-console
                      console.log('serialized DOM    :', JSON.stringify(serialized));
                      // eslint-disable-next-line no-console
                      console.log('liveEditTextRef   :', JSON.stringify(newText));
                      // eslint-disable-next-line no-console
                      console.log('committed to store:', JSON.stringify(newText));
                      // eslint-disable-next-line no-console
                      console.groupEnd();
                    }
                  }}
                />
                </div>
              ) : showCurved ? (
                // ── Curved text via SVG textPath ──────────────────────────────
                <svg
                  width={element.width}
                  height={element.height}
                  style={{ overflow: 'visible', pointerEvents: 'none' }}
                >
                  <defs>
                    {svgShadowFilter}
                    {hasGradientFill && buildSVGGradient(`text-fill-grad-${element.id}`, fillGrad)}
                    {hasGradientStroke && buildSVGGradient(`text-stroke-grad-${element.id}`, strokeGrad)}
                    <path
                      id={`text-path-${element.id}`}
                      d={(() => {
                        const w = element.width;
                        const halfChord = w / 2;
                        const effectiveAngle = Math.max(1, Math.min(179, (Math.abs(textCurve) / 360) * 179));
                        const angleRad = (effectiveAngle * Math.PI) / 180;
                        const r = halfChord / Math.sin(angleRad / 2);
                        const distToCenter = Math.sqrt(Math.max(0, r * r - halfChord * halfChord));
                        const sagitta = r - distToCenter;
                        const isUp = textCurve > 0;
                        // Top-anchored: anchor the curve to the TOP of the box (offset
                        // by one font-size so glyphs sit inside the box). Previously
                        // midY = element.height / 2 vertically centred the curve, so
                        // growing the box's height would re-centre and the text would
                        // "snap" away from where the user typed it on blur.
                        const midY = svgFontSize + (isUp ? 0 : sagitta);
                        const startY = isUp ? midY + sagitta : midY - sagitta;
                        const sweep = isUp ? 0 : 1;
                        return `M 0,${startY} A ${r},${r} 0 0,${sweep} ${w},${startY}`;
                      })()}
                    />
                  </defs>
                  <text
                    fill={!hasFillEnabled ? 'none' : hasGradientFill ? `url(#text-fill-grad-${element.id})` : (element.color ?? '#000000')}
                    fontFamily={element.fontFamily ?? 'Poppins'}
                    fontWeight={element.fontWeight ?? 'normal'}
                    fontStyle={element.fontStyle ?? 'normal'}
                    fontSize={svgFontSize}
                    textDecoration={element.textDecoration ?? 'none'}
                    letterSpacing={element.letterSpacing && element.letterSpacing !== 'normal' ? element.letterSpacing : undefined}
                    stroke={hasGradientStroke ? `url(#text-stroke-grad-${element.id})` : (strokeWidth > 0 ? strokeColor : 'none')}
                    strokeWidth={strokeWidth > 0 || hasGradientStroke ? strokeWidth || 1 : undefined}
                    paintOrder="stroke fill"
                    filter={hasShadow ? `url(#text-shadow-${element.id})` : undefined}
                  >
                    <textPath href={`#text-path-${element.id}`} startOffset="50%" textAnchor="middle">
                      {element.text || ''}
                    </textPath>
                  </text>
                </svg>
              ) : (
                // ── Static text — shared render tree with PageThumbnailPreview ──
                // The thumbnail renders this SAME component (staticTextRender),
                // so editor and thumbnail can no longer drift. Handles the
                // gradient-SVG and plain flex-nested cases internally.
                <StaticTextBlock element={element} />
              )}
              </div>
              {!isEditingThis && renderResizeHandles(element)}
            </div>
          );
        }
        case 'image': {
          const imgClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          const imgEraserMaskStyle = getEraserMaskStyle(element);
          // Crop/fit-aware rendering. Without any crop props the legacy
          // objectFit:cover path renders — pixel-identical back-compat.
          const imgFit = element.fit || 'cover';
          const hasCustomCrop = !!(element.cropScale && element.cropScale !== 1)
            || !!element.cropOffsetX || !!element.cropOffsetY
            || !!element.cropRotation || imgFit !== 'cover';
          const imgPseudoFrame = {
            width: element.width,
            height: element.height,
            fit: imgFit,
            image: {
              offsetX: element.cropOffsetX || 0,
              offsetY: element.cropOffsetY || 0,
              scale: element.cropScale || 1,
            },
          };
          const imgLayout = hasCustomCrop
            ? computeFrameImageLayout(imgPseudoFrame, element.naturalWidth, element.naturalHeight)
            : null;
          const isCroppingImage = cropElementId === element.id;
          const cropGhostLayout = imgLayout || { left: 0, top: 0, width: element.width, height: element.height };

          // borderRadius for the clipped content layer (shared with thumbnail).
          const imgBorderRadius = imageBoxRadius(element);
          return (
            <div
              key={elementKey}
              data-frame-wrapper={element.id}
              style={{
                ...commonStyle,
                padding: 0,
                userSelect: 'none',
                WebkitUserSelect: 'none',
                // During inner-image edit the ghost + handles must show beyond
                // the box; the clip lives on the inner layer below.
                overflow: isCroppingImage ? 'visible' : 'hidden',
              }}
              onMouseDown={(e) => (isCroppingImage ? handleImageCropPanStart(e, element) : handleElementMouseDown(e, element))}
              onDoubleClick={(e) => { e.stopPropagation(); onStartCrop?.(element.id); }}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              onDragOver={(e) => handleImageDragOver(e, element)}
              onDragLeave={() => handleImageDragLeave(element)}
              onDrop={(e) => handleImageDrop(e, element)}
            >
              {/* Clipped content layer: always clips the rendered photo to the
                  element box / mask shape / corner radius. */}
              <div
                style={{
                  position: 'absolute', inset: 0,
                  overflow: 'hidden',
                  borderRadius: imgBorderRadius,
                  clipPath: imgClipId ? `url(#${imgClipId})` : undefined,
                  ...imgEraserMaskStyle,
                }}
              >
                {/* SVG clip-path defs for Figma-style mask */}
                {imgClipId && renderClipPathDefs(element)}
                {/* Eraser mask defs */}
                {renderEraserMaskDefs(element)}
                <img
                  src={element.src}
                  alt="element"
                  crossOrigin="anonymous"
                  onLoad={(e) => {
                    if (!element.naturalWidth || !element.naturalHeight) {
                      onUpdateElement(element.id, {
                        naturalWidth: e.target.naturalWidth,
                        naturalHeight: e.target.naturalHeight,
                      });
                    }
                  }}
                  draggable={false}
                  style={{
                    // Shared with the thumbnail (imageImgStyle): crop layout or cover.
                    ...imageImgStyle(element),
                    pointerEvents: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                  }}
                />
              </div>
              {/* Crop mode: ghost of the full photo beyond the element box
                  (unclipped — rendered in the outer layer). */}
              {isCroppingImage && (
                <img
                  src={element.src}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: cropGhostLayout.left, top: cropGhostLayout.top,
                    width: cropGhostLayout.width, height: cropGhostLayout.height,
                    transform: `rotate(${element.cropRotation || 0}deg)`,
                    opacity: 0.35,
                    pointerEvents: 'none',
                    maxWidth: 'none',
                  }}
                />
              )}
              {/* Legacy mask outline indicator */}
              {isSelected && element.mask && (
                <div
                  style={{
                    position: 'absolute',
                    left: element.mask.x,
                    top: element.mask.y,
                    width: element.mask.width,
                    height: element.mask.height,
                    border: '2px dashed #6366f1',
                    borderRadius: element.mask.type === 'ellipse' ? '50%' : 0,
                    pointerEvents: 'none'
                  }}
                />
              )}
              {isCroppingImage && renderCropControls(element, element.cropScale || 1, (e) => {
                const el = elementsRef.current.find((x) => x.id === element.id);
                if (!el) return;
                const sc = Number(e.target.value);
                const pseudo = {
                  width: el.width, height: el.height, fit: el.fit || 'cover',
                  image: { offsetX: 0, offsetY: 0, scale: sc },
                };
                const { offsetX, offsetY } = clampCropOffsets(
                  pseudo, el.naturalWidth, el.naturalHeight,
                  el.cropOffsetX || 0, el.cropOffsetY || 0
                );
                onUpdateElement(element.id, { cropScale: sc, cropOffsetX: offsetX, cropOffsetY: offsetY });
              })}
              {dropTargetId === element.id && (
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(139,92,246,0.12)',
                    boxShadow: 'inset 0 0 0 2px #8b5cf6',
                    pointerEvents: 'none', zIndex: 5,
                  }}
                />
              )}
              {isCroppingImage && renderInnerImageHandles(element, cropGhostLayout, 'image')}
              {!isCroppingImage && renderResizeHandles(element)}
            </div>
          );
        }
        case 'frame': {
          const frameClip = getFrameClip(element);
          const frameClipId = `frame-clip-${element.id}`;
          const fImg = element.image;
          const fLayout = fImg
            ? computeFrameImageLayout(element, fImg.naturalWidth, fImg.naturalHeight)
            : null;
          const isCroppingFrame = cropElementId === element.id;

          return (
            <div
              key={elementKey}
              data-testid={`frame-element-${element.id}`}
              data-frame-wrapper={element.id}
              style={{
                ...commonStyle,
                padding: 0,
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onMouseDown={(e) => (isCroppingFrame ? handleCropPanStart(e, element) : handleElementMouseDown(e, element))}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (element.image) onStartCrop?.(element.id);
              }}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              onDragOver={(e) => handleImageDragOver(e, element)}
              onDragLeave={() => handleImageDragLeave(element)}
              onDrop={(e) => handleImageDrop(e, element)}
            >
              {/* Clip def: normalized path scaled to frame px. userSpaceOnUse
                  units keep this compatible with utils/exportClipPath.js. */}
              <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                <defs>
                  <clipPath id={frameClipId}>
                    <path d={frameClip.path} transform={`scale(${element.width / frameClip.vbW} ${element.height / frameClip.vbH})`} />
                  </clipPath>
                </defs>
              </svg>
              {/* Crop mode: ghost of the full photo beyond the frame boundary */}
              {isCroppingFrame && fImg && fLayout && (
                <img
                  src={fImg.src}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: fLayout.left, top: fLayout.top,
                    width: fLayout.width, height: fLayout.height,
                    transform: `rotate(${fImg.rotation || 0}deg)`,
                    opacity: 0.35,
                    pointerEvents: 'none',
                    maxWidth: 'none',
                  }}
                />
              )}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  clipPath: `url(#${frameClipId})`,
                  overflow: 'hidden',
                  background: fImg ? undefined : '#e5e7eb',
                }}
              >
                {fImg ? (
                  <img
                    src={fImg.src}
                    alt=""
                    crossOrigin="anonymous"
                    draggable={false}
                    onLoad={(e) => {
                      // Persist natural dims once so fit math and export are
                      // deterministic on reload. updateElement does not
                      // snapshot history, so this is not an undo step.
                      if (!fImg.naturalWidth || !fImg.naturalHeight) {
                        onUpdateElement(element.id, {
                          image: {
                            ...fImg,
                            naturalWidth: e.target.naturalWidth,
                            naturalHeight: e.target.naturalHeight,
                          },
                        });
                      }
                    }}
                    style={{
                      position: 'absolute',
                      left: fLayout.left,
                      top: fLayout.top,
                      width: fLayout.width,
                      height: fLayout.height,
                      transform: `rotate(${fImg.rotation || 0}deg)`,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      maxWidth: 'none',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      position: 'absolute', inset: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#9ca3af',
                    }}
                  >
                    <FrameImagePlaceholderIcon style={{ width: '28%', height: '28%', maxWidth: 64, maxHeight: 64 }} />
                  </div>
                )}
              </div>
              {/* Dashed outline so empty/unselected frames are discoverable */}
              {!fImg && (
                <svg
                  viewBox={`0 0 ${frameClip.vbW} ${frameClip.vbH}`}
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}
                >
                  <path d={frameClip.path} fill="none" stroke="#9ca3af" strokeWidth={Math.max(frameClip.vbW, frameClip.vbH) * 0.008} strokeDasharray={`${Math.max(frameClip.vbW, frameClip.vbH) * 0.03} ${Math.max(frameClip.vbW, frameClip.vbH) * 0.02}`} vectorEffect="non-scaling-stroke" />
                </svg>
              )}
              {dropTargetId === element.id && (
                <svg
                  viewBox={`0 0 ${frameClip.vbW} ${frameClip.vbH}`}
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5, overflow: 'visible' }}
                >
                  <path d={frameClip.path} fill="rgba(139,92,246,0.12)" stroke="#8b5cf6" strokeWidth={Math.max(frameClip.vbW, frameClip.vbH) * 0.015} vectorEffect="non-scaling-stroke" />
                </svg>
              )}
              {isCroppingFrame && renderCropControls(element, fImg?.scale || 1, (e) => {
                const el = elementsRef.current.find((x) => x.id === element.id);
                if (!el || !el.image) return;
                const sc = Number(e.target.value);
                const { offsetX, offsetY } = clampCropOffsets(
                  { ...el, image: { ...el.image, scale: sc } },
                  el.image.naturalWidth, el.image.naturalHeight,
                  el.image.offsetX || 0, el.image.offsetY || 0
                );
                onUpdateElement(element.id, { image: { ...el.image, scale: sc, offsetX, offsetY } });
              })}
              {isCroppingFrame && fImg && renderInnerImageHandles(element, fLayout, 'frame')}
              {!isCroppingFrame && renderResizeHandles(element)}
            </div>
          );
        }
        case 'shape': {
          // Gradient support for shapes
          const shapeFillGradId = `shape-fill-grad-${element.id}`;
          const shapeStrokeGradId = `shape-stroke-grad-${element.id}`;
          const shapeHasFillGrad = element.fillGradient && (element.fillGradient.colors || []).length >= 2;
          const shapeHasStrokeGrad = element.strokeGradient && (element.strokeGradient.colors || []).length >= 2;

          // Use SVG for all shapes to properly support stroke and corner radius
          const renderShapeSVG = () => {
            const w = Math.max(1, Number(element.width) || 1);
            const h = Math.max(1, Number(element.height) || 1);
            const isMaskShapeEl = element.isMaskShape || element.isClipBase;
            // Mask shape: transparent fill, dashed outline ONLY when selected so it's invisible at rest
            const maskSelected = isSelected || isMultiSelected;
            const fill = isMaskShapeEl ? 'none' : (shapeHasFillGrad ? `url(#${shapeFillGradId})` : (element.fill || '#6366f1'));
            const stroke = isMaskShapeEl ? (maskSelected ? 'rgba(99,102,241,0.8)' : 'none') : (shapeHasStrokeGrad ? `url(#${shapeStrokeGradId})` : (element.stroke || '#000000'));
            const strokeWidth = isMaskShapeEl ? (maskSelected ? Math.max(1.5, Math.max(0, Number(element.strokeWidth) || 0)) : 0) : Math.max(0, Number(element.strokeWidth) || 0);
            const radius = Math.max(0, Number(element.cornerRadius) || 0);
            const perCorner = element.cornerRadii;

            switch (element.shape) {
              case 'rect':
              case 'roundedRect': {
                const fallback = element.shape === 'roundedRect' ? 12 : 0;
                const maxRx = Math.max(0, (w - strokeWidth) / 2);
                const maxRy = Math.max(0, (h - strokeWidth) / 2);

                const innerW = w - strokeWidth;
                const innerH = h - strokeWidth;
                const innerX = strokeWidth / 2;
                const innerY = strokeWidth / 2;

                // Per-corner radii take precedence; otherwise fall back to the
                // single `cornerRadius`. Each corner is independently clamped to
                // the available half-extent of the rect (after stroke inset).
                if (perCorner && typeof perCorner === 'object') {
                  const clampPair = (a, b, dim) => {
                    const av = Math.max(0, Number(a) || 0);
                    const bv = Math.max(0, Number(b) || 0);
                    if (av + bv <= dim || av + bv === 0) return [av, bv];
                    const k = dim / (av + bv);
                    return [av * k, bv * k];
                  };
                  let tl = Math.max(0, Number(perCorner.tl) || 0);
                  let tr = Math.max(0, Number(perCorner.tr) || 0);
                  let br = Math.max(0, Number(perCorner.br) || 0);
                  let bl = Math.max(0, Number(perCorner.bl) || 0);
                  // CSS-style overlap clamping per edge.
                  [tl, tr] = clampPair(tl, tr, innerW);
                  [bl, br] = clampPair(bl, br, innerW);
                  [tl, bl] = clampPair(tl, bl, innerH);
                  [tr, br] = clampPair(tr, br, innerH);
                  const x = innerX, y = innerY;
                  const x2 = x + innerW, y2 = y + innerH;
                  const d = [
                    `M${x + tl},${y}`,
                    `L${x2 - tr},${y}`,
                    tr ? `A${tr},${tr} 0 0 1 ${x2},${y + tr}` : '',
                    `L${x2},${y2 - br}`,
                    br ? `A${br},${br} 0 0 1 ${x2 - br},${y2}` : '',
                    `L${x + bl},${y2}`,
                    bl ? `A${bl},${bl} 0 0 1 ${x},${y2 - bl}` : '',
                    `L${x},${y + tl}`,
                    tl ? `A${tl},${tl} 0 0 1 ${x + tl},${y}` : '',
                    'Z',
                  ].filter(Boolean).join(' ');
                  return (
                    <path
                      d={d}
                      fill={fill}
                      stroke={strokeWidth ? stroke : 'none'}
                      strokeWidth={strokeWidth}
                      strokeLinejoin="round"
                    />
                  );
                }

                const rx = Math.min(Number(radius) || fallback, maxRx);
                const ry = Math.min(Number(radius) || fallback, maxRy);
                return (
                  <rect
                    x={innerX}
                    y={innerY}
                    width={innerW}
                    height={innerH}
                    rx={rx}
                    ry={ry}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                  />
                );
              }
              case 'circle':
                const cx = w / 2;
                const cy = h / 2;
                return (
                  <ellipse
                    cx={cx}
                    cy={cy}
                    rx={w / 2 - strokeWidth / 2}
                    ry={h / 2 - strokeWidth / 2}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                  />
                );
              case 'triangle':
                const triPoints = `${w / 2},${strokeWidth / 2} ${strokeWidth / 2},${h - strokeWidth / 2} ${w - strokeWidth / 2},${h - strokeWidth / 2}`;
                return (
                  <polygon
                    points={triPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'star':
                const starPath = (() => {
                  const cx = w / 2;
                  const cy = h / 2;
                  const outer = Math.min(w, h) / 2 - strokeWidth;
                  const inner = outer * 0.4;
                  let path = '';
                  for (let i = 0; i < 10; i++) {
                    const r = i % 2 === 0 ? outer : inner;
                    const angle = (i * 36 - 90) * Math.PI / 180;
                    const x = cx + r * Math.cos(angle);
                    const y = cy + r * Math.sin(angle);
                    path += (i === 0 ? 'M' : 'L') + `${x},${y}`;
                  }
                  return path + 'Z';
                })();
                return (
                  <path
                    d={starPath}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'hexagon':
                const hexPoints = (() => {
                  const cx = w / 2;
                  const cy = h / 2;
                  const r = Math.min(w, h) / 2 - strokeWidth;
                  let points = '';
                  for (let i = 0; i < 6; i++) {
                    const angle = (i * 60 - 90) * Math.PI / 180;
                    const x = cx + r * Math.cos(angle);
                    const y = cy + r * Math.sin(angle);
                    points += `${x},${y} `;
                  }
                  return points.trim();
                })();
                return (
                  <polygon
                    points={hexPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'pentagon':
                const pentPoints = (() => {
                  const cx = w / 2;
                  const cy = h / 2;
                  const r = Math.min(w, h) / 2 - strokeWidth;
                  let points = '';
                  for (let i = 0; i < 5; i++) {
                    const angle = (i * 72 - 90) * Math.PI / 180;
                    const x = cx + r * Math.cos(angle);
                    const y = cy + r * Math.sin(angle);
                    points += `${x},${y} `;
                  }
                  return points.trim();
                })();
                return (
                  <polygon
                    points={pentPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'diamond':
                const diamondPoints = `${w / 2},${strokeWidth / 2} ${w - strokeWidth / 2},${h / 2} ${w / 2},${h - strokeWidth / 2} ${strokeWidth / 2},${h / 2}`;
                return (
                  <polygon
                    points={diamondPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'heart':
                const heartPath = `M${w / 2},${h * 0.9} C${w * 0.2},${h * 0.7} ${w * 0.05},${h * 0.5} ${w * 0.05},${h * 0.3} C${w * 0.05},${h * 0.15} ${w * 0.15},${h * 0.05} ${w * 0.275},${h * 0.05} C${w * 0.35},${h * 0.05} ${w * 0.42},${h * 0.1} ${w / 2},${h * 0.2} C${w * 0.58},${h * 0.1} ${w * 0.65},${h * 0.05} ${w * 0.725},${h * 0.05} C${w * 0.85},${h * 0.05} ${w * 0.95},${h * 0.15} ${w * 0.95},${h * 0.3} C${w * 0.95},${h * 0.5} ${w * 0.8},${h * 0.7} ${w / 2},${h * 0.9} Z`;
                return (
                  <path
                    d={heartPath}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                  />
                );
              case 'arrow':
                const arrowPoints = `${strokeWidth / 2},${h * 0.3} ${w * 0.7},${h * 0.3} ${w * 0.7},${strokeWidth / 2} ${w - strokeWidth / 2},${h / 2} ${w * 0.7},${h - strokeWidth / 2} ${w * 0.7},${h * 0.7} ${strokeWidth / 2},${h * 0.7}`;
                return (
                  <polygon
                    points={arrowPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'octagon':
                const octPoints = (() => {
                  const cx = w / 2;
                  const cy = h / 2;
                  const r = Math.min(w, h) / 2 - strokeWidth;
                  let points = '';
                  for (let i = 0; i < 8; i++) {
                    const angle = (i * 45 - 90 + 22.5) * Math.PI / 180;
                    const x = cx + r * Math.cos(angle);
                    const y = cy + r * Math.sin(angle);
                    points += `${x},${y} `;
                  }
                  return points.trim();
                })();
                return (
                  <polygon
                    points={octPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'cross':
                const crossW = w * 0.35;
                const crossH = h * 0.35;
                const crossPoints = `${w / 2 - crossW / 2},${strokeWidth / 2} ${w / 2 + crossW / 2},${strokeWidth / 2} ` +
                  `${w / 2 + crossW / 2},${h / 2 - crossH / 2} ${w - strokeWidth / 2},${h / 2 - crossH / 2} ` +
                  `${w - strokeWidth / 2},${h / 2 + crossH / 2} ${w / 2 + crossW / 2},${h / 2 + crossH / 2} ` +
                  `${w / 2 + crossW / 2},${h - strokeWidth / 2} ${w / 2 - crossW / 2},${h - strokeWidth / 2} ` +
                  `${w / 2 - crossW / 2},${h / 2 + crossH / 2} ${strokeWidth / 2},${h / 2 + crossH / 2} ` +
                  `${strokeWidth / 2},${h / 2 - crossH / 2} ${w / 2 - crossW / 2},${h / 2 - crossH / 2}`;
                return (
                  <polygon
                    points={crossPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'cylinder':
                const cylRx = w / 2 - strokeWidth / 2;
                const cylRy = h * 0.15;
                const cylPath = `M ${strokeWidth / 2},${cylRy + strokeWidth / 2} ` +
                  `L ${strokeWidth / 2},${h - cylRy - strokeWidth / 2} ` +
                  `A ${cylRx},${cylRy} 0 0,0 ${w - strokeWidth / 2},${h - cylRy - strokeWidth / 2} ` +
                  `L ${w - strokeWidth / 2},${cylRy + strokeWidth / 2} ` +
                  `A ${cylRx},${cylRy} 0 0,1 ${strokeWidth / 2},${cylRy + strokeWidth / 2} Z`;
                return (
                  <g>
                    <ellipse
                      cx={w / 2}
                      cy={cylRy + strokeWidth / 2}
                      rx={cylRx}
                      ry={cylRy}
                      fill={fill}
                      stroke={strokeWidth ? stroke : 'none'}
                      strokeWidth={strokeWidth}
                    />
                    <path
                      d={cylPath}
                      fill={fill}
                      stroke={strokeWidth ? stroke : 'none'}
                      strokeWidth={strokeWidth}
                      strokeLinejoin="round"
                    />
                  </g>
                );
              case 'parallelogram':
                const skewX = w * 0.25;
                const paraPoints = `${skewX + strokeWidth / 2},${strokeWidth / 2} ${w - strokeWidth / 2},${strokeWidth / 2} ` +
                  `${w - skewX - strokeWidth / 2},${h - strokeWidth / 2} ${strokeWidth / 2},${h - strokeWidth / 2}`;
                return (
                  <polygon
                    points={paraPoints}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                  />
                );
              case 'line':
                return (
                  <line
                    x1={strokeWidth / 2}
                    y1={h / 2}
                    x2={w - strokeWidth / 2}
                    y2={h / 2}
                    stroke={fill}
                    strokeWidth={Math.max(h, strokeWidth || 2)}
                    strokeLinecap="round"
                  />
                );
              default: {
                const maxRx = Math.max(0, (w - strokeWidth) / 2);
                const maxRy = Math.max(0, (h - strokeWidth) / 2);
                const rx = Math.min(Number(radius) || 0, maxRx);
                const ry = Math.min(Number(radius) || 0, maxRy);
                return (
                  <rect
                    x={strokeWidth / 2}
                    y={strokeWidth / 2}
                    width={w - strokeWidth}
                    height={h - strokeWidth}
                    rx={rx}
                    ry={ry}
                    fill={fill}
                    stroke={strokeWidth ? stroke : 'none'}
                    strokeWidth={strokeWidth}
                  />
                );
              }
            }
          };

          const shapeClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          const isMaskShapeDisplay = element.isMaskShape || element.isClipBase;
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: buildElementTransform(element),
                cursor: element.locked ? 'not-allowed' : 'move',
                pointerEvents: element.locked ? 'none' : 'auto',
                clipPath: shapeClipId ? `url(#${shapeClipId})` : undefined,
                mixBlendMode: blendModeStyle(element),
                boxShadow: shadowBoxCss(element),
                ...getEraserMaskStyle(element),
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onDoubleClick={(e) => {
                // Shape mask host: double-click edits the clipped image inside.
                const t = resolveImageReplaceTarget(element, elementsRef.current);
                if (t && t.kind === 'clippedImage') {
                  e.stopPropagation();
                  onStartCrop?.(t.targetId);
                }
              }}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              onDragOver={(e) => handleImageDragOver(e, element)}
              onDragLeave={() => handleImageDragLeave(element)}
              onDrop={(e) => handleImageDrop(e, element)}
              data-testid={`shape-element-${element.id}`}
            >
              {shapeClipId && renderClipPathDefs(element)}
              {renderEraserMaskDefs(element)}
              {(isSelected || isMultiSelected) && (
                <div
                  style={{
                    position: 'absolute',
                    inset: -4,
                    border: `2px solid ${isSelected ? '#6366f1' : '#a855f7'}`,
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              )}
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${element.width} ${element.height}`}
                // fillOpacity / strokeOpacity are SVG presentation attributes that
                // inherit into child shapes — single place to apply per-element opacity.
                fillOpacity={isMaskShapeDisplay ? undefined : (element.fillOpacity ?? 1)}
                strokeOpacity={isMaskShapeDisplay ? undefined : (element.strokeOpacity ?? 1)}
                style={{
                  filter: composeFilter(element),
                  opacity: element.opacity || 1,
                  overflow: 'visible',
                  // Dashed outline only when the mask shape is selected — invisible otherwise
                  strokeDasharray: (isMaskShapeDisplay && (isSelected || isMultiSelected)) ? '6 4' : undefined,
                }}
              >
                {(shapeHasFillGrad || shapeHasStrokeGrad) && (
                  <defs>
                    {shapeHasFillGrad && buildSVGGradient(shapeFillGradId, element.fillGradient)}
                    {shapeHasStrokeGrad && buildSVGGradient(shapeStrokeGradId, element.strokeGradient)}
                  </defs>
                )}
                {renderShapeSVG()}
              </svg>
              {dropTargetId === element.id && (
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(139,92,246,0.12)',
                    boxShadow: 'inset 0 0 0 2px #8b5cf6',
                    borderRadius: '4px',
                    pointerEvents: 'none', zIndex: 2,
                  }}
                />
              )}
              {renderResizeHandles(element)}
            </div>
          );
        }
        case 'svg': {
          const svgClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: buildElementTransform(element),
                cursor: element.locked ? 'not-allowed' : 'move',
                pointerEvents: element.locked ? 'none' : 'auto',
                opacity: element.opacity || 1,
                filter: composeFilter(element),
                mixBlendMode: blendModeStyle(element),
                boxShadow: shadowBoxCss(element),
                clipPath: svgClipId ? `url(#${svgClipId})` : undefined,
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              onDragOver={(e) => handleImageDragOver(e, element)}
              onDragLeave={() => handleImageDragLeave(element)}
              onDrop={(e) => handleImageDrop(e, element)}
              data-testid={`svg-element-${element.id}`}
            >
              {svgClipId && renderClipPathDefs(element)}
              {(isSelected || isMultiSelected) && (
                <div
                  style={{
                    position: 'absolute',
                    inset: -4,
                    border: `2px solid ${isSelected ? '#6366f1' : '#a855f7'}`,
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              )}
              <SvgContent element={element} />
              {renderResizeHandles(element)}
            </div>
          );
        }
        case 'sticker': {
          const stickerClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: buildElementTransform(element),
                cursor: element.locked ? 'not-allowed' : 'move',
                pointerEvents: element.locked ? 'none' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: `${Math.min(element.width, element.height) * 0.8}px`,
                lineHeight: 1,
                opacity: element.opacity || 1,
                filter: composeFilter(element),
                mixBlendMode: blendModeStyle(element),
                boxShadow: shadowBoxCss(element),
                clipPath: stickerClipId ? `url(#${stickerClipId})` : undefined,
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              data-testid={`sticker-element-${element.id}`}
            >
              {stickerClipId && renderClipPathDefs(element)}
              {(isSelected || isMultiSelected) && (
                <div
                  style={{
                    position: 'absolute',
                    inset: -4,
                    border: `2px solid ${isSelected ? '#6366f1' : '#a855f7'}`,
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              )}
              {element.emoji}
              {renderResizeHandles(element)}
            </div>
          );
        }
        case 'chart': {
          const chartClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          return (
            <div
              key={elementKey}
              style={{
                ...commonStyle,
                backgroundColor: element.chartBackground === 'transparent' ? 'transparent' : (element.chartBackground || '#ffffff'),
                padding: '20px',
                borderRadius: '8px',
                boxShadow: element.chartBackground === 'transparent' ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
                clipPath: chartClipId ? `url(#${chartClipId})` : undefined,
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
            >
              {chartClipId && renderClipPathDefs(element)}
              <svg width="100%" height="100%" viewBox="0 0 400 300" style={{ pointerEvents: 'none' }}>
                {renderChartSVG(element)}
              </svg>
              {renderResizeHandles(element)}
            </div>
          );
        }

        case 'table': {
          const tableClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          const getTableStyles = () => {
            const baseStyles = {
              width: '100%',
              height: '100%',
              borderCollapse: 'collapse',
              backgroundColor: '#ffffff',
              pointerEvents: 'none'
            };

            switch (element.design) {
              case 'striped':
                return { ...baseStyles, 'tr:nth-child(even)': { backgroundColor: '#f9fafb' } };
              case 'bordered':
                return { ...baseStyles, border: `2px solid ${element.borderColor || '#d1d5db'}` };
              case 'modern':
                return { ...baseStyles, borderSpacing: '0 8px' };
              default:
                return baseStyles;
            }
          };

          return (
            <div
              key={elementKey}
              style={{
                ...commonStyle,
                backgroundColor: '#ffffff',
                padding: '10px',
                overflow: 'auto',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                clipPath: tableClipId ? `url(#${tableClipId})` : undefined,
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
            >
              {tableClipId && renderClipPathDefs(element)}
              <table style={getTableStyles()}>
                <thead>
                  <tr>
                    {Array.from({ length: element.cols || 3 }).map((_, colIndex) => (
                      <th
                        key={colIndex}
                        style={{
                          backgroundColor: element.headerColor || '#6366f1',
                          color: '#ffffff',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontSize: '14px',
                          fontWeight: '600',
                          border: element.design === 'bordered' ? `1px solid ${element.borderColor || '#d1d5db'}` : 'none'
                        }}
                      >
                        Header {colIndex + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: (element.rows || 3) - 1 }).map((_, rowIndex) => (
                    <tr
                      key={rowIndex}
                      style={{
                        backgroundColor: element.design === 'striped' && rowIndex % 2 === 0 ? '#f9fafb' : '#ffffff',
                        transition: element.design === 'hover' ? 'background-color 0.2s' : 'none'
                      }}
                    >
                      {Array.from({ length: element.cols || 3 }).map((_, colIndex) => (
                        <td
                          key={colIndex}
                          style={{
                            padding: '12px 8px',
                            fontSize: '13px',
                            color: '#374151',
                            border: element.design === 'bordered' ? `1px solid ${element.borderColor || '#d1d5db'}` : 'none',
                            borderTop: element.design !== 'bordered' ? `1px solid #e5e7eb` : undefined
                          }}
                        >
                          Cell {rowIndex + 1}-{colIndex + 1}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {renderResizeHandles(element)}
            </div>
          );
        }

        case 'drawing': {
          const drawingClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          const isDrawingMask = element.isMaskShape || element.isClipBase;
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: element.locked ? 'not-allowed' : 'move',
                border: (isSelected || isMultiSelected) ? `2px solid ${isSelected ? '#6366f1' : '#a855f7'}` : 'none',
                boxSizing: 'border-box',
                pointerEvents: element.locked ? 'none' : 'auto',
                clipPath: drawingClipId ? `url(#${drawingClipId})` : undefined,
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              data-testid={`drawing-element-${element.id}`}
            >
              {drawingClipId && renderClipPathDefs(element)}
              <svg
                width={element.width}
                height={element.height}
                viewBox={`0 0 ${element.width} ${element.height}`}
                style={{
                  filter: element.filter || 'none',
                  opacity: element.opacity || 1,
                  pointerEvents: 'none',
                  overflow: 'visible'
                }}
              >
                <path
                  d={element.path}
                  stroke={isDrawingMask ? 'rgba(99,102,241,0.8)' : element.stroke}
                  strokeWidth={element.strokeWidth}
                  fill={isDrawingMask ? 'none' : 'none'}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={isDrawingMask ? '6 4' : undefined}
                />
              </svg>
              {renderResizeHandles(element)}
            </div>
          );
        }

        case 'qrcode': {
          const qrClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: element.locked ? 'not-allowed' : 'move',
                border: (isSelected || isMultiSelected) ? `2px solid ${isSelected ? '#6366f1' : '#a855f7'}` : 'none',
                boxSizing: 'border-box',
                pointerEvents: element.locked ? 'none' : 'auto',
                backgroundColor: element.bgColor || '#ffffff',
                padding: '8px',
                borderRadius: '8px',
                clipPath: qrClipId ? `url(#${qrClipId})` : undefined,
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              data-testid={`qrcode-element-${element.id}`}
            >
              {qrClipId && renderClipPathDefs(element)}
              <StaticQR element={element} />
              {renderResizeHandles(element)}
            </div>
          );
        }

        case 'penPath': {
          // Pen tool created path (Bézier curves)
          const penClipId = element.clippedTo ? `mask-clip-${element.id}` : null;
          const isPenMask = element.isMaskShape || element.isClipBase;
          const hasStrokeGradient = element.strokeGradient && element.strokeGradient.type;
          const hasFillGradient = element.fillGradient && element.fillGradient.type;

          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: element.locked ? 'not-allowed' : 'move',
                border: (isSelected || isMultiSelected) ? `2px solid ${isSelected ? '#6366f1' : '#a855f7'}` : 'none',
                boxSizing: 'border-box',
                pointerEvents: element.locked ? 'none' : 'auto',
                clipPath: penClipId ? `url(#${penClipId})` : undefined,
                ...getEraserMaskStyle(element),
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onDoubleClick={(e) => { e.stopPropagation(); if (typeof onToolChange === 'function') onToolChange('direct-selection'); }}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              data-testid={`penpath-element-${element.id}`}
            >
              {penClipId && renderClipPathDefs(element)}
              {renderEraserMaskDefs(element)}
              <svg
                width={element.width}
                height={element.height}
                viewBox={`0 0 ${element.width} ${element.height}`}
                style={{
                  overflow: 'visible',
                  filter: element.filter || 'none',
                  opacity: element.opacity || 1,
                  pointerEvents: 'none'
                }}
              >
                {/* Gradient definitions */}
                {(hasStrokeGradient || hasFillGradient) && (
                  <defs>
                    {hasStrokeGradient && buildSVGGradient(`stroke-grad-${element.id}`, element.strokeGradient)}
                    {hasFillGradient && buildSVGGradient(`fill-grad-${element.id}`, element.fillGradient)}
                  </defs>
                )}
                <path
                  d={element.path}
                  stroke={isPenMask ? 'rgba(99,102,241,0.8)' : (hasStrokeGradient ? `url(#stroke-grad-${element.id})` : element.stroke)}
                  strokeWidth={element.strokeWidth}
                  fill={isPenMask ? 'none' : (hasFillGradient ? `url(#fill-grad-${element.id})` : (element.fill || 'none'))}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={isPenMask ? '6 4' : undefined}
                />
              </svg>
              {/* Anchor point overlay — shown when Direct Selection tool is active */}
              {isSelected && activeTool === 'direct-selection' && element.points && (
                <svg
                  width={element.width}
                  height={element.height}
                  viewBox={`0 0 ${element.width} ${element.height}`}
                  style={{
                    position: 'absolute', top: 0, left: 0,
                    pointerEvents: 'none', overflow: 'visible',
                  }}
                >
                  {/* ── Segment hit areas (rendered first = lower z; transparent wide paths) ── */}
                  {element.points.map((point, idx) => {
                    if (!element.closed && idx === element.points.length - 1) return null;
                    const nextIdx = element.closed ? (idx + 1) % element.points.length : idx + 1;
                    const nextPt = element.points[nextIdx];
                    if (!nextPt) return null;
                    const cp1 = point.handleOut || point;
                    const cp2 = nextPt.handleIn || nextPt;
                    return (
                      <path
                        key={`seg-${idx}`}
                        d={`M ${point.x} ${point.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${nextPt.x} ${nextPt.y}`}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={10}
                        style={{ pointerEvents: 'stroke', cursor: 'cell' }}
                        onMouseDown={(e) => handleSegmentClick(e, element, idx)}
                      />
                    );
                  })}

                  {/* ── Anchor points and bezier handles ── */}
                  {element.points.map((point, idx) => (
                    <g key={idx}>
                      {/* Handle line + circle – In */}
                      {point.handleIn && (
                        <>
                          <line x1={point.x} y1={point.y} x2={point.handleIn.x} y2={point.handleIn.y}
                            stroke="#6366f1" strokeWidth={1} />
                          <circle cx={point.handleIn.x} cy={point.handleIn.y} r={4} fill="#ec4899"
                            style={{ pointerEvents: 'auto', cursor: 'grab' }}
                            onMouseDown={(e) => handleHandleMouseDown(e, element, idx, 'in')} />
                        </>
                      )}
                      {/* Handle line + circle – Out */}
                      {point.handleOut && (
                        <>
                          <line x1={point.x} y1={point.y} x2={point.handleOut.x} y2={point.handleOut.y}
                            stroke="#6366f1" strokeWidth={1} />
                          <circle cx={point.handleOut.x} cy={point.handleOut.y} r={4} fill="#ec4899"
                            style={{ pointerEvents: 'auto', cursor: 'grab' }}
                            onMouseDown={(e) => handleHandleMouseDown(e, element, idx, 'out')} />
                        </>
                      )}
                      {/* Anchor point (rendered last = on top of handles) */}
                      <circle
                        cx={point.x} cy={point.y} r={5}
                        fill={selectedAnchorIndex === idx ? '#6366f1' : '#ffffff'}
                        stroke="#6366f1" strokeWidth={2}
                        style={{ pointerEvents: 'auto', cursor: 'move' }}
                        onMouseDown={(e) => handleAnchorPointMouseDown(e, element, idx)}
                      />
                    </g>
                  ))}
                </svg>
              )}
              {renderResizeHandles(element)}
            </div>
          );
        }

        case 'brush': {
          const brushPreset = BRUSH_PRESETS.find(p => p.id === element.brushType) || BRUSH_PRESETS[0];
          const brushFilterId = `brush-filter-${element.id}`;
          const bStrokeW = (element.strokeWidth || 4) * brushPreset.widthMultiplier;
          const bOpacity = (element.opacity ?? 1) * brushPreset.opacityMultiplier;
          const bColor = element.stroke || '#000000';
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: element.locked ? 'not-allowed' : 'move',
                border: (isSelected || isMultiSelected) ? `2px dashed ${isSelected ? '#6366f1' : '#a5b4fc'}` : 'none',
                boxSizing: 'border-box',
                pointerEvents: element.locked ? 'none' : 'auto',
                ...getEraserMaskStyle(element),
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              data-testid={`brush-element-${element.id}`}
            >
              {renderEraserMaskDefs(element)}
              <svg width="100%" height="100%" style={{ pointerEvents: 'none', overflow: 'visible' }}>
                <defs>
                  {brushPreset.filterType === 'chalk' && (
                    <filter id={brushFilterId} x="-15%" y="-40%" width="130%" height="180%">
                      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" seed="3" result="noise" />
                      <feDisplacementMap in="SourceGraphic" in2="noise" scale={bStrokeW * 0.7} xChannelSelector="R" yChannelSelector="G" result="displaced" />
                      <feComposite in="displaced" in2="SourceGraphic" operator="in" />
                    </filter>
                  )}
                  {brushPreset.filterType === 'spray' && (
                    <filter id={brushFilterId} x="-60%" y="-120%" width="220%" height="340%">
                      <feTurbulence type="turbulence" baseFrequency="0.025" numOctaves="1" seed="7" result="noise" />
                      <feDisplacementMap in="SourceGraphic" in2="noise" scale={bStrokeW * 1.8} xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                  )}
                  {brushPreset.filterType === 'texture' && (
                    <filter id={brushFilterId} x="-5%" y="-20%" width="110%" height="140%">
                      <feTurbulence type="fractalNoise" baseFrequency="0.4" numOctaves="4" seed="2" result="noise" />
                      <feColorMatrix in="noise" type="saturate" values="0" result="greyNoise" />
                      <feBlend in="SourceGraphic" in2="greyNoise" mode="multiply" result="blended" />
                      <feComposite in="blended" in2="SourceGraphic" operator="in" />
                    </filter>
                  )}
                </defs>

                {brushPreset.filterType === 'calligraphy' ? (
                  /* Calligraphy: two overlapping skewed strokes to simulate angled nib */
                  <g transform="skewX(-18)">
                    <path d={element.path} stroke={bColor} strokeWidth={bStrokeW * 0.2}
                      strokeOpacity={bOpacity} fill="none" strokeLinecap="butt" strokeLinejoin="miter" />
                    <path d={element.path} stroke={bColor} strokeWidth={bStrokeW}
                      strokeOpacity={bOpacity * 0.45} fill="none" strokeLinecap="butt" strokeLinejoin="miter" />
                  </g>
                ) : (
                  <path
                    d={element.path}
                    stroke={bColor}
                    strokeWidth={bStrokeW}
                    strokeOpacity={bOpacity}
                    fill="none"
                    strokeLinecap={brushPreset.strokeLinecap}
                    strokeLinejoin={brushPreset.strokeLinejoin}
                    filter={brushPreset.filterType ? `url(#${brushFilterId})` : undefined}
                  />
                )}
              </svg>
              {renderResizeHandles(element)}
            </div>
          );
        }

        case 'eraser':
          // Eraser stroke element - renders as white/transparent stroke
          return (
            <div
              key={elementKey}
              style={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: element.locked ? 'not-allowed' : 'move',
                border: (isSelected || isMultiSelected) ? `2px dashed ${isSelected ? '#ef4444' : '#f87171'}` : 'none',
                boxSizing: 'border-box',
                pointerEvents: element.locked ? 'none' : 'auto'
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element)}
              onContextMenu={(e) => handleElementContextMenu(e, element)}
              data-testid={`eraser-element-${element.id}`}
            >
              <svg
                width="100%"
                height="100%"
                style={{
                  pointerEvents: 'none'
                }}
              >
                <defs>
                  <filter id={`blur-${element.id}`}>
                    <feGaussianBlur stdDeviation={element.hardness < 50 ? (100 - element.hardness) / 10 : 0} />
                  </filter>
                </defs>
                <path
                  d={element.path}
                  stroke="#ffffff"
                  strokeWidth={element.strokeWidth}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter={element.hardness < 50 ? `url(#blur-${element.id})` : undefined}
                />
              </svg>
              {renderResizeHandles(element)}
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="flex-1 flex flex-col bg-gray-50 relative overflow-hidden">
        {/* Top Toolbar */}
        <div className="h-14 bg-white border-b flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Input
              value={projectName}
              onChange={(e) => onProjectNameChange(e.target.value)}
              className="w-64 h-9 text-sm"
              placeholder="Project name"
            />

            <div className="flex items-center gap-2 pl-3 border-l">
              <Button variant="ghost" size="icon" title="Undo (Ctrl+Z)" onClick={() => onUndo?.()} disabled={!canUndo}>
                <Undo2 className={`w-4 h-4 ${!canUndo ? 'opacity-30' : ''}`} />
              </Button>
              <Button variant="ghost" size="icon" title="Redo (Ctrl+Shift+Z)" onClick={() => onRedo?.()} disabled={!canRedo}>
                <Redo2 className={`w-4 h-4 ${!canRedo ? 'opacity-30' : ''}`} />
              </Button>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              <Button
                variant={activeTool === TOOLS.SELECT ? "secondary" : "ghost"}
                size="icon"
                title="Selection Tool (V)"
                onClick={() => typeof onToolChange === 'function' && onToolChange(TOOLS.SELECT)}
                className={activeTool === TOOLS.SELECT ? "bg-purple-100 text-purple-700" : ""}
              >
                <MousePointer2 className="w-4 h-4" />
              </Button>
              <Button
                variant={activeTool === TOOLS.PEN ? "secondary" : "ghost"}
                size="icon"
                title="Pen Tool (P)"
                onClick={() => {
                  if (typeof onToolChange === 'function') onToolChange(TOOLS.PEN);
                  setPenPoints([]);
                  setPenCursorPos(null);
                  setPenDragAnchor(null);
                }}
                className={activeTool === TOOLS.PEN ? "bg-purple-100 text-purple-700" : ""}
              >
                {/* Fountain pen nib — fills ~20×20 of 24×24 viewBox to match lucide icon scale */}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  {/* Nib body: tip bottom-left (3,21), curved back spans to (21,3) */}
                  <path d="M3 21 L7 5 Q12 1 21 5 L18 17 Z" />
                  {/* Center slit from tip toward vent hole edge */}
                  <line x1="3" y1="21" x2="11" y2="12" />
                  {/* Breather / vent hole — centered inside nib body */}
                  <circle cx="13" cy="10" r="2.5" />
                </svg>
              </Button>
              <Button
                variant={activeTool === TOOLS.BRUSH ? "secondary" : "ghost"}
                size="icon"
                title="Brush Tool (B)"
                onClick={() => {
                  if (typeof onToolChange === 'function') onToolChange(TOOLS.BRUSH);
                  setBrushPoints([]);
                  setBrushCursorPos(null);
                  setBrushIsDrawing(false);
                }}
                className={activeTool === TOOLS.BRUSH ? "bg-purple-100 text-purple-700" : ""}
              >
                <Paintbrush className="w-4 h-4" />
              </Button>
              <Button
                variant={activeTool === TOOLS.ERASER ? "secondary" : "ghost"}
                size="icon"
                title="Eraser Tool (E)"
                onClick={() => {
                  if (typeof onToolChange === 'function') onToolChange(TOOLS.ERASER);
                  setEraserCursorPos(null);
                }}
                className={activeTool === TOOLS.ERASER ? "bg-red-100 text-red-600" : ""}
              >
                <Eraser className="w-4 h-4" />
              </Button>
              <Button
                variant={activeTool === TOOLS.MASK ? "secondary" : "ghost"}
                size="icon"
                title="Clipping Mask Tool (M)"
                onClick={() => {
                  if (typeof onToolChange === 'function') onToolChange(TOOLS.MASK);
                  setMaskHoverElementId(null);
                }}
                className={activeTool === TOOLS.MASK ? "bg-indigo-100 text-indigo-700" : ""}
              >
                {/* Clipping mask — diagonal Venn fills full 20×20 of the 24×24 viewBox */}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  {/* Content layer — dashed top-left circle, spans (2,2)→(16,16) */}
                  <circle cx="9" cy="9" r="7" strokeDasharray="3.5 2" />
                  {/* Mask shape — solid bottom-right circle, spans (8,8)→(22,22) */}
                  <circle cx="15" cy="15" r="7" />
                  {/* Intersection lens — diagonal almond shape at ~12% opacity */}
                  <path d="M16 8 A7 7 0 0 1 8 16 A7 7 0 0 1 16 8Z" fill="currentColor" fillOpacity="0.12" stroke="none" />
                </svg>
              </Button>
            </div>
          </div>

          {/* Contextual Top Toolbar Tools */}
          <div className="flex items-center gap-2 flex-1 justify-center">
            {selectedElements?.length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-700 font-medium"
                onClick={handleCreateMask}
                title="Use top element as Mask"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M12 12C12 8.68629 14.6863 6 18 6C21.3137 6 24 8.68629 24 12C24 15.3137 21.3137 18 18 18C14.6863 18 12 15.3137 12 12ZM18 8C15.7909 8 14 9.79086 14 12C14 14.2091 15.7909 16 18 16C20.2091 16 22 14.2091 22 12C22 9.79086 20.2091 8 18 8Z" fill="currentColor" />
                  <path d="M6 18C9.31371 18 12 15.3137 12 12C12 8.68629 9.31371 6 6 6C2.68629 6 0 8.68629 0 12C0 15.3137 2.68629 18 6 18Z" fill="currentColor" />
                </svg>
                Use as Mask
              </Button>
            )}

            {selectedElement && (selectedElement.isMaskShape || selectedElement.clippedTo) && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-rose-600 border-rose-200 bg-rose-50 hover:bg-rose-100 font-medium"
                onClick={handleReleaseMask}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C12 15.3137 14.6863 18 18 18C21.3137 18 24 15.3137 24 12C24 8.68629 21.3137 6 18 6C14.6863 6 12 8.68629 12 12Z" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                  <path d="M12 12C12 8.68629 9.31371 6 6 6C2.68629 6 0 8.68629 0 12C0 15.3137 2.68629 18 6 18C9.31371 18 12 15.3137 12 12Z" fill="currentColor" />
                </svg>
                Release Mask
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {guides.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllGuides} title="Clear all guides" className="text-gray-500">
                <Ruler className="w-4 h-4 mr-1" />
                Clear guides ({guides.length})
              </Button>
            )}
            {typeof onCanvasSizeChange === 'function' && (
              <Button variant="ghost" size="sm" onClick={() => setShowResizeDialog(true)} title="Resize canvas">
                <Layout className="w-4 h-4 mr-1" />
                {canvasSize.width}×{canvasSize.height}
              </Button>
            )}
          </div>

          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowDownloadDialog(true)}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>

        {/* ── Brush Library Panel ── */}
        {activeTool === TOOLS.BRUSH && (
          <div
            style={{
              position: 'absolute',
              top: 62,
              left: 8,
              zIndex: 50,
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 14,
              boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
              padding: '10px 8px 8px',
              width: 90,
              userSelect: 'none',
            }}
          >
            <p style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center', marginBottom: 8 }}>
              Brushes
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {BRUSH_PRESETS.map(preset => {
                const isActive = selectedBrushPreset === preset.id;
                // Static S-curve preview path
                const previewD = 'M 4 14 Q 15 5 37 10 Q 50 13 58 7';
                const previewW = Math.max(preset.defaultSize * preset.widthMultiplier * 0.35, 1.2);
                const prevFilterId = `lib-prev-${preset.id}`;
                return (
                  <button
                    key={preset.id}
                    title={`${preset.name} — ${preset.description}`}
                    onClick={() => {
                      setSelectedBrushPreset(preset.id);
                      setBrushSettings(s => ({ ...s, size: preset.defaultSize }));
                    }}
                    style={{
                      width: '100%',
                      padding: '5px 4px 4px',
                      borderRadius: 9,
                      border: isActive ? '2px solid #7c3aed' : '2px solid transparent',
                      background: isActive ? '#f5f3ff' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    {/* Mini SVG stroke preview */}
                    <svg width="66" height="20" viewBox="0 0 66 20" style={{ display: 'block', overflow: 'visible' }}>
                      <defs>
                        {preset.filterType === 'chalk' && (
                          <filter id={prevFilterId} x="-5%" y="-40%" width="110%" height="180%">
                            <feTurbulence type="fractalNoise" baseFrequency="1.0" numOctaves="3" seed="3" result="n" />
                            <feDisplacementMap in="SourceGraphic" in2="n" scale={previewW * 0.9} xChannelSelector="R" yChannelSelector="G" result="d" />
                            <feComposite in="d" in2="SourceGraphic" operator="in" />
                          </filter>
                        )}
                        {preset.filterType === 'spray' && (
                          <filter id={prevFilterId} x="-30%" y="-80%" width="160%" height="260%">
                            <feTurbulence type="turbulence" baseFrequency="0.06" numOctaves="1" seed="5" result="n" />
                            <feDisplacementMap in="SourceGraphic" in2="n" scale={previewW * 2.5} xChannelSelector="R" yChannelSelector="G" />
                          </filter>
                        )}
                        {preset.filterType === 'texture' && (
                          <filter id={prevFilterId} x="-5%" y="-30%" width="110%" height="160%">
                            <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="3" seed="2" result="n" />
                            <feColorMatrix in="n" type="saturate" values="0" result="g" />
                            <feBlend in="SourceGraphic" in2="g" mode="multiply" result="b" />
                            <feComposite in="b" in2="SourceGraphic" operator="in" />
                          </filter>
                        )}
                      </defs>
                      {preset.filterType === 'calligraphy' ? (
                        <g transform="skewX(-18)">
                          <path d={previewD} stroke="#6366f1" strokeWidth={previewW * 0.25}
                            fill="none" strokeLinecap="butt" strokeLinejoin="miter" strokeOpacity="0.9" />
                          <path d={previewD} stroke="#6366f1" strokeWidth={previewW}
                            fill="none" strokeLinecap="butt" strokeLinejoin="miter" strokeOpacity="0.4" />
                        </g>
                      ) : (
                        <path
                          d={previewD}
                          stroke="#6366f1"
                          strokeWidth={previewW}
                          strokeOpacity={preset.opacityMultiplier}
                          fill="none"
                          strokeLinecap={preset.strokeLinecap}
                          strokeLinejoin={preset.strokeLinejoin}
                          filter={preset.filterType ? `url(#${prevFilterId})` : undefined}
                        />
                      )}
                    </svg>
                    <span style={{ fontSize: 9, color: isActive ? '#7c3aed' : '#6b7280', fontWeight: isActive ? 600 : 400 }}>
                      {preset.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Download Dialog */}
        <DownloadDialog
          open={showDownloadDialog}
          onOpenChange={setShowDownloadDialog}
          pages={pages}
          currentPageId={currentPageId}
          canvasSize={canvasSize}
          canvasRef={canvasRef}
          elements={elements}
          canvasBackground={canvasBackground}
          onSwitchPage={onSwitchPage}
          templateId={downloadTemplateId}
          sessionType={downloadSessionType}
          onQuotaExceeded={onDownloadQuotaExceeded}
          onDownloadsDisabled={onDownloadDisabled}
        />

        {/* Resize Canvas Dialog */}
        <Dialog open={showResizeDialog} onOpenChange={setShowResizeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Resize Canvas</DialogTitle>
              <DialogDescription>
                Enter any dimensions (1–10 000 px). Content is not scaled; elements outside the new bounds will be clipped.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-4">

              {/* Width / Height inputs + lock toggle */}
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="resize-width" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Width (px)</Label>
                  <Input
                    id="resize-width"
                    type="number"
                    min={MIN_CANVAS}
                    max={MAX_CANVAS}
                    step={1}
                    value={resizeWidth}
                    onChange={(e) => {
                      const val = e.target.value;
                      setResizeWidth(val);
                      if (lockAspectRatio) {
                        const w = Number(val);
                        const h = Number(resizeHeight);
                        const ratio = h / Number(resizeWidth || 1);
                        if (w > 0 && isFinite(ratio)) setResizeHeight(String(Math.round(w * ratio)));
                      }
                    }}
                    className="h-10 text-sm font-mono"
                    placeholder="e.g. 1080"
                  />
                </div>

                {/* Lock aspect ratio */}
                <button
                  type="button"
                  onClick={() => setLockAspectRatio(l => !l)}
                  title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                  className={`mb-0.5 w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 transition-all ${
                    lockAspectRatio
                      ? 'border-purple-400 bg-purple-50 text-purple-600'
                      : 'border-gray-200 bg-white text-gray-400 hover:border-purple-300 hover:text-purple-500'
                  }`}
                >
                  {lockAspectRatio ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                </button>

                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="resize-height" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Height (px)</Label>
                  <Input
                    id="resize-height"
                    type="number"
                    min={MIN_CANVAS}
                    max={MAX_CANVAS}
                    step={1}
                    value={resizeHeight}
                    onChange={(e) => {
                      const val = e.target.value;
                      setResizeHeight(val);
                      if (lockAspectRatio) {
                        const h = Number(val);
                        const ratio = Number(resizeWidth) / Number(resizeHeight || 1);
                        if (h > 0 && isFinite(ratio)) setResizeWidth(String(Math.round(h * ratio)));
                      }
                    }}
                    className="h-10 text-sm font-mono"
                    placeholder="e.g. 1920"
                  />
                </div>
              </div>

              {/* Live size preview */}
              <p className="text-xs text-center text-gray-400 tabular-nums -mt-1">
                {Number(resizeWidth) > 0 && Number(resizeHeight) > 0
                  ? `${Math.round(Number(resizeWidth))} × ${Math.round(Number(resizeHeight))} px`
                  : 'Enter valid dimensions'}
              </p>

              {/* Popular sizes */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Popular sizes</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Square',        w: 1080, h: 1080 },
                    { label: 'HD 16:9',       w: 1920, h: 1080 },
                    { label: 'Story 9:16',    w: 1080, h: 1920 },
                    { label: 'Facebook Post', w: 1200, h:  630 },
                    { label: 'A4 Portrait',   w: 2480, h: 3508 },
                    { label: 'Twitter Post',  w: 1600, h:  900 },
                  ].map(({ label, w, h }) => (
                    <Button
                      key={label}
                      variant="outline"
                      size="sm"
                      onClick={() => { setResizeWidth(String(w)); setResizeHeight(String(h)); }}
                      className="justify-start text-xs h-8 font-normal"
                    >
                      <span className="font-semibold mr-1.5">{label}</span>
                      <span className="text-gray-400 tabular-nums">{w}×{h}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResizeDialog(false)}>
                Cancel
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                disabled={!(Number(resizeWidth) >= MIN_CANVAS && Number(resizeHeight) >= MIN_CANVAS)}
                onClick={() => {
                  const w = Math.max(MIN_CANVAS, Math.min(MAX_CANVAS, Math.round(Number(resizeWidth) || canvasSize.width)));
                  const h = Math.max(MIN_CANVAS, Math.min(MAX_CANVAS, Math.round(Number(resizeHeight) || canvasSize.height)));
                  onCanvasSizeChange?.({ width: w, height: h });
                  setShowResizeDialog(false);
                }}
              >
                Resize
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Canvas Area — Canva-style vertical multi-page stack */}
        {(() => {
          const PAGE_GAP = 60;
          const zoomedW = Math.round(canvasSize.width * zoom);
          const zoomedH = Math.round(canvasSize.height * zoom);
          const activePageIndex = pages.findIndex(p => p.id === currentPageId);
          const isHiddenPage = (pages[activePageIndex] || {}).hidden ?? false;

          return (
            <div
              ref={canvasAreaRef}
              className="flex-1 overflow-auto bg-gray-100"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 0 }}
            >
              <div
                style={{
                  padding: '24px',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: RULER_SIZE + zoomedW + 48,
                  boxSizing: 'border-box',
                }}
              >
                {pages.map((page, pageIndex) => {
                  const isActive = page.id === currentPageId;

                  if (!isActive) {
                    /* ── Inactive page: read-only preview, click to switch ── */
                    return (
                      <div
                        key={page.id}
                        ref={el => { pageRefs.current[page.id] = el; }}
                        style={{ marginBottom: pageIndex < pages.length - 1 ? PAGE_GAP : 0, display: 'flex', flexDirection: 'column', flexShrink: 0 }}
                      >
                        {/* Page label */}
                        <div style={{ marginLeft: RULER_SIZE, marginBottom: 4, fontSize: 11, color: '#9ca3af', userSelect: 'none', fontWeight: 500 }}>
                          Page {pageIndex + 1}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                          {/* Ruler-width spacer so canvas aligns with active page */}
                          <div style={{ width: RULER_SIZE, flexShrink: 0 }} />
                          {/* Canvas preview — uses PageThumbnailPreview scaled to current zoom */}
                          <div
                            id={`page-canvas-${page.id}`}
                            style={{
                              width: zoomedW,
                              height: zoomedH,
                              position: 'relative',
                              overflow: 'hidden',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                              borderRadius: 2,
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          >
                            <PageThumbnailPreview
                              page={page}
                              canvasSize={canvasSize}
                              thumbWidth={zoomedW}
                            />
                            {/* Overlay: intercepts all pointer events, click switches to this page */}
                            <div
                              style={{ position: 'absolute', inset: 0, zIndex: 10 }}
                              onClick={() => onSwitchPage && onSwitchPage(page.id)}
                            />
                            {/* Hidden page overlay */}
                            {page.hidden && (
                              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                <EyeOff style={{ width: 24, height: 24, color: 'white' }} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  /* ── Active page: full rulers + interactive canvas ── */
                  return (
                    <div
                      key={page.id}
                      ref={el => { pageRefs.current[page.id] = el; }}
                      style={{ marginBottom: pageIndex < pages.length - 1 ? PAGE_GAP : 0, display: 'flex', flexDirection: 'column', flexShrink: 0 }}
                    >
                  <div style={{ marginLeft: RULER_SIZE, marginBottom: 4, fontSize: 11, color: '#7c3aed', userSelect: 'none', fontWeight: 600 }}>
                    Page {activePageIndex + 1}
                  </div>
                  {/* Rulers + canvas block */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexShrink: 0,
                    }}
                  >
                    {/* Top row: corner + horizontal ruler */}
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <div
                        style={{
                          width: RULER_SIZE,
                          height: RULER_SIZE,
                          flexShrink: 0,
                          background: '#e5e7eb',
                          borderRight: '1px solid #d1d5db',
                          borderBottom: '1px solid #d1d5db',
                        }}
                      />
                      <div
                        ref={rulerTopRef}
                        role="presentation"
                        style={{
                          width: zoomedW,
                          height: RULER_SIZE,
                          flexShrink: 0,
                          background: '#e5e7eb',
                          borderBottom: '1px solid #d1d5db',
                          cursor: 'crosshair',
                          position: 'relative',
                        }}
                        onMouseDown={(e) => handleRulerMouseDown(e, 'vertical')}
                      >
                        {/* Tick marks every 50px in design space */}
                        {hTicks50.map((px) => (
                          <div
                            key={px}
                            style={{
                              position: 'absolute',
                              left: px * zoom,
                              top: 0,
                              width: 1,
                              height: px % 100 === 0 ? 10 : 6,
                              background: '#6b7280',
                            }}
                          />
                        ))}
                        {hTicks100.map((px) => (
                          <span
                            key={`l-${px}`}
                            style={{
                              position: 'absolute',
                              left: px * zoom + 2,
                              top: 2,
                              fontSize: 9,
                              color: '#4b5563',
                              pointerEvents: 'none',
                            }}
                          >
                            {px}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Bottom row: vertical ruler + canvas */}
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <div
                        ref={rulerLeftRef}
                        role="presentation"
                        style={{
                          width: RULER_SIZE,
                          height: zoomedH,
                          flexShrink: 0,
                          background: '#e5e7eb',
                          borderRight: '1px solid #d1d5db',
                          cursor: 'crosshair',
                          position: 'relative',
                        }}
                        onMouseDown={(e) => handleRulerMouseDown(e, 'horizontal')}
                      >
                        {vTicks50.map((py) => (
                          <div
                            key={py}
                            style={{
                              position: 'absolute',
                              top: py * zoom,
                              left: 0,
                              height: 1,
                              width: py % 100 === 0 ? 10 : 6,
                              background: '#6b7280',
                            }}
                          />
                        ))}
                        {vTicks100.map((py) => (
                          <span
                            key={`l-${py}`}
                            style={{
                              position: 'absolute',
                              top: py * zoom + 2,
                              left: 2,
                              fontSize: 9,
                              color: '#4b5563',
                              pointerEvents: 'none',
                            }}
                          >
                            {py}
                          </span>
                        ))}
                      </div>
                      {/* Canvas wrapper - fixed pixel size to avoid sub-pixel/blank at high zoom */}
                      <div
                        style={{
                          width: zoomedW,
                          height: zoomedH,
                          minWidth: zoomedW,
                          minHeight: zoomedH,
                          flexShrink: 0,
                          overflow: 'visible',
                          position: 'relative',
                        }}
                      >
                        {/* Active page highlight ring */}
                        <div style={{ position: 'absolute', inset: -3, border: '3px solid #7c3aed', borderRadius: 3, pointerEvents: 'none', zIndex: 50 }} />
                        {/* Scaled canvas layer - GPU compositing for stable rendering at high zoom */}
                        <div
                          ref={canvasRef}
                          id={`page-canvas-${page.id}`}
                          className="shadow-xl canvas-scaled-layer"
                          style={{
                            width: canvasSize.width,
                            height: canvasSize.height,
                            // GPU promotion (translateZ/will-change) is gated on
                            // canvasFontsReady — promoting before the page's
                            // webfonts load caches the layer with fallback
                            // metrics and never re-rasterizes, leaving duplicated
                            // pages' text shifted down until an interaction.
                            transform: canvasFontsReady ? `translateZ(0) scale(${zoom})` : `scale(${zoom})`,
                            transformOrigin: 'top left',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            background: canvasBackground,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            overflow: 'hidden',
                            willChange: canvasFontsReady ? 'transform' : 'auto',
                            backfaceVisibility: canvasFontsReady ? 'hidden' : undefined,
                          }}
                          onClick={handleCanvasClick}
                          onDoubleClick={handleCanvasDoubleClick}
                          onMouseDown={handleCanvasMouseDown}
                          onMouseMove={handleCanvasMouseMove}
                          onMouseUp={handleCanvasMouseUp}
                          onMouseLeave={() => { if (activeTool === TOOLS.MASK) setMaskHoverElementId(null); }}
                        >
                          {/* ── Duotone filter defs (referenced by filter:url) ── */}
                          {elements.map((el) => {
                            const t = duotoneTableValues(el);
                            if (!t) return null;
                            return (
                              <svg key={t.id} width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                                <filter id={t.id} colorInterpolationFilters="sRGB">
                                  <feColorMatrix type="matrix" values="0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0 0 0 1 0" />
                                  <feComponentTransfer>
                                    <feFuncR type="table" tableValues={t.r} />
                                    <feFuncG type="table" tableValues={t.g} />
                                    <feFuncB type="table" tableValues={t.b} />
                                  </feComponentTransfer>
                                </filter>
                              </svg>
                            );
                          })}
                          {/* ── Smart-alignment guides (Canva/Figma purple) ── */}
                          {(smartGuides.v.length > 0 || smartGuides.h.length > 0) && (
                            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
                              {smartGuides.v.map((vx, i) => (
                                <div key={`sgv-${i}`} style={{ position: 'absolute', left: vx, top: 0, width: Math.max(1, 1 / zoom), height: canvasSize.height, background: '#a855f7' }} />
                              ))}
                              {smartGuides.h.map((hy, i) => (
                                <div key={`sgh-${i}`} style={{ position: 'absolute', top: hy, left: 0, height: Math.max(1, 1 / zoom), width: canvasSize.width, background: '#a855f7' }} />
                              ))}
                            </div>
                          )}
                          {/* ── Background image layer ── */}
                          {backgroundConfig?.type === 'image' && backgroundConfig?.url && (
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 0,
                                pointerEvents: 'none',
                                // CSS fallback: if the image URL is broken/expired, this gray shows through
                                // instead of the white canvas, making it clear a background was intended.
                                backgroundColor: '#f3f4f6',
                                backgroundImage: `url(${backgroundConfig.url})`,
                                backgroundSize: backgroundConfig.size || 'cover',
                                backgroundPosition: backgroundConfig.position || 'center center',
                                backgroundRepeat: 'no-repeat',
                                opacity: backgroundConfig.opacity ?? 1,
                                filter: backgroundConfig.blur > 0 ? `blur(${backgroundConfig.blur}px)` : 'none',
                                // Bleed slightly beyond edges to avoid white halos when blur is active
                                margin: backgroundConfig.blur > 0 ? `-${backgroundConfig.blur * 2}px` : 0,
                                width: backgroundConfig.blur > 0 ? `calc(100% + ${backgroundConfig.blur * 4}px)` : '100%',
                                height: backgroundConfig.blur > 0 ? `calc(100% + ${backgroundConfig.blur * 4}px)` : '100%',
                              }}
                            />
                          )}
                          {elements.map(renderElement)}

                          {/* ── Group selection indicator + resize handles ──
                              Dashed outline at the group's computed bbox (via getGroupBounds —
                              same source as the resize math, so outline and handles never drift).
                              Eight resize handles around the bbox; their onMouseDown reuses the
                              existing handleResizeMouseDown / isResizing / resizeHandle state
                              machine so the resize-move handler in handleMouseMove sees identical
                              control flow as for single elements. Rotate handle lands in Task 10. */}
                          {(() => {
                            if (selectedElement?.type !== 'group') return null;
                            const groupBounds = getGroupBounds(elements, selectedElement.id);
                            if (!groupBounds) return null;
                            const handlePositions = {
                              nw: { top: -4, left: -4, cursor: 'nwse-resize' },
                              n:  { top: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
                              ne: { top: -4, right: -4, cursor: 'nesw-resize' },
                              e:  { top: '50%', right: -4, transform: 'translateY(-50%)', cursor: 'ew-resize' },
                              se: { bottom: -4, right: -4, cursor: 'nwse-resize' },
                              s:  { bottom: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
                              sw: { bottom: -4, left: -4, cursor: 'nesw-resize' },
                              w:  { top: '50%', left: -4, transform: 'translateY(-50%)', cursor: 'ew-resize' },
                            };
                            return (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: groupBounds.x,
                                  top: groupBounds.y,
                                  width: groupBounds.width,
                                  height: groupBounds.height,
                                  pointerEvents: 'none', // wrapper passes through; handles re-enable below
                                  zIndex: 9998,
                                }}
                              >
                                {/* Dashed outline (decorative; no events) */}
                                <div
                                  style={{
                                    position: 'absolute',
                                    inset: 0,
                                    border: '2px dashed #6366f1',
                                    boxSizing: 'border-box',
                                    pointerEvents: 'none',
                                  }}
                                />
                                {/* Resize handles (only when unlocked) */}
                                {!selectedElement.locked && ['nw','n','ne','e','se','s','sw','w'].map((handle) => (
                                  <div
                                    key={handle}
                                    onMouseDown={(e) => handleResizeMouseDown(e, handle)}
                                    style={{
                                      position: 'absolute',
                                      width: 8,
                                      height: 8,
                                      backgroundColor: '#6366f1',
                                      border: '2px solid white',
                                      borderRadius: '50%',
                                      pointerEvents: 'auto',
                                      ...handlePositions[handle],
                                      zIndex: 10,
                                    }}
                                  />
                                ))}
                                {/* Rotate handle (only when unlocked) — same SVG icon as
                                    renderResizeHandles' rotate handle so groups and leaves
                                    share visual language. Sits above the top edge. */}
                                {!selectedElement.locked && (
                                  <div
                                    onMouseDown={handleRotateMouseDown}
                                    style={{
                                      position: 'absolute',
                                      top: -30,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: 20,
                                      height: 20,
                                      cursor: 'grab',
                                      pointerEvents: 'auto',
                                      zIndex: 10,
                                    }}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* ── Isolation-mode outline ──
                              Shows the bounds of the group currently being edited (editingGroupId).
                              Renders as a dashed indigo rectangle with a 4px inset air-gap so it
                              doesn't visually conflict with the inner group-selection outline.
                              Read-only visual; pointer-events:none so it doesn't intercept clicks
                              on descendants. Bounds source = getGroupBounds(elements, editingGroupId)
                              — shared with every other group geometry consumer.
                              Distinct visual treatment from the group-SELECTION outline:
                              same colour but wider air-gap + slightly thinner border so isolated
                              context reads as "frame" rather than "selection". */}
                          {editingGroupId && (() => {
                            const bounds = getGroupBounds(elements, editingGroupId);
                            if (!bounds) return null;
                            return (
                              <div
                                data-editing-group-outline="1"
                                style={{
                                  position: 'absolute',
                                  left: bounds.x - 4,
                                  top: bounds.y - 4,
                                  width: bounds.width + 8,
                                  height: bounds.height + 8,
                                  border: '1px dashed #6366f1',
                                  pointerEvents: 'none',
                                  boxSizing: 'border-box',
                                  zIndex: 9996,
                                }}
                              />
                            );
                          })()}

                          {/* ── Marquee drag-to-select overlay ──
                              Lives inside the canvas scale transform so the box scales with zoom.
                              Solid 1px indigo border with a translucent fill so the user can
                              clearly see which area they're selecting. pointer-events: none so
                              the overlay never intercepts the mousemove that drives it. */}
                          {marqueeBox && (
                            <div
                              style={{
                                position: 'absolute',
                                left: marqueeBox.x,
                                top: marqueeBox.y,
                                width: marqueeBox.w,
                                height: marqueeBox.h,
                                border: '1px solid #6366f1',
                                background: 'rgba(99, 102, 241, 0.08)',
                                pointerEvents: 'none',
                                boxSizing: 'border-box',
                                zIndex: 9997,
                              }}
                            />
                          )}

                          {/* ── Pen Tool: input overlay ── */}
                          {activeTool === TOOLS.PEN && (
                            <div
                              style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 9999, cursor: 'crosshair',
                              }}
                              onMouseDown={handlePenMouseDown}
                              onMouseMove={handlePenMouseMove}
                              onMouseUp={handlePenMouseUp}
                              onDoubleClick={handlePenDoubleClick}
                            />
                          )}

                          {/* ── Pen Tool: live preview SVG ── */}
                          {activeTool === TOOLS.PEN && (penPoints.length > 0 || penDragAnchor) && (
                            <svg
                              style={{
                                position: 'absolute', top: 0, left: 0,
                                width: canvasSize.width, height: canvasSize.height,
                                overflow: 'visible', pointerEvents: 'none', zIndex: 10000,
                              }}
                            >
                              {/* Path drawn so far */}
                              {penPoints.length >= 2 && (
                                <path
                                  d={buildPreviewPath(penPoints)}
                                  stroke="#6366f1"
                                  strokeWidth={1.5 / zoom}
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              )}

                              {/* Preview line / curve from last point to cursor */}
                              {penPoints.length > 0 && penCursorPos && (() => {
                                const last = penPoints[penPoints.length - 1];
                                const cp1 = last.handleOut || last;
                                return (
                                  <path
                                    d={`M ${last.x} ${last.y} C ${cp1.x} ${cp1.y}, ${penCursorPos.x} ${penCursorPos.y}, ${penCursorPos.x} ${penCursorPos.y}`}
                                    stroke="#6366f1"
                                    strokeWidth={1 / zoom}
                                    strokeDasharray={`${5 / zoom} ${3 / zoom}`}
                                    fill="none"
                                  />
                                );
                              })()}

                              {/* Current drag-point bezier curve preview */}
                              {penDragAnchor && penIsDragging && penHandlePreview && penPoints.length > 0 && (() => {
                                const last = penPoints[penPoints.length - 1];
                                const cp1 = last.handleOut || last;
                                return (
                                  <path
                                    d={`M ${last.x} ${last.y} C ${cp1.x} ${cp1.y}, ${penHandlePreview.handleIn.x} ${penHandlePreview.handleIn.y}, ${penDragAnchor.x} ${penDragAnchor.y}`}
                                    stroke="#6366f1"
                                    strokeWidth={1.5 / zoom}
                                    fill="none"
                                    strokeLinecap="round"
                                  />
                                );
                              })()}

                              {/* Placed anchor points */}
                              {penPoints.map((point, i) => (
                                <g key={i}>
                                  {/* Handle lines */}
                                  {point.handleOut && (
                                    <>
                                      <line x1={point.x} y1={point.y} x2={point.handleOut.x} y2={point.handleOut.y}
                                        stroke="#6366f1" strokeWidth={1 / zoom} />
                                      <circle cx={point.handleOut.x} cy={point.handleOut.y} r={3 / zoom}
                                        fill="#6366f1" />
                                    </>
                                  )}
                                  {point.handleIn && (
                                    <>
                                      <line x1={point.x} y1={point.y} x2={point.handleIn.x} y2={point.handleIn.y}
                                        stroke="#6366f1" strokeWidth={1 / zoom} />
                                      <circle cx={point.handleIn.x} cy={point.handleIn.y} r={3 / zoom}
                                        fill="#6366f1" />
                                    </>
                                  )}
                                  {/* Anchor circle */}
                                  <circle cx={point.x} cy={point.y} r={4 / zoom}
                                    fill="white" stroke="#6366f1" strokeWidth={1.5 / zoom} />
                                </g>
                              ))}

                              {/* Current drag-point in progress */}
                              {penDragAnchor && (
                                <g>
                                  {penIsDragging && penHandlePreview && (
                                    <>
                                      <line x1={penDragAnchor.x} y1={penDragAnchor.y}
                                        x2={penHandlePreview.handleOut.x} y2={penHandlePreview.handleOut.y}
                                        stroke="#6366f1" strokeWidth={1 / zoom} />
                                      <line x1={penDragAnchor.x} y1={penDragAnchor.y}
                                        x2={penHandlePreview.handleIn.x} y2={penHandlePreview.handleIn.y}
                                        stroke="#6366f1" strokeWidth={1 / zoom} />
                                      <circle cx={penHandlePreview.handleOut.x} cy={penHandlePreview.handleOut.y}
                                        r={3 / zoom} fill="#6366f1" />
                                      <circle cx={penHandlePreview.handleIn.x} cy={penHandlePreview.handleIn.y}
                                        r={3 / zoom} fill="#6366f1" />
                                    </>
                                  )}
                                  <circle cx={penDragAnchor.x} cy={penDragAnchor.y} r={4 / zoom}
                                    fill="white" stroke="#6366f1" strokeWidth={1.5 / zoom} />
                                </g>
                              )}

                              {/* Green close-path indicator on first anchor */}
                              {penPoints.length > 1 && penCursorPos && (() => {
                                const first = penPoints[0];
                                const screenDist = Math.hypot(
                                  (penCursorPos.x - first.x) * zoom,
                                  (penCursorPos.y - first.y) * zoom,
                                );
                                if (screenDist < 12) {
                                  return (
                                    <circle cx={first.x} cy={first.y} r={7 / zoom}
                                      fill="none" stroke="#22c55e" strokeWidth={2 / zoom} />
                                  );
                                }
                                return null;
                              })()}
                            </svg>
                          )}

                          {/* ── Eraser Tool: capture overlay ── */}
                          {activeTool === TOOLS.ERASER && (
                            <div
                              style={{
                                position: 'absolute', top: 0, left: 0,
                                width: canvasSize.width, height: canvasSize.height,
                                cursor: 'none', zIndex: 9999,
                              }}
                              onMouseDown={handleEraserMouseDown}
                              onMouseMove={handleEraserMouseMove}
                              onMouseUp={handleEraserMouseUp}
                              onMouseLeave={handleEraserMouseLeave}
                            />
                          )}

                          {/* ── Eraser Tool: cursor circle SVG ── */}
                          {activeTool === TOOLS.ERASER && eraserCursorPos && (
                            <svg
                              style={{
                                position: 'absolute', top: 0, left: 0,
                                width: canvasSize.width, height: canvasSize.height,
                                overflow: 'visible', pointerEvents: 'none', zIndex: 10001,
                              }}
                            >
                              <circle
                                cx={eraserCursorPos.x}
                                cy={eraserCursorPos.y}
                                r={eraserSettings.size / 2}
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth={1 / zoom}
                                strokeOpacity={0.8}
                                strokeDasharray={`${3 / zoom} ${2 / zoom}`}
                              />
                              {/* Crosshair dot */}
                              <circle
                                cx={eraserCursorPos.x}
                                cy={eraserCursorPos.y}
                                r={1.5 / zoom}
                                fill="#ef4444"
                                fillOpacity={0.8}
                              />
                            </svg>
                          )}

                          {/* ── Clipping Mask Tool: selection + hover highlights ── */}
                          {activeTool === TOOLS.MASK && (
                            <svg
                              style={{
                                position: 'absolute', top: 0, left: 0,
                                width: canvasSize.width, height: canvasSize.height,
                                overflow: 'visible', pointerEvents: 'none', zIndex: 10000,
                              }}
                            >
                              {/* Blue dashed outline for each selected element */}
                              {(selectedElements || []).map(el => (
                                <rect
                                  key={`mask-sel-${el.id}`}
                                  x={el.x - 3 / zoom}
                                  y={el.y - 3 / zoom}
                                  width={el.width + 6 / zoom}
                                  height={el.height + 6 / zoom}
                                  fill="none"
                                  stroke="#6366f1"
                                  strokeWidth={2 / zoom}
                                  strokeDasharray={`${6 / zoom} ${3 / zoom}`}
                                  rx={3 / zoom}
                                />
                              ))}
                              {/* Green outline for hovered element (not already selected) */}
                              {maskHoverElementId && !(selectedElements || []).some(el => el.id === maskHoverElementId) && (() => {
                                const hovered = elementsRef.current.find(el => el.id === maskHoverElementId);
                                if (!hovered) return null;
                                return (
                                  <rect
                                    x={hovered.x - 3 / zoom}
                                    y={hovered.y - 3 / zoom}
                                    width={hovered.width + 6 / zoom}
                                    height={hovered.height + 6 / zoom}
                                    fill="rgba(34,197,94,0.08)"
                                    stroke="#22c55e"
                                    strokeWidth={1.5 / zoom}
                                    rx={3 / zoom}
                                  />
                                );
                              })()}
                              {/* Labels: "Top (mask)" on highest-z selected, "Content" on others */}
                              {(selectedElements || []).length >= 2 && (() => {
                                const sorted = [...selectedElements].sort((a, b) => {
                                  const ia = elementsRef.current.findIndex(el => el.id === a.id);
                                  const ib = elementsRef.current.findIndex(el => el.id === b.id);
                                  return ib - ia;
                                });
                                return sorted.map((el, i) => (
                                  <text
                                    key={`mask-label-${el.id}`}
                                    x={el.x + 4 / zoom}
                                    y={el.y - 6 / zoom}
                                    fontSize={10 / zoom}
                                    fill={i === 0 ? '#6366f1' : '#64748b'}
                                    fontFamily="sans-serif"
                                    fontWeight="600"
                                  >
                                    {i === 0 ? 'Mask shape' : 'Content'}
                                  </text>
                                ));
                              })()}
                            </svg>
                          )}

                          {/* ── Brush Tool: capture overlay ── */}
                          {activeTool === TOOLS.BRUSH && (
                            <div
                              style={{
                                position: 'absolute', top: 0, left: 0,
                                width: canvasSize.width, height: canvasSize.height,
                                cursor: 'none', zIndex: 9999,
                              }}
                              onMouseDown={handleBrushMouseDown}
                              onMouseMove={handleBrushMouseMove}
                              onMouseUp={handleBrushMouseUp}
                              onMouseLeave={() => setBrushCursorPos(null)}
                            />
                          )}

                          {/* ── Brush Tool: live stroke preview + cursor SVG ── */}
                          {activeTool === TOOLS.BRUSH && (() => {
                            const livePreset = BRUSH_PRESETS.find(p => p.id === selectedBrushPreset) || BRUSH_PRESETS[0];
                            const liveStrokeW = brushSettings.size * livePreset.widthMultiplier;
                            const liveOpacity = brushSettings.opacity * livePreset.opacityMultiplier;
                            const liveFilterId = 'brush-live-filter';
                            return (
                              <svg
                                style={{
                                  position: 'absolute', top: 0, left: 0,
                                  width: canvasSize.width, height: canvasSize.height,
                                  overflow: 'visible', pointerEvents: 'none', zIndex: 10001,
                                }}
                              >
                                <defs>
                                  {livePreset.filterType === 'chalk' && (
                                    <filter id={liveFilterId} x="-15%" y="-40%" width="130%" height="180%">
                                      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" seed="3" result="noise" />
                                      <feDisplacementMap in="SourceGraphic" in2="noise" scale={liveStrokeW * 0.7} xChannelSelector="R" yChannelSelector="G" result="displaced" />
                                      <feComposite in="displaced" in2="SourceGraphic" operator="in" />
                                    </filter>
                                  )}
                                  {livePreset.filterType === 'spray' && (
                                    <filter id={liveFilterId} x="-60%" y="-120%" width="220%" height="340%">
                                      <feTurbulence type="turbulence" baseFrequency="0.025" numOctaves="1" seed="7" result="noise" />
                                      <feDisplacementMap in="SourceGraphic" in2="noise" scale={liveStrokeW * 1.8} xChannelSelector="R" yChannelSelector="G" />
                                    </filter>
                                  )}
                                  {livePreset.filterType === 'texture' && (
                                    <filter id={liveFilterId} x="-5%" y="-20%" width="110%" height="140%">
                                      <feTurbulence type="fractalNoise" baseFrequency="0.4" numOctaves="4" seed="2" result="noise" />
                                      <feColorMatrix in="noise" type="saturate" values="0" result="greyNoise" />
                                      <feBlend in="SourceGraphic" in2="greyNoise" mode="multiply" result="blended" />
                                      <feComposite in="blended" in2="SourceGraphic" operator="in" />
                                    </filter>
                                  )}
                                </defs>

                                {/* In-progress stroke preview matching selected preset */}
                                {brushPoints.length >= 2 && (
                                  livePreset.filterType === 'calligraphy' ? (
                                    <g transform="skewX(-18)">
                                      <path d={buildSmoothPath(brushPoints)} stroke={brushSettings.color}
                                        strokeWidth={liveStrokeW * 0.2} strokeOpacity={liveOpacity}
                                        fill="none" strokeLinecap="butt" strokeLinejoin="miter" />
                                      <path d={buildSmoothPath(brushPoints)} stroke={brushSettings.color}
                                        strokeWidth={liveStrokeW} strokeOpacity={liveOpacity * 0.45}
                                        fill="none" strokeLinecap="butt" strokeLinejoin="miter" />
                                    </g>
                                  ) : (
                                    <path
                                      d={buildSmoothPath(brushPoints)}
                                      stroke={brushSettings.color}
                                      strokeWidth={liveStrokeW}
                                      strokeOpacity={liveOpacity}
                                      fill="none"
                                      strokeLinecap={livePreset.strokeLinecap}
                                      strokeLinejoin={livePreset.strokeLinejoin}
                                      filter={livePreset.filterType ? `url(#${liveFilterId})` : undefined}
                                    />
                                  )
                                )}

                                {/* Circular cursor — shows actual rendered brush radius */}
                                {brushCursorPos && (
                                  <circle
                                    cx={brushCursorPos.x}
                                    cy={brushCursorPos.y}
                                    r={Math.max(liveStrokeW / 2, 2)}
                                    fill="none"
                                    stroke={brushSettings.color}
                                    strokeWidth={1 / zoom}
                                    strokeOpacity={0.6}
                                  />
                                )}
                              </svg>
                            );
                          })()}

                          {isHiddenPage && (
                            <div
                              style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                background: 'rgba(0,0,0,0.4)', zIndex: 9999,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                pointerEvents: 'none'
                              }}
                            >
                              <div style={{ background: 'rgba(0,0,0,0.6)', color: 'white', padding: '12px 24px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <EyeOff style={{ width: 24, height: 24, color: 'white' }} />
                                <span style={{ fontSize: 16, fontWeight: 500, color: 'white' }}>Page Hidden</span>
                              </div>
                            </div>
                          )}

                          {/* Ruler guides - design coordinates, same scale as canvas */}
                          {[...guides, ...(newGuideDrag ? [{ id: 'preview', type: newGuideDrag.type, position: newGuideDrag.position }] : [])].map((g) => (
                            g.type === 'horizontal' ? (
                              <div
                                key={g.id}
                                role="presentation"
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  top: g.position - GUIDE_HIT_SIZE / 2,
                                  width: canvasSize.width,
                                  height: GUIDE_HIT_SIZE,
                                  cursor: 'ns-resize',
                                  zIndex: 9998,
                                }}
                                onMouseDown={(e) => g.id !== 'preview' && handleGuideMouseDown(e, g)}
                                onDoubleClick={(e) => g.id !== 'preview' && handleGuideDoubleClick(e, g.id)}
                              >
                                <div style={{ position: 'absolute', left: 0, top: GUIDE_HIT_SIZE / 2 - 1, width: '100%', height: 2, background: GUIDE_COLOR, pointerEvents: 'none' }} />
                              </div>
                            ) : (
                              <div
                                key={g.id}
                                role="presentation"
                                style={{
                                  position: 'absolute',
                                  left: g.position - GUIDE_HIT_SIZE / 2,
                                  top: 0,
                                  width: GUIDE_HIT_SIZE,
                                  height: canvasSize.height,
                                  cursor: 'ew-resize',
                                  zIndex: 9998,
                                }}
                                onMouseDown={(e) => g.id !== 'preview' && handleGuideMouseDown(e, g)}
                                onDoubleClick={(e) => g.id !== 'preview' && handleGuideDoubleClick(e, g.id)}
                              >
                                <div style={{ position: 'absolute', left: GUIDE_HIT_SIZE / 2 - 1, top: 0, width: 2, height: '100%', background: GUIDE_COLOR, pointerEvents: 'none' }} />
                              </div>
                            )
                          ))}                        </div>

                        {/* Page control buttons — floats on the right side of the canvas */}
                        {(() => {
                          const currentPageIndex = pages.findIndex(p => p.id === currentPageId);
                          const activePage = pages[currentPageIndex] || {};
                          const isLocked = activePage.locked ?? false;
                          const isHidden = isHiddenPage;

                          const handleMoveUp = () => {
                            if (currentPageIndex > 0 && onReorderPages) {
                              const nextPages = [...pages];
                              [nextPages[currentPageIndex - 1], nextPages[currentPageIndex]] = [nextPages[currentPageIndex], nextPages[currentPageIndex - 1]];
                              onReorderPages(nextPages);
                            }
                          };

                          const handleMoveDown = () => {
                            if (currentPageIndex < pages.length - 1 && onReorderPages) {
                              const nextPages = [...pages];
                              [nextPages[currentPageIndex + 1], nextPages[currentPageIndex]] = [nextPages[currentPageIndex], nextPages[currentPageIndex + 1]];
                              onReorderPages(nextPages);
                            }
                          };

                          const handleDelete = () => {
                            if (pages.length > 1 && onDeletePage) {
                              onDeletePage(currentPageId);
                            }
                          };

                          const handleToggleHide = () => {
                            if (onHidePage) {
                              onHidePage(currentPageId, !isHidden);
                            }
                          };

                          const btnStyle = {
                            height: 28, width: 28,
                            background: 'rgba(255,255,255,0.92)',
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: 6,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                            transition: 'background 0.2s'
                          };
                          const disabledBtnStyle = { ...btnStyle, opacity: 0.5, cursor: 'not-allowed' };

                          return (
                            <div
                              style={{
                                position: 'absolute',
                                top: 16,
                                right: -44, // roughly 16px spacing from edge + 28px button width
                                zIndex: 50,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                pointerEvents: 'all',
                              }}
                            >
                              {onLockPage && (
                                <button
                                  onClick={() => onLockPage(currentPageId, !isLocked)}
                                  title={isLocked ? "Unlock page" : "Lock page"}
                                  style={btnStyle}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                                >
                                  {isLocked ? (
                                    <Lock style={{ width: 13, height: 13, color: '#374151' }} />
                                  ) : (
                                    <Unlock style={{ width: 13, height: 13, color: '#374151' }} />
                                  )}
                                </button>
                              )}

                              <button
                                onClick={onDuplicatePage}
                                title="Duplicate page"
                                style={btnStyle}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                              >
                                <Copy style={{ width: 13, height: 13, color: '#374151' }} />
                              </button>

                              <button
                                onClick={onAddPage}
                                title="Add page below"
                                style={btnStyle}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                              >
                                <Plus style={{ width: 16, height: 16, color: '#374151' }} />
                              </button>

                              <button
                                onClick={handleDelete}
                                title={pages.length > 1 ? "Delete page" : "Cannot delete last page"}
                                style={pages.length > 1 ? btnStyle : disabledBtnStyle}
                                onMouseEnter={e => { if (pages.length > 1) e.currentTarget.style.background = '#fee2e2'; }}
                                onMouseLeave={e => { if (pages.length > 1) e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                                disabled={pages.length <= 1}
                              >
                                <Trash2 style={{ width: 13, height: 13, color: pages.length > 1 ? '#ef4444' : '#9ca3af' }} />
                              </button>

                              <button
                                onClick={handleToggleHide}
                                title={isHidden ? "Unhide page" : "Hide page"}
                                style={btnStyle}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                              >
                                {isHidden ? (
                                  <EyeOff style={{ width: 13, height: 13, color: '#374151' }} />
                                ) : (
                                  <Eye style={{ width: 13, height: 13, color: '#374151' }} />
                                )}
                              </button>

                              <button
                                onClick={handleMoveUp}
                                title="Move page up"
                                style={currentPageIndex > 0 ? btnStyle : disabledBtnStyle}
                                onMouseEnter={e => { if (currentPageIndex > 0) e.currentTarget.style.background = '#fff'; }}
                                onMouseLeave={e => { if (currentPageIndex > 0) e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                                disabled={currentPageIndex <= 0}
                              >
                                <ChevronUp style={{ width: 16, height: 16, color: currentPageIndex > 0 ? '#374151' : '#9ca3af' }} />
                              </button>

                              <button
                                onClick={handleMoveDown}
                                title="Move page down"
                                style={currentPageIndex < pages.length - 1 ? btnStyle : disabledBtnStyle}
                                onMouseEnter={e => { if (currentPageIndex < pages.length - 1) e.currentTarget.style.background = '#fff'; }}
                                onMouseLeave={e => { if (currentPageIndex < pages.length - 1) e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
                                disabled={currentPageIndex >= pages.length - 1}
                              >
                                <ChevronDown style={{ width: 16, height: 16, color: currentPageIndex < pages.length - 1 ? '#374151' : '#9ca3af' }} />
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                    );
                })}

              </div>
            </div>
          );
        })()}

        {/* Right-click context menu */}
        {contextMenu && (
          <>
            {/* Invisible backdrop to close menu */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
              onMouseDown={closeContextMenu}
              onContextMenu={(e) => { e.preventDefault(); closeContextMenu(); }}
            />
            <div
              style={{
                position: 'fixed',
                top: contextMenu.y,
                left: contextMenu.x,
                zIndex: 10000,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: 180,
                overflow: 'hidden',
              }}
            >
              {/* Use as Mask — shown when 2+ elements selected and right-clicked element is part of multi-selection */}
              {selectedElements && selectedElements.length >= 2 && selectedElements.some(el => el.id === selectedElement.id) && (
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '9px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: '#4338ca', textAlign: 'left',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  onMouseDown={(e) => { e.stopPropagation(); handleCreateMask(); closeContextMenu(); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 12C12 8.686 14.686 6 18 6C21.314 6 24 8.686 24 12C24 15.314 21.314 18 18 18C14.686 18 12 15.314 12 12ZM18 8C15.791 8 14 9.791 14 12C14 14.209 15.791 16 18 16C20.209 16 22 14.209 22 12C22 9.791 20.209 8 18 8Z" fill="currentColor" />
                    <path d="M6 18C9.314 18 12 15.314 12 12C12 8.686 9.314 6 6 6C2.686 6 0 8.686 0 12C0 15.314 2.686 18 6 18Z" fill="currentColor" />
                  </svg>
                  Use as Mask
                </button>
              )}

              {/* Release Mask — shown when element is a mask shape or is clipped */}
              {selectedElement && (selectedElement.isMaskShape || selectedElement.clippedTo || selectedElement.clipMask) && (
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '9px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: '#dc2626', textAlign: 'left',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  onMouseDown={(e) => { e.stopPropagation(); handleReleaseMask(); closeContextMenu(); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 12C12 15.314 14.686 18 18 18C21.314 18 24 15.314 24 12C24 8.686 21.314 6 18 6C14.686 6 12 8.686 12 12Z" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                    <path d="M12 12C12 8.686 9.314 6 6 6C2.686 6 0 8.686 0 12C0 15.314 2.686 18 6 18C9.314 18 12 15.314 12 12Z" fill="currentColor" />
                  </svg>
                  Release Mask
                </button>
              )}

              {/* Divider + common actions */}
              {(selectedElements?.length >= 2 || selectedElement?.isMaskShape || selectedElement?.clippedTo) && (
                <div style={{ height: 1, background: '#e5e7eb', margin: '2px 0' }} />
              )}

              <button
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '9px 14px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, color: '#374151', textAlign: 'left',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                onMouseDown={(e) => { e.stopPropagation(); onDeleteElement && onDeleteElement(selectedElement.id); closeContextMenu(); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                </svg>
                Delete
              </button>
            </div>
          </>
        )}

      </div>
    );
};

export default CanvasEditor;
